import { GuideAufgaben } from './GuideAufgaben';
import { GuideAufgabenKontext } from './GuideAufgabenKontext';
import { angeboteneAntwortwege } from '../../shared/guide-antwortwege-sicht';
import { GuideZielinventar, GuideZielinventarInhalt } from './GuideZielinventar';
import { GuideRichtung } from './GuideRichtung';
import { GuideRichtungKontext } from './GuideRichtungKontext';
import { GuideAuftrag, GuideBeratung, GuideBeratungsstand } from './GuideAuftrag';
import { httpBoundary, BoundaryParseError, BoundarySourceError } from '@werkingflow/trust-boundary';
import { z } from 'zod';
import { buehnenLeseFehlermeldung, buehnenLeseUrl, SchnellbuehneSchema } from '../../shared/schnellbuehnen';
import { Schnellchat } from './Schnellchat';
import type { BuehnenBezug } from '../../shared/buehnen-anordnung';
/**
 * Guide-Spalte (GD) — Rafaels Einstieg in seinen eigenen Tag.
 *
 * DREI EBENEN (Rafael, 04.09.2026)
 *   1. BOARD — beim Einschalten sieht er seine Projektverwaltung: die Themen des
 *      Fokus-Boards in SEINER Reihenfolge, je eine Zeile mit dem, was laeuft. Er
 *      waehlt, womit er weiterarbeitet. Woertlich: "wenn ich GD einschalte, will ich
 *      direkt die Projektverwaltung sehen und aus deren Prioritaeten erkennen, mit
 *      welchem Thema ich weiterarbeiten will."
 *   2. THEMA — der Workspace wird aufbereitet, und die Spalte zeigt, was dort
 *      ansteht: der laufende Meilenstein, EINE Entscheidungs-Karte ausfuehrlich,
 *      weitere nur benannt.
 *   3. SITZUNG — "und dann sollte darunter eben der Chat sein, der mir genau sagt,
 *      worum es geht." Der Chat, in dem die Arbeit haengt, wird geoeffnet; ein
 *      Panel-TYP allein reicht nicht (der erste Versuch landete im Passwort-Gate
 *      von PROD: "Das bringt mir genau gar nichts").
 *
 * WARUM SIE NEBEN DEM WORKSPACE STEHT UND NICHT DARIN
 * Die Panels sind Electron-<webview>s. Ein Workspace im Workspace hiesse, sie ein
 * zweites Mal zu rendern — zaeh, und der Guide waere ausgerechnet dort langsam, wo
 * er Ruhe schaffen soll. Deshalb: schmale Spalte daneben, der bestehende
 * Workspace-Container wird nur schmaler.
 *
 * DIE REGEL, DIE BLEIBT: EINE SACHE LESBAR, DER REST NUR BENENNBAR
 * Genau eine Karte wird ausgeschrieben; alle weiteren stehen als Rangzeile da
 * (Nummer, Titel, Thema, Kopf) und lassen sich vorziehen. Wer die Rangzeilen
 * aufklappt, hat die Liste zurueckgebaut, gegen die die Spalte existiert.
 *
 * ANTWORTEN
 * Option/Rueckfrage geht ausschliesslich an den dauerhaften Antwortkern.
 * zustellenAn bleibt nur fuer nicht migrierte Sonderaktionen und Althistorie.
 */

import { useAuth } from '../contexts/AuthContext';
import GuideErklaerkarte from './GuideErklaerkarte';
import { setGuideAnsichtMerk, setHeuteWahl, useHeuteWahl } from '../lib/useUiPreferences';
import { useGuideAnsichtSteuerung } from '../lib/guideAnsichtSteuerung';
import { AbsendeRegister, useAbsendeAnbieter, useAbsenden } from './GuideAbsendeRegister';
import { AbsendenKnopf } from './GuideAbsendenKnopf';
import { bereinigeHeuteWahl, spaeterEintrag, tagesSchluessel, warteschlange, zielZeile } from './guide-heute';
import { hatUngesendetes, QUEUE_KUERZEL, queueTaste } from './guide-queue-tasten';
import GuideKurzErklaerung from './GuideKurzErklaerung';
import { usage } from '../lib/cuiUsage';
import { useFensterBuehne } from '../lib/fensterBuehne';
import { isShadowClient } from '../lib/shadowClient';
import { guideNetzFehler } from '../lib/guideNetz';
import { useStageUsage } from '../hooks/useStageUsage';
import type { GuideBeraterStart } from './GuideBeraterChat';
import { GuideSendProvider, useGuideSend } from './GuideSendContext';
import { ENTWURF_VORBELEGT } from './GuideSendQueue';
import GuideAbsendeFehler from './GuideAbsendeFehler';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLiveChannel } from '../hooks/useLiveChannel';
import { useStageRefresh, StageRefreshFehler, stageRefreshHinweis } from '../hooks/useStageRefresh';
import { useSessionStore } from '../contexts/SessionStore';
import CuiLitePanel from './panels/CuiLitePanel';
import { GuideHistorie } from './GuideHistorie';
import { BuehnenKettenZeile } from './BuehnenKettenZeile';
import type { Kachel, Layoutrezept } from './layoutrezept';
/* Bestehende Importe aus GuidePanel bleiben gueltig; die Typen liegen in layoutrezept.ts (kein Zyklus GuideHof → GuidePanel). */
export type { Kachel, Layoutrezept } from './layoutrezept';
import { useGuideHausfilter } from './GuideFlaeche';
import { hofansichtVersteckt } from './guide-oberthemen';
import { Hofansicht, HausUmschalter, HofSchalter, buehnenStatusSatz, STUFE_WORT, archivBildUrl, istOffeneFrage, istOffenerVorschlag, aktuellerBerater, fragenAnDich, type BuehneAnsicht, type BeraterZustand, type Entscheidung as BuehnenEntscheidung, type Vorschlag, type SitzungOhneBerater } from './GuideHof';
import { useGuideAntwort } from './GuideAntwort';
import { BildEinfuegeZone } from './BildEinfuegeZone';
import { baueBuehne, type BauKachel, type BuehnenAuswahl } from '../../shared/guide-layout';
import { ACCOUNTS } from '../types';
import { hinweisFehlenderArbeitsbereich } from '../lib/buehnenWirt';
import { buehneNichtGezeichnet, rechtsAufBildschirm2, warteBisBelegt } from '../lib/guideOeffnungsBeleg';
import { auftragGegenRafael, rafaelNavigiert, rafaelNavStand } from '../lib/rafaelNavigation';

const API = '/api';
/** So lange darf der Assistent nach einer Antwort brauchen, bevor es laut wird (Kanzler Dev, 14.10.2026). */
const ASSISTENT_FRIST_MS = 60_000;

/**
 * Rafaels eigene Zahl, im Browser gemessen — nicht die auf dem Server.
 *
 * Rafael, 08.09. 10:23Z: "das Laden des Guides dauert oft 5-10 Sekunden". Eine
 * serverseitig gemessene Zeit sagt nichts ueber seine Tailscale-Strecke; erst eine
 * Messung HIER, ab dem `fetch`-Aufruf bis zum fertig geparsten JSON, zeigt, ob eine
 * Aenderung sein Problem trifft. `console.info` statt eines UI-Elements, damit
 * nichts an der Spalte selbst haengt, das spaeter wieder entfernt werden muss —
 * lesbar per Browser-Konsole (bzw. `/api/browser-console/...` fuers Fernsteuern).
 */
function meldeLadezeit(route: string, start: number, r: Response | null): void {
  const ms = Math.round(performance.now() - start);
  const bytes = r?.headers.get('content-length');
  const kodierung = r?.headers.get('content-encoding');
  console.info(`[guide-performance] ${route}: ${ms}ms${bytes ? `, ${bytes} bytes` : ''}${kodierung ? ` (${kodierung})` : ''}`);
}

// --- Typen (Spiegel von server/routes/guide.ts) -------------------------------

type Modus = 'voll' | 'halb' | 'leer';
type Quellenart = 'gemessen' | 'gelesen' | 'aussage';

interface Option { kuerzel: string; text: string; folge: string }
interface Quelle { art: Quellenart; text: string; am?: string }
/** `am` = Zeitpunkt des letzten Zustands-Wechsels; der Server liefert es seit jeher mit. */
interface Zustellung { sessionId: string; zustand: 'offen' | 'zugestellt' | 'fehlgeschlagen'; am: string; fehler?: string; versuche: number }

export interface Karte {
  id: string;
  /** Wann die Karte eingestellt wurde (ISO) — fuer 'wird seit … gebaut'. */
  angelegt_am?: string;
  titel: string;
  sachlage: string;
  entscheidung: string;
  /** Rafaels Format vom 05.09.: leer bei Karten aus der Zeit davor. */
  nutzersicht?: string;
  ohne_entscheidung?: string;
  empfehlung?: string;
  /** Das staerkste Argument GEGEN die Empfehlung — gegen blindes Durchwinken. */
  gegenargument?: string;
  optionen: Option[];
  wartet_auf: string[];
  /** Pfad/Dateiname der Entscheidungsseite, die zu DIESER Karte gehoert. */
  grundlage?: string;
  ziel_workspace?: string;
  panels: string[];
  dringlichkeit: 'hoch' | 'mittel' | 'niedrig';
  kopfanstrengung: 'leicht' | 'mittel' | 'schwer';
  quelle: Quelle[];
  /** Haus, in dem die wartende Sitzung dieser Karte laeuft. */
  haus?: 'dev' | 'partner';
  haengt_an?: string;
  layout?: Layoutrezept;
  layout_geprueft?: Rezeptpruefung | null;
  eingestellt_von: { label: string; sessionId?: string };
  /*
   * Ueberarbeitung waehrend sie Rafael vorliegt (Rafael, 07.09.: "Sessions
   * ueberarbeiten Entscheidungen, waehrend du daran arbeitest").
   *
   * Beide Angaben kommen vom Server mit — GET /guide/karten gibt die Karte
   * vollstaendig zurueck, es gibt dort keine Feldliste, die sie schluckt.
   * Gezeigt wird der Hinweis NUR, wenn nach dem ersten Vorlegen geschrieben
   * wurde: wer eine Karte vor ihrem ersten Auftritt umbaut, hat sie verfasst,
   * nicht unter ihm ausgetauscht. Sonst stuende der Hinweis irgendwann auf
   * jeder Karte, und ein Hinweis, der immer da ist, wird nicht mehr gelesen.
   */
  geaendert_am?: string;
  geaendert_felder?: string[];
  messung?: { vorgelegt_am?: string };
  zustand: string;
  zustellung: Zustellung[];
  /** Nur bei Handlungen einer Arbeitsbuehne: Rafaels Wegklick-Antwort. */
  handlung_status?: { wert: 'erledigt' | 'spaeter' | 'frage'; text?: string; am: string };
  /** Nur bei zustand='nicht_eingereiht': warum das keine offene Entscheidung ist. */
  grund?: string;
}

interface ThemaBezug { id: string; name: string | null; fehlt: boolean }

/**
 * WARUM EINE KARTE AUF IHREM PLATZ STEHT — der Server rechnet es, hier wird es nur
 * gezeigt. Nichts davon wird im Client nachgerechnet: eine zweite Sortierlogik in
 * der Flaeche waere genau die zweite Wahrheit, die irgendwann anders entscheidet
 * als der Server (derselbe Fehler wie beim doppelten Fertig-Tor am 06.09.).
 */
interface Platzgrund {
  satz: string;
  vorgezogen: boolean;
  block: { id: string; name: string } | null;
  thema: { id: string; name: string | null; platz: number | null; von: number } | null;
  dringlichkeit: Karte['dringlichkeit'];
  kopfanstrengung: Karte['kopfanstrengung'];
  angelegt_am: string;
}

interface Rangzeile {
  platz: number;
  id: string;
  titel: string;
  /** Welchem Haus diese Karte gehoert — die volle Karte trug es schon, die Reihe seit 8cabd4017. */
  haus?: 'dev' | 'partner';
  thema: ThemaBezug | null;
  dringlichkeit: Karte['dringlichkeit'];
  kopfanstrengung: Karte['kopfanstrengung'];
  warum: Platzgrund;
}

interface Workspacebezug { id: string; name: string; quelle: 'gesetzt' | 'vorschlag' }


/** Ergebnis der Vorbereitungs-Pruefung zu genau diesem Rezept. */
interface Rezeptpruefung {
  am: string;
  von: string;
  befund: 'unbewertet' | 'ok' | 'maengel';
  anmerkung?: string;
  fingerabdruck: string;
  /** Wurde auf DIESEM Rechner geprueft? Sonst ist es keine Freigabe. */
  gilt_hier: boolean;
  host: string;
}

interface BoardThema {
  id: string;
  name: string;
  gruppe: 'produkt' | 'geschaeft';
  reihenfolge: number;
  aktiv: boolean;
  laufender_meilenstein: { id: string; titel: string; termin?: string } | null;
  laufend: number;
  wochenziele: Array<{ id: string; titel: string; termin?: string }>;
  offene_schritte: number;
  geschobene_schritte?: number;
  offene_karten: number;
  /** Kopfanstrengung der naechsten offenen Karte — Etikett, kein Filter. */
  kopf?: 'leicht' | 'mittel' | 'schwer' | null;
  meilensteine: { fertig: number; gesamt: number };
  workspace: Workspacebezug | null;
  lebende_sitzungen: number | null;
  hat_rezept?: boolean;
  /**
   * Rafaels Ampel (06.09.), seit 07.09. mit vier Raengen: fertig (er kann klicken),
   * braucht_dich (eine Sitzung wartet auf seine Antwort), arbeit (jemand arbeitet
   * wirklich — Werkzeug, nicht Prozess), ruht.
   */
  zustand?: 'fertig' | 'braucht_dich' | 'arbeit' | 'ruht';
  rang?: number;
  /** Ein Satz vom Server, was die Sitzungen des Arbeitsbereichs tun. */
  sitzungslage?: string | null;
  entscheidung_fertig?: number;
  wird_gearbeitet?: boolean | null;
  warum_nicht_fertig?: string[];
  /** Nichts Offenes, nichts Laufendes, nichts Vorbereitetes. */
  ruhend?: boolean;
}

interface BoardBlock {
  id: string;
  name: string;
  themen: string[];
  zustand: 'offen' | 'pausiert';
  offene_karten: number;
  /** Haus seiner offenen Karten — null ohne offene Karte. Fehlt bei alter Server-Fassung. */
  haus?: 'dev' | 'partner' | 'gemischt' | null;
}

interface Board {
  modus: Modus;
  themen: BoardThema[];
  /** Fehlt, solange die alte Server-Fassung laeuft — dann bleibt die flache Liste. */
  bloecke?: BoardBlock[];
  aktiver_block?: string | null;
  ohne_thema: number;
  offen_gesamt: number;
  /** Alle Sitzungen des Hauses in einem Satz (Server, 09.09.); fehlt bei alter Fassung. */
  sitzungslage_gesamt?: string | null;
  sitzungen_gesamt?: number | null;
  zurueckgestellt: number;
  konfig: {
    metaKoordinator?: Spaltenchat; infoSession?: Spaltenchat; coachModell?: CoachModell;
    /** Die Sitzungen der Aemter beider Haeuser (Kanzler, Vizekanzler, Assistenten) —
     *  sie haben per Definition keine Buehne. Server: `konfigOeffentlich`. */
    aemter?: string[];
  };
  /** Die beiden festen Chats, live aufgeloest. null = nicht nachgesehen. */
  konfig_sitzungen?: { metaKoordinator: Sitzung | null; infoSession: Sitzung | null } | null;
  /** Stufe 1: was die Sessions ohne Rafael entschieden haben. Fehlt bei alter Server-Fassung. */
  selbst_entschieden?: SelbstEntschieden[];
  /** Wer gerade angemeldet unterwegs ist. Fehlt bei alter Server-Fassung. */
  konten?: AktivesKonto[];
  /** Welches Haus traegt den neueren CUI. Fehlt bei alter Server-Fassung. */
  staende_haeuser?: StaendeHaeuser;
}

/** Gebaut/gestartet EINES Hauses. Server: `standVonHaus` in guide.ts. */
interface HausStand {
  haus: 'dev' | 'partner';
  hier: boolean;
  /** mtime von dist/index.html — die Oberflaeche, die Rafael sieht. null = nicht gemessen. */
  gebaut: string | null;
  /** Prozessstart des Servers. null = nicht gemessen. */
  gestartet: string | null;
  /**
   * Commit des SERVERPROZESSES (friert beim Start ein) — kein Aktualitaetsmass, sondern
   * das Gegenzeugnis dazu, ob eine kleine Bauzeit-Differenz nur Jitter ist.
   */
  commit: string | null;
  /** Minuten, die der Start NACH dem Buendel lag — der Neustart brachte dann nichts Neues. */
  start_ohne_neues_buendel_minuten: number | null;
  fehler: string | null;
}

/** Beide Haeuser plus das Urteil darueber. Server: `staendeDerHaeuser` in guide.ts. */
interface StaendeHaeuser {
  gemessen_am: string;
  hier: 'dev' | 'partner';
  haeuser: HausStand[];
  /** Das Haus mit dem AELTEREN Buendel. null = gleichauf ODER nicht vergleichbar (siehe `unklar`). */
  zurueck: 'dev' | 'partner' | null;
  abstand_minuten: number | null;
  /** Laufen beide Serverprozesse auf demselben Commit? null = mindestens einer unbekannt. */
  gleicher_commit: boolean | null;
  /** Sagt der Commit-Vergleich ueberhaupt etwas ueber die Buendel? Waehrend eines Rollouts nicht. */
  commit_aussagekraeftig: boolean;
  /** Warum nicht verglichen werden konnte — im Wortlaut. null = es konnte. */
  unklar: string | null;
}

/** Ein Konto aus Rafaels Freigabeliste. Ohne Passwort — die Liste sagt WER, nicht WOMIT. */
interface AktivesKonto {
  kuerzel: string;
  label: string;
  email: string;
  app: string | null;
  umgebung: 'staging' | 'produktion';
  box: string;
  hinweis: string | null;
}

interface SelbstEntschieden {
  id: string;
  titel: string;
  weil: string;
  am: string;
  von: string | null;
  thema: ThemaBezug | null;
  einspruch: { am: string; notiz?: string } | null;
}

interface Spaltenchat { sessionId: string; accountId?: string; label?: string }
/** Modelle fuer den Coach — Server kennt dieselbe Liste (guide.ts COACH_MODELLE). */
type CoachModell = 'fable' | 'opus';

interface Sitzung {
  sessionId: string;
  gefunden: boolean;
  accountId?: string;
  projectId?: string;
  projectName?: string;
  titel?: string;
  /** Der Name des Fadens — was Rafael als Ueberschrift wiedererkennt. */
  betreff?: string | null;
  /** EIN Satz vom Meta-Koordinator, wo die Arbeit steht. Nie ein Chat-Auszug. */
  stand?: string | null;
  stand_fehlt?: string;
  laeuft?: boolean;
  /** Bewusst beendet — eine Antwort dorthin belebt den Faden wieder. */
  beendet?: boolean;
  aufmerksamkeit?: string;
  rolle?: 'wartet' | 'arbeitet';
  /** Welche Entscheidungs-Karten an dieser Sitzung haengen. */
  fuer_karte?: string[];
  /**
   * Was die Sitzung WIRKLICH tut, aus dem Transkript-Ende (Server, 07.09.). Fehlt
   * bei alter Serverfassung — dann bleibt die Zeile bei laeuft/schlaeft.
   */
  taetigkeit?: {
    art: 'arbeitet' | 'limit' | 'wartet_auf_karte' | 'wartet_auf_antwort' | 'still' | 'tot' | 'beendet' | 'unbekannt';
    wort: string;
    seit: string | null;
    bis?: string | null;
    karte?: { id: string; titel: string; fertig: boolean } | null;
    quelle: string;
  };
}

interface FensterPasst { passt: boolean | null; grund: string; workspace: string | null }

type ThemaSichtInArbeit = Array<{ id: string; titel: string; seit: string | null; sessionId: string | null; zitat: string | null }>;

/*
 * RAFAELS EIGENE AUFTRAEGE — eigene Liste, nicht in `in_arbeit` gemischt.
 *
 * Rafael, 09.09.2026 16:2xZ: "ich hab hier grad eine antwort reingegeben - das sollte
 * ich schon auch bemerkbar machen im guide oder - sollte da nicht in bearbeitung
 * stehen und ich automatisch auf die themen zurueckkommen". Sein Satz ging 16:21Z
 * raus, die Sitzung dahinter starb 16:21:48Z am Kontolimit, und im Guide stand davon
 * nichts — aus seiner Sicht ins Nichts gegangen.
 *
 * Getrennt von `in_arbeit`, weil das aus KARTEN kommt (`kartenInArbeit()`) und ein
 * Anstoss gar keine Karte anlegt. Der Server fuehrt die Liste (554d2584, 4e9b5f161);
 * fehlt sie (aeltere Fassung), wird hier nichts gezeigt.
 */
type ThemaSichtAuftraege = Array<{
  id: string;
  /** Rafaels Satz. Nie leer — ein Anstoss ohne Satz bekommt einen Ersatztitel. */
  was: string;
  zeit: string;
  zustand: 'angenommen' | 'wird_bearbeitet' | 'fertig' | 'ausgefallen';
  seit: string;
  sitzung: string | null;
  /** Nur bei 'ausgefallen', nie leer — der Satz, der sagt, WAS schiefging. */
  grund?: string;
}>;

interface ThemaSicht {
  /** Der Arbeitsbereich, in dem das Rezept landet — der der wartenden Sitzung. */
  ziel_workspace?: string | null;
  thema: { id: string; name: string; gruppe: string } | null;
  ohne_thema: boolean;
  laufender_meilenstein: {
    id: string; titel: string; termin?: string; notiz?: string;
    offene_schritte: string[]; offene_schritte_gesamt: number;
  } | null;
  wochenziele: Array<{ id: string; titel: string; termin?: string }>;
  /** Was Rafael sich fuer HEUTE vorgenommen hat — siehe `tagesrahmen` im Server. */
  tagesrahmen?: {
    tag: string;
    zum_thema: Array<{ id: string; text: string }>;
    ohne_zuordnung: Array<{ id: string; text: string }>;
    ohne_zuordnung_gesamt: number;
  };
  aktuelle_karte: (Karte & {
    thema_aufgeloest?: ThemaBezug | null;
    /** Warum diese Karte oben liegt — vom Server begruendet, nicht hier hergeleitet. */
    warum_vorne?: Platzgrund;
    /** DAS Tor: der Server entscheidet, ob die Karte vorlegbar ist. */
    fertig?: boolean;
    fertig_fehlt?: string[];
    /** Ob das Zielfenster wirklich zu dieser Karte passt — dreiwertig, null = unbekannt. */
    fenster_passt?: FensterPasst;
    /**
     * Gesetzt, wenn Rafael eine Karte des ANDEREN Hauses angeklickt hat: ihre Frage
     * steht hier, ihre Buehne nicht. Das Rezept faehrt in dem Fall gar nicht mit —
     * eine Partner-Karte darf nur die Buehne vom Partner-Server zeigen (09.09. 14:15Z).
     */
    im_anderen_haus?: string | null;
  }) | null;
  /**
   * Was der Server zur angeklickten Karte sagt. `gefunden: false` heisst: es gibt sie
   * nicht mehr — dann faellt der Wunsch weg, statt in jedem weiteren Abruf zu haengen.
   * Fehlt das Feld (aeltere Serverfassung), wurde keine Karte genannt.
   */
  karte_gewuenscht?: { id: string; gefunden: boolean; haus: string | null } | null;
  reihe: Rangzeile[];
  reihe_gekuerzt: number;
  /**
   * Alle weiteren Entscheidungen dieser Buehne mit Optionen — Rafael, 09.09.2026
   * 12:27Z: "alles was zu einer buehne gehoert moeglichst in einem schlag
   * beantworten". Fehlt bei einer aelteren Serverfassung; dann bleibt es bei `reihe`.
   */
  weitere?: Karte[];
  /**
   * Gesetzt, wenn dieses Thema im EIGENEN Haus keine offene Entscheidung hat, im
   * anderen aber schon (Server: guide.ts, Feld `fremdes_haus`). Ohne diese Auskunft
   * meldete die Buehne "liegt nichts vor", waehrend die Fragenliste daneben drei
   * Entscheidungen zeigte — Rafaels Bild vom 09.09. 14:58Z. Fehlt bei einer
   * aelteren Serverfassung; dann bleibt es beim bisherigen Satz.
   */
  fremdes_haus?: { anzahl: number; haus: 'dev' | 'partner'; titel: string } | null;
  /** Was ohne Rafael entschieden wurde (Stufe 1, 24 h) — steht mit auf der Liste (Rafael, 13:0xZ). */
  autonom?: Array<{ id: string; titel: string; weil: string; am: string; einspruch: boolean }>;
  /** Karten, die eine Sitzung gerade ueberarbeitet — nicht in der Reihe, aber sichtbar. */
  in_ueberarbeitung?: Array<{ id: string; titel: string; ueberarbeitung: { am: string; von: string; grund: string } }>;
  /** Aussortierte Entscheidungen der letzten sieben Tage — sichtbar, nie still weg (Rafael, 12:20Z). */
  aussortiert?: Array<{ id: string; titel: string; grund: string | null; angelegt_am: string; geprueft_am: string | null;
    veraltet: { am: string; von: string; durch?: string; aussage?: string; grund: string } | null }>;
  karten_gesamt: number;
  karten_passend_zum_kopf: number;
  workspace: Workspacebezug | null;
  /** Rezept des Themas — gilt, wenn die vorliegende Karte keins hat. */
  layout: Layoutrezept | null;
  layout_geprueft?: Rezeptpruefung | null;
  /** Karten, auf die Rafael im Chat geantwortet hat — keine Fragen mehr. */
  in_arbeit?: ThemaSichtInArbeit;
  auftraege?: ThemaSichtAuftraege;
  sitzungen: Sitzung[];
  sitzungsfehler: string | null;
  /** Arbeitsbuehne statt Entscheidungskarten-Thema — siehe `handlungen` unten. */
  arbeitsbuehne?: boolean;
  /** Nur bei `arbeitsbuehne`: das Handlungsprotokoll des Themas, neueste zuerst. */
  handlungen?: Karte[];
}

interface Props {
  assistentModus?: boolean;
  oeffnungsAuftrag?: { clientId: string; buehneId: string; auftragId: string; expiresAt: number; handNav?: number; nurAngebot?: boolean } | null;
  /**
   * Räumt, was über der Bühne liegt (Mission Control, „Alle Chats“), erst NACH der Annahme
   * eines Öffnungsauftrags. null = geräumt; sonst der Grund, warum nicht (ungesicherter Text).
   */
  onAbdeckungRaeumen?: () => string | null;
  /** Schaltet auf einen Workspace (Station 2: das aufbereitete Fenster). */
  onOeffnenWorkspace: (projectId: string, panels?: string[]) => boolean | void;
  /** Oeffnet die Sitzung, in der die Arbeit haengt (Station 3). */
  /*
   * ACHTUNG — DIESER WEG SCHREIBT.
   *
   * Er reicht einen Aktivierungsplan an den LayoutManager; der haengt den Chat per
   * addNode in Rafaels gespeichertes Layout und ruft danach ausdruecklich
   * saveLayout. Zum blossen ANSEHEN eines Chats ist `sitzungAlsBuehne` da.
   *
   * Stand 07.09.2026: im Guide ruft ihn niemand mehr auf — alle Klickwege
   * ("zeig mir diesen Chat") gehen ueber die Buehne. Der Anschluss bleibt, weil
   * das Entfernen bis in App.tsx reicht; er ist ein Anschluss, kein Angebot.
   */
  onOeffnenSitzung: (projectId: string, sessionId: string, accountId: string) => void;
  onSchliessen: () => void;
  /**
   * Meldet, ob der Guide gerade die BUEHNE ist (kein Thema gewaehlt) — dann legt
   * App eine eigene Flaeche ueber den Arbeitsbereich. Muss stabil sein
   * (useCallback), sonst laeuft der Abmelde-Effekt bei jedem Rendern.
   */
  onBuehne?: (aktiv: boolean, grund?: string | null) => void;
  /**
   * Reicht das Fenster EINER Entscheidung nach oben — als flexlayout-Dokument, gebaut
   * aus dem Rezept der Karte. `null` raeumt die Buehne wieder ab.
   *
   * Bewusst getrennt von `onBuehne`: das ist der VORHANG („nichts vorzuzeigen"), das
   * hier ist der BUEHNENBODEN („hier ist das Fenster dieser Karte"). Zwei Zustaende,
   * zwei Meldungen — ein gemeinsames Flag haette beide Faelle stillschweigend
   * vermischt.
   */
  onBeraterOeffnen?: () => void;
  onBeraterBuehne?: (start: GuideBeraterStart | null) => void;
  onBuehnenLayout?: (doc: unknown | null, projectId: string | null, bezug?: BuehnenBezug) => void;
  /**
   * Meldet, ob Rafael GERADE an einer Karte ist (liest, tippt, ein Fenster wird
   * umgebaut). App verschiebt dann ein faelliges Neuladen, statt ihm den Satz unter
   * den Fingern wegzunehmen. Muss stabil sein (useCallback).
   */
  onArbeit?: (aktiv: boolean) => void;
  /**
   * Der Guide fuellt seinen Container selbst aus (App bestimmt die Breite ueber
   * den Griff) und stellt gross dar. Rafael, 06.09.: "Schriften gross, keine
   * Kleinzeilen." Ohne die Flagge bleibt die alte, schmale Spalte — sie ist noch
   * der Weg, auf dem der Guide neben einem fremden Arbeitsbereich steht.
   */
  breitDargestellt?: boolean;
  /**
   * Direkt in einem Thema starten (Deep-Link `?panel=guide&thema=<id>`). Damit kann
   * ein Buehnen-Rezept als linke Kachel die Fragenliste des Guide setzen statt einer
   * Textseite — Rafael, 09.09.2026 12:51Z: "links einfach eine liste an ja-nein
   * fragen sowie dem freien textfenster".
   */
  startThema?: string | null;
  /**
   * Deep-Link auf EINE Karte innerhalb von `startThema` — der Weg, auf dem eine
   * Aufnahme (`GET /api/capture/guide?thema=…&karte=…`) genau die Karte zeigen kann,
   * die Rafael angeklickt haette. Ohne `startThema` wirkungslos: eine Karte lebt in
   * einem Thema, und die Route weist die Kombination schon vorher ab.
   */
  startKarte?: string | null;
  /**
   * Deep-Link `?panel=guide&guide_buehne=<id>`: oeffnet diese eine Buehne im Guide — fuer die
   * Aufnahme `GET /api/capture/guide?buehne=<id>`, die zeigt, was ueber der offenen Buehne steht
   * (Kettenzeile, Rafael 03.10.2026). `?buehne=` allein ist die Fenstervorschau (App.tsx).
   */
  startBuehne?: string | null;
}

// --- Beschriftungen -----------------------------------------------------------

/**
 * BESCHRIFTUNG DES KOPF-SCHALTERS.
 *
 * Frueher stand hier "Voller Kopf / Halber Kopf / Leerlauf" — eine Tankanzeige.
 * Genau das ist fachlich falsch: die Vorstellung, Selbststeuerung verbrauche
 * einen zehrenden Vorrat, ist die Glukose-These, und die gilt als widerlegt
 * (Hagger et al. 2016, Wiederholung in 23 Laboren; Recherche 06.09.,
 * local-storage/research/gd-kognition/ergebnis-20260906.md, Punkt 8). Tragfaehig
 * ist der Schalter als SELBSTBERICHT: was will ich mir jetzt zumuten.
 *
 * Deshalb benennen die Schaltflaechen jetzt, was sie bewirken, statt einen
 * Zustand zu behaupten, den niemand gemessen hat. Die Schluessel im Server
 * (voll/halb/leer) bleiben unveraendert — sie stecken in gespeicherten Staenden.
 */
const QUELLE_TEXT: Record<Quellenart, string> = { gemessen: 'selbst gemessen', gelesen: 'gelesen', aussage: 'deine Angabe' };
const QUELLE_FARBE: Record<Quellenart, string> = { gemessen: 'var(--tn-green)', gelesen: 'var(--tn-orange)', aussage: 'var(--tn-link)' };
const DRINGLICH_FARBE: Record<Karte['dringlichkeit'], string> = { hoch: 'var(--tn-red)', mittel: 'var(--tn-orange)', niedrig: 'var(--tn-dim)' };

const ZURUECK_PRESETS: Array<{ grund: string; label: string; brauchtText: boolean }> = [
  { grund: 'kein-kopf', label: 'Kein Kopf dafuer', brauchtText: false },
  { grund: 'brauche-info', label: 'Brauche Info ...', brauchtText: true },
  { grund: 'muss-reden', label: 'Muss mit jemandem reden', brauchtText: false },
  { grund: 'freitext', label: 'Anderer Grund ...', brauchtText: true },
];

/**
 * Der Umbau-Vorhang ueber dem Arbeitsbereich (App.tsx horcht auf dieses Ereignis).
 *
 * Rafael, 06.09.: "Beim Klick sollte es gleich schwarz werden und nicht erst:
 * Uebergang, dann Neuladen." Entscheidend ist das WANN: `vorhangAn` muss SYNCHRON
 * im Klick laufen, vor dem ersten `await`. Ein Aufruf nach dem ersten fetch waere
 * schon zu spaet — dann steht das alte Fenster noch einen Wimpernschlag da und
 * springt danach, und genau das kostet ihn den Faden.
 */
function vorhangAn(was: string): void {
  window.dispatchEvent(new CustomEvent('guide-umbau', { detail: { aktiv: true, was } }));
}

/**
 * Vorhang hoch.
 *
 * Zwei Bildwechsel abwarten, damit der Browser das neue Fenster wirklich
 * gezeichnet hat — ohne das hebt sich der Vorhang eine Zehntelsekunde zu frueh
 * und man sieht doch wieder das Springen, gegen das er da ist.
 *
 * ABER NICHT NUR: `requestAnimationFrame` laeuft in einem Fenster, das der Browser
 * fuer unsichtbar haelt, gar nicht — und dann bleibt der Vorhang UNTEN. Genau so
 * gemessen am 06.09. im Browser-Panel: Abdunkelung nach 10 ms, Freigabe nach 15 s
 * immer noch nicht. Ein Vorhang, der haengenbleibt, ist schlimmer als das
 * Springen, gegen das er hilft. Deshalb laeuft ein Zeitgeber mit; wer zuerst
 * kommt, hebt ihn.
 */
function vorhangAus(): void {
  let gesendet = false;
  const senden = () => {
    if (gesendet) return;
    gesendet = true;
    window.dispatchEvent(new CustomEvent('guide-umbau', { detail: { aktiv: false } }));
  };
  requestAnimationFrame(() => requestAnimationFrame(senden));
  setTimeout(senden, 120);
}

// --- Bausteine ----------------------------------------------------------------

const box: React.CSSProperties = {
  background: 'var(--tn-surface)', border: '1px solid var(--tn-border)', borderRadius: 6, padding: 10,
};

/**
 * SACHLAGE IST MARKDOWN — und wurde als Rohtext ausgegeben.
 *
 * Rafael, 09.09.2026 16:5xZ, zum Thema "Vertrieb & Marketing": "die Form des
 * Vertriebs- und Marketing-Panels ist voellig sinnlos … kaum lesbar, wird nicht
 * gerendert, unstrukturiert". Auf seinem Bild stand die Handlung als EIN Textblock
 * ueber die volle Hoehe, mit den Sternchen als Zeichen.
 *
 * Gezaehlt 16:5xZ auf theme_1784090875096_z90npu: k-fachmedien-drei-anfragen-20260909
 * 5.311 Zeichen mit 16 Fett-Auszeichnungen, k-linkedin-sieben-kommentare-20260909
 * 3.549 Zeichen mit 24. Diese 40 Sternchen sind das, was er gesehen hat.
 *
 * SICHER: `react-markdown` setzt ohne `rehype-raw` KEIN rohes HTML ein — und genau
 * das ist hier noetig, weil der Text aus Karten kommt, die Sitzungen schreiben.
 * Kein `dangerouslySetInnerHTML`, nirgends.
 *
 * SPARSAM: gerendert wird, was diese Texte wirklich benutzen — Fett, Listen,
 * Absaetze, Zwischenueberschriften, Code. Ueberschriften bekommen bewusst keine
 * eigene Groesse: in einer schmalen Spalte neben einer Entscheidung waere eine
 * Zwischenueberschrift in 18px lauter als die Frage, um die es geht.
 */
function Fliesstext({ text, farbe = 'var(--tn-text)', groesse = 12 }: { text: string; farbe?: string; groesse?: number }) {
  const p: React.CSSProperties = { margin: '0 0 6px 0' };
  return (
    <div style={{ fontSize: groesse, lineHeight: 1.5, color: farbe }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p style={p}>{children}</p>,
          ul: ({ children }) => <ul style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ol>,
          li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
          h1: ({ children }) => <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 3 }}>{children}</div>,
          h2: ({ children }) => <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 3 }}>{children}</div>,
          h3: ({ children }) => <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 3 }}>{children}</div>,
          h4: ({ children }) => <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 3 }}>{children}</div>,
          strong: ({ children }) => <strong style={{ color: 'var(--tn-text)' }}>{children}</strong>,
          code: ({ children }) => (
            <code style={{ fontFamily: 'monospace', fontSize: '0.92em', background: 'var(--tn-bg)', padding: '0 3px', borderRadius: 3 }}>{children}</code>
          ),
          pre: ({ children }) => (
            <pre style={{ margin: '0 0 6px 0', padding: 6, overflowX: 'auto', background: 'var(--tn-bg)', borderRadius: 4, fontSize: '0.92em' }}>{children}</pre>
          ),
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--tn-blue)' }}>{children}</a>
          ),
          hr: () => <div style={{ borderTop: '1px solid var(--tn-border)', margin: '6px 0' }} />,
          blockquote: ({ children }) => (
            <div style={{ borderLeft: '2px solid var(--tn-border)', paddingLeft: 8, margin: '0 0 6px 0' }}>{children}</div>
          ),
          table: ({ children }) => (
            <table style={{ borderCollapse: 'collapse', margin: '0 0 6px 0', fontSize: '0.95em' }}>{children}</table>
          ),
          td: ({ children }) => <td style={{ border: '1px solid var(--tn-border)', padding: '2px 5px' }}>{children}</td>,
          th: ({ children }) => <th style={{ border: '1px solid var(--tn-border)', padding: '2px 5px', textAlign: 'left' }}>{children}</th>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Ein langer Text frisst die Spalte. Rafaels Bild zeigte EINEN Block ueber die volle
 * Hoehe — 5.311 Zeichen sind keine Handlung mehr, die man ueberfliegt.
 *
 * Deshalb: die ersten Zeilen offen, der Rest hinter "mehr". Der Schnitt liegt an
 * einem Absatz, nicht mitten im Satz, und die Grenze ist grosszuegig genug, dass ein
 * normal langer Text (die meisten Handlungen) gar nicht erst geschnitten wird. Was
 * hier steht, verschwindet nie — es ist einen Klick entfernt.
 */
const LANG_AB = 600;

function LangerFliesstext({ text, farbe, groesse }: { text: string; farbe?: string; groesse?: number }) {
  if (text.length <= LANG_AB) return <Fliesstext text={text} farbe={farbe} groesse={groesse} />;
  // An einem Absatz schneiden, sonst reisst der Anfang mitten im Satz ab. Gibt es
  // keinen, wird nicht geschnitten — dann ist der ganze Text ein Absatz, und ein
  // harter Schnitt saehe aus wie ein Fehler.
  const schnitt = text.lastIndexOf('\n\n', LANG_AB);
  if (schnitt < 120) return <Fliesstext text={text} farbe={farbe} groesse={groesse} />;
  return (
    <>
      <Fliesstext text={text.slice(0, schnitt)} farbe={farbe} groesse={groesse} />
      <details>
        <summary style={{ fontSize: 10, color: 'var(--tn-text-muted)', cursor: 'pointer' }}>
          mehr ({text.length - schnitt} Zeichen)
        </summary>
        <Fliesstext text={text.slice(schnitt)} farbe={farbe} groesse={groesse} />
      </details>
    </>
  );
}

/**
 * `id` ist Pflicht und wird NICHT aus dem Text abgeleitet: eine deutsche
 * Beschriftung ("Spaeter, weil ...") wird beim Ableiten zu `sp-ter-weil-`, weil
 * Umlaute und Auslassungspunkte wegfallen. Solche Kennungen sind das, woran
 * Oberflaechen-Tests und die headless-Steuerung greifen — sie muessen lesbar und
 * stabil sein, nicht ein Nebenprodukt der Rechtschreibung.
 */
function Knopf({ id, label, onClick, ton = 'normal', disabled }: { id: string; label: string; onClick: () => void; ton?: 'normal' | 'stark' | 'leise'; disabled?: boolean }) {
  return (
    <button
      data-ai-id={`guide-btn-${id}`}
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1, padding: '7px 6px', fontSize: 12, borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1, whiteSpace: 'nowrap',
        fontWeight: ton === 'stark' ? 700 : 500,
        background: ton === 'stark' ? 'var(--tn-blue)' : 'var(--tn-surface)',
        color: ton === 'stark' ? 'var(--tn-bg)' : 'var(--tn-text-muted)',
        border: `1px solid ${ton === 'stark' ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
      }}
    >
      {label}
    </button>
  );
}

/**
 * DIE HAUS-MARKE — dieselbe Kachel-Kennzeichnung fuer Karten UND Bloecke.
 *
 * Rafael, 09.09.2026 05:2xZ: "es muss einfach ... gekennzeichnet sein ob etwas
 * am dev oder am partner server ist" — und ausdruecklich nicht nur ein kleines
 * Label zwischen anderen Angaben (das war der Stand bei der Karte allein, siehe
 * ihre Vorgeschichte 08.09. 17:39Z). Eine gefuellte Farbflaeche statt nur eines
 * Rahmens macht die Marke so gewichtig wie die Dringlichkeits-Chips daneben; das
 * Kuerzel daneben ist Absicht, nicht Zierde — zwei Toene, die auf einem grossen
 * Bildschirm klar verschieden sind, verschwimmen in einer kleinen Kachel neben
 * ohnehin farbigen Zustandsmarken oft zum selben Grauton. Dieselbe Komponente
 * laeuft im Guide UND im Remote-Guide (ein Quellcode, zwei Haeuser) — "gleich
 * aussehend" ist damit keine zweite Pflege, sondern eine Eigenschaft des Codes.
 */
function HausMarke({ haus }: { haus?: 'dev' | 'partner' | 'gemischt' | null }) {
  if (!haus) return null;
  const FARBE: Record<'dev' | 'partner' | 'gemischt', string> = {
    dev: 'var(--tn-link)', partner: 'var(--tn-orange)', gemischt: 'var(--tn-purple)',
  };
  const KUERZEL: Record<'dev' | 'partner' | 'gemischt', string> = { dev: 'DEV', partner: 'PRT', gemischt: 'MIX' };
  const TITEL: Record<'dev' | 'partner' | 'gemischt', string> = {
    dev: 'Die wartende Sitzung laeuft auf dem Dev-Server.',
    partner: 'Die wartende Sitzung laeuft auf dem Partner-Server — dort anmelden, um sie zu öffnen.',
    gemischt: 'Enthaelt offene Karten aus beiden Haeusern.',
  };
  return (
    <span
      title={TITEL[haus]}
      style={{
        fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
        background: FARBE[haus], color: 'var(--tn-bg)', letterSpacing: '0.3px', lineHeight: '13px',
      }}
    >
      {KUERZEL[haus]}
    </span>
  );
}

/**
 * DER EINE ZUSTELLWEG — wer antwortet, stellt auch zu.
 *
 * Koordinator, 09.09.2026 15:26Z, dreimal gemessen: 316 zugestellt, 57 offen, und
 * `zugestellt` bewegte sich nicht mehr. Aus der Fragenliste einer Buehne ist noch
 * nie eine Antwort bei einer Sitzung angekommen. Ursache war keine kaputte
 * Zustellung, sondern eine fehlende: `bestaetigen` und die Zeilenknoepfe prueften
 * nur `r.ok` und warfen den Rumpf der Antwort weg — dort liefert der Server
 * `nachricht` und `broadcast`. Zugestellt hat allein die ausgeschriebene Karte,
 * weil nur SIE diesen Weg kannte (er stand als useCallback in ihrer Komponente).
 * Rafaels zwei Report-Antworten von 13:42Z standen deshalb auf "beantwortet",
 * waehrend beide Sitzungen weiter warteten.
 *
 * Darum steht der Weg jetzt auf Modul-Ebene: jede Ansicht, die eine Antwort
 * abschickt, ruft dieselbe Funktion. Eine neue Antwort-Ansicht, die das vergisst,
 * ist der naechste stille Verlust — deshalb liegt hier auch der Fehlersatz
 * (`zustellFehlerSatz`), damit niemand den Rueckgabewert wegwerfen muss, um
 * fertig auszusehen.
 *
 * WARUM DER CLIENT UND NICHT DER SERVER: /api/mission/send verlangt eine echte
 * Benutzer-Sitzung, das interne Token deckt den Mission-Namensraum nicht ab
 * (Kopf von server/routes/guide.ts). Das trifft sich mit der Haus-Frage: dieselbe
 * Flaeche laeuft im Dev- UND im Partner-Haus, und /mission/send geht jeweils an
 * den EIGENEN Server — die Sitzung einer Partner-Karte ist von der Partner-CUI
 * aus erreichbar, vom Dev-CUI aus nicht. Ein Fehlgriff bleibt trotzdem kein
 * stiller: /send antwortet fuer eine unbekannte Sitzung mit 404, und das steht
 * danach als `zustellung[].zustand='fehlgeschlagen'` samt Wortlaut auf der Karte.
 */
type Zustellbefund = { sessionId: string; zustand: 'zugestellt' | 'fehlgeschlagen'; fehler?: string };

async function zustellenAn(
  karte: { id: string; wartet_auf: string[] },
  nachricht: string | null,
  broadcast?: Array<{ sessionId: string; nachricht: string }>,
): Promise<Zustellbefund[]> {
  const ziele: Array<{ sessionId: string; nachricht: string }> = [
    ...(nachricht ? karte.wartet_auf.map(sessionId => ({ sessionId, nachricht })) : []),
    ...(broadcast ?? []),
  ];
  const befunde: Zustellbefund[] = [];
  for (const { sessionId, nachricht: text } of ziele) {
    let ergebnis: Zustellbefund;
    /*
     * DIE DOPPEL-SEND-SPERRE IST KEIN GRUND, EINE ANTWORT WEGZUWERFEN.
     *
     * Von den sieben fehlgeschlagenen Zustellungen im Bestand (07.-08.09.2026)
     * waren VIER ein 409 "Fuer Session X laeuft bereits ein Senden (seit 17s /
     * 29s / 22s / 2s)" — die Zustellung traf auf eine Sitzung, der in genau
     * diesem Moment jemand anderes schrieb. Die Sperre in /mission/send ist
     * richtig (sie verhindert doppeltes Senden), aber sie dauert Sekunden, und
     * die Antwort war nach EINEM Versuch endgueltig verloren.
     *
     * Nur dieser eine Fall wird wiederholt. Die drei uebrigen Fehlschlaege waren
     * 503 "no account available" mit "loest sich in Stunden von selbst" — dagegen
     * hilft kein Warten von Sekunden; die gehoeren sichtbar liegen (siehe
     * NieAngekommen) und nicht in eine Schleife.
     */
    const WARTEN_MS = [4000, 12000];
    let versuch = 0;
    for (;;) {
      try {
        const r = await fetch(`${API}/mission/send`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accountId: 'auto', sessionId, message: text }),
        });
        if (r.ok) { ergebnis = { sessionId, zustand: 'zugestellt' }; break; }
        const koerper = (await r.text()).slice(0, 300);
        if (r.status === 409 && koerper.includes('sendInFlight') && versuch < WARTEN_MS.length) {
          await new Promise(f => setTimeout(f, WARTEN_MS[versuch]));
          versuch += 1;
          continue;
        }
        ergebnis = {
          sessionId, zustand: 'fehlgeschlagen',
          fehler: `${r.status}${versuch ? ` (nach ${versuch + 1} Versuchen)` : ''}: ${koerper}`,
        };
      } catch (e) {
        ergebnis = { sessionId, zustand: 'fehlgeschlagen', fehler: String(e).slice(0, 300) };
      }
      break;
    }
    befunde.push(ergebnis);
    /*
     * Das Ergebnis wird IMMER zurueckgemeldet, auch das schlechte: ein Eintrag,
     * der auf 'offen' stehen bleibt, sieht spaeter aus wie "noch unterwegs" und
     * nicht wie "nie losgeschickt" — genau daran waren die 57 nicht zu erkennen.
     */
    await fetch(`${API}/guide/karten/${karte.id}/zustellung`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ergebnis),
    }).catch(() => {});
  }
  return befunde;
}

/** Ein Satz ueber das, was NICHT angekommen ist — oder null, wenn alles ankam. */
function zustellFehlerSatz(titel: string, befunde: Zustellbefund[]): string | null {
  const kaputt = befunde.filter(b => b.zustand === 'fehlgeschlagen');
  if (!kaputt.length) return null;
  return `„${titel}“: deine Antwort ist gespeichert, aber ${kaputt.length === 1 ? 'die wartende Sitzung' : `${kaputt.length} wartende Sitzungen`} `
    + `hat sie nicht erreicht — ${kaputt.map(b => `${b.sessionId.slice(0, 8)}: ${b.fehler}`).join(' · ')}`;
}

// --- Panel --------------------------------------------------------------------

/**
 * DIE REGIE: den Zielarbeitsbereich fuer EINE Frage herrichten.
 *
 * Nicht "ein Panel dazulegen", sondern das Layout frisch schreiben, sodass nur zu
 * sehen ist, was fuer diese Entscheidung zaehlt. Rafael am 04.09., nachdem ein
 * Klick ihm vier fremde Chats und den Tool Hub aufgeklappt hat: "Jedes Mal werden
 * mir Mengen an Chats angezeigt, die ich gar nicht sehen muss."
 *
 * WARUM DAS HIER STEHT UND NICHT AUF DEM SERVER
 * Layouts liegen pro Benutzer (`<id>__<uid>.json`) und der control:apply-layout-
 * Rundruf wird beim Empfaenger nach uid gefiltert. Eine Session mit internem Token
 * schreibt die geteilte Altdatei und gilt als uid null — auf dem Dev-Server faellt
 * das nicht auf (Auth aus), auf dem Partner-Server kaeme bei ihm nichts an.
 * Geschrieben wird deshalb aus SEINEM Client, mit seiner Anmeldung.
 *
 * KEINE SITZUNG WIRD BEENDET. Ein Layout ist reine Ansicht: eine Kachel weniger
 * ist kein Chat weniger. Was aus dem Blick faellt, laeuft weiter — die Spalte sagt
 * es ihm sogar hin.
 */

/**
 * Panel-Komponenten, die ein Rezept nennen darf. Spiegel der Liste im LayoutManager.
 *
 * Exportiert fuer BuehnenVorschau.tsx (Deep-Link-Screenshot einer einzelnen Karte,
 * siehe dort): dieselbe Pruefung, die `herrichten` unten macht, muss dort VOR dem
 * Bauen der Buehne laufen, statt ein zweites Mal abgeschrieben zu werden.
 */
/*
 * WELCHE KACHELN EIN REZEPT NENNEN DARF — abgeglichen mit dem, was die Flaeche
 * WIRKLICH baut (LayoutManager.factory), nicht mit dem, was im Menue steht.
 *
 * Am 07.09. liefen die beiden Listen auseinander, in BEIDE Richtungen:
 *   - 'sub-sessions' und 'peer-awareness' standen hier, aber es gibt gar keine
 *     solche Kachel (kein Zweig in der factory, keine Komponente). Ein Rezept
 *     damit galt als geprueft und lieferte Rafael "Unknown panel" — ein Ja, das
 *     die Sache nicht deckt. Entfernt; kein Rezept nutzte sie (nachgesehen).
 *   - sieben Kacheln, die die Flaeche baut, waren hier verboten: deck-annotate,
 *     energy-cases, group-chat, report-cases und die drei sandbox-*. Ein
 *     gueltiges Rezept wurde also abgewiesen. Aufgenommen.
 *
 * Der Abgleich ist ab jetzt eine Pruefung (guide-panels.test.ts) und nicht mehr
 * eine Frage der Sorgfalt: wer eine Kachel hinzufuegt, sieht sofort, dass diese
 * Liste nachzuziehen ist.
 */

const GeleseneBuehne = z.object({ id: z.string().min(1), titel: z.string(), haus: z.enum(['dev', 'partner']),
  workspace: z.string().nullable().optional(), textblock: z.string(),
  schnellbuehne: SchnellbuehneSchema.optional(),
  rezept: z.object({ kacheln: z.array(z.object({ art: z.string() }).passthrough()) }).passthrough().nullable().optional(),
}).passthrough();
class BuehnenLeseFehler extends StageRefreshFehler {
  constructor(public id: string, public status: number) { super("http", buehnenLeseFehlermeldung(id, status)); }
}
/** Vergleich gegen das tatsächlich übergebene Rezept, niemals gegen den nachgeladenen Datenstand links. */
function rezeptStand(projectId: string, rezept: Layoutrezept, bezug?: BuehnenBezug, haus?: 'dev' | 'partner'): string {
  return JSON.stringify([projectId, haus, rezept, bezug]);
}

async function leseBuehne(id: string): Promise<BuehneAnsicht> {
  try {
    const data = await httpBoundary({ name: 'cui.buehne.lesen', url: buehnenLeseUrl(id), schema: GeleseneBuehne, timeoutMs: 10000,
      onStatus: status => status >= 400 ? { throw: new BuehnenLeseFehler(id, status) } : undefined });
    if (data.id !== id) throw new StageRefreshFehler('daten', 'Der empfangene Stand gehört zu einer anderen Bühne.');
    return data as unknown as BuehneAnsicht;
  } catch (error) {
    if (error instanceof BuehnenLeseFehler) throw error;
    if (error instanceof BoundaryParseError) throw new StageRefreshFehler('daten', 'Daten empfangen, aber ihr Format ist für diese Ansicht nicht gültig.');
    if (error instanceof BoundarySourceError) throw new StageRefreshFehler(error.message.includes('deadline') ? 'zeitgrenze' : 'verbindung',
      error.message.includes('deadline') ? 'Der Abruf hat die Zeitgrenze von 10 Sekunden erreicht.' : 'Die Verbindung für den Abruf ist fehlgeschlagen.');
    throw error;
  }
}
export const BEKANNTE_PANELS = new Set(['schnellbuehne',
  'activity-feed', 'architecture', 'background-ops', 'bridge-monitor', 'browser',
  'business-angel', 'business-docs', 'business-overview', 'sales', 'calendar', 'conversation-queue',
  'cui', 'cui-lite', 'deploy', 'error-monitor', 'feedback', 'gmail', 'images', 'infisical-monitor',
  'infrastructure', 'input-audit', 'knowledge', 'knowledge-fullscreen', 'linkedin', 'mail',
  'maintenance', 'mission', 'mission-chat', 'my-tasks', 'netzwerk', 'notes', 'partner-inbox', 'focus',
  'customer-comms', 'partner-server', 'partner-browsers', 'platform-admin',
  'preview', 'privat-angel', 'prompt-explorer', 'qa-dashboard', 'repo-dashboard', 'report-builder',
  'system-health', 'team-status', 'tool-hub', 'uploads', 'virtual-office', 'watchdog',
  'prod-chat',
  // Nachgetragen 07.09.: die Flaeche baut sie, das Rezept durfte sie nicht nennen.
  'deck-annotate', 'energy-cases', 'group-chat', 'report-cases',
  'sandbox-business-angel', 'sandbox-privat-angel', 'sandbox-rafael-angel',
  // 23.09.: Bericht-Anmerkungen (inhalt.reportId), fuer Partner-Buehnen.
  'report-annotate',
  // 05.10.: Rafaels vier Postfaecher + Kalender (inhalt.konto/nachricht/ansicht …), nur Haus Dev.
  'postfaecher',
  // 26.10.: Business-Ordner lesen (inhalt.offen/filter/suche/hilfsordner/archivOffen), nur Admin.
  'business-navigator',
]);

/**
 * Baut aus dem Rezept ein Layout-Dokument (flexlayout), eine Reihe, links nach rechts.
 *
 * Exportiert: BuehnenVorschau.tsx braucht GENAU diese Funktion, um aus einer Karte
 * dieselbe Buehne zu bauen, die `herrichten` unten fuer Rafaels echtes Fenster baut —
 * eine zweite, mitgepflegte Fassung wuerde ueber kurz oder lang abweichen (siehe
 * shared/guide-kachel-id.ts fuer denselben Grundsatz auf der Kennungs-Ebene).
 */

/**
 * DER BERATER-CHAT GEHOERT IN DIE BUEHNE — Rafael, 13.09.2026 10:27Z.
 *
 * Sein Wortlaut: „wenn jede buehne einen berater hat macht es auch sinn in der rechten
 * haelfte des chats den jeweiligen berater chat wirklich anzeigen - dann kann ich
 * entscheiden ob ich direkt auf die buehnenfragen antworte oder chatte". Der Zweck
 * steht im letzten Halbsatz: die WAHL. Links klicken ODER rechts reden, ohne die
 * Flaeche zu verlassen.
 *
 * WARUM HIER UND NICHT IM REZEPT. Gemessen am selben Tag ueber alle 29 lebenden
 * Buehnen: KEINE EINZIGE hat eine `sitzung`-Kachel, alle Rezepte sind reine Panels.
 * „Wirklich anzeigen" ist also woertlich zu nehmen. Es 29 Mal nachzutragen waere
 * ausserdem die falsche Stelle: das Rezept ist, was der Berater fuer SEINE SACHE
 * hergerichtet hat — er selbst kommt darin so wenig vor wie ein Redner in seinen
 * eigenen Folien. Und wer an der Buehne sitzt, ist Zustand (`berater[]` wird bei jedem
 * Abruf gerechnet); in einem gespeicherten Rezept veraltete es still.
 *
 * DIE HAELFTE, NICHT EIN DRITTEL. `baueBuehne` gibt jedem Tabset ohne `breite` das
 * Gewicht `round(100/n)`. Mit `g` Gruppen vor dem Hinzufuegen bekommt die Chat-Kachel
 * `g * round(100/(g+1))` — also so viel wie alle anderen zusammen. Bei einem Panel ist
 * das halb/halb, bei zweien auch.
 */
export function rezeptMitBeraterchat(b: BuehneAnsicht): { rezept: Layoutrezept; berater: BeraterZustand | null; ohne: string | null } {
  const rezept = b.rezept!;
  const berater = aktuellerBerater(b);
  /*
   * DREI GRUENDE, KEINE KACHEL ZU BAUEN — und jeder wird gesagt statt verschwiegen.
   * Der dritte ist der heikle: kennt DIESER Prozess das Transkript nicht (kein
   * `konto`), oeffnet eine Sitzungs-Kachel ein leeres Fenster, das wie ein Defekt
   * aussieht (CLAUDE.md, `/api/capture/buehne`). Dann lieber der Satz dazu.
   */
  if (!berater) {
    return { rezept, berater: null, ohne: b.stumm_erlaubt ? null : 'Diese Bühne hat keinen eingetragenen Berater.' };
  }
  if (!berater.konto) {
    return { rezept, berater, ohne: `Der Chat von ${berater.sitzung.slice(0, 8)} liegt nicht in diesem Haus — `
      + 'er lässt sich hier nur nicht anzeigen. Deine Nachricht erreicht ihn trotzdem.' };
  }
  if (rezept.kacheln.some(k => k.art === 'sitzung' && k.sessionId === berater.sitzung)) {
    return { rezept, berater, ohne: null };            // das Rezept hat ihn schon
  }
  const zeile = Math.min(0, ...rezept.kacheln.map(k => (Number.isFinite(k.zeile) ? Number(k.zeile) : 0)));
  const inZeile = rezept.kacheln.filter(k => (Number.isFinite(k.zeile) ? Number(k.zeile) : 0) === zeile);
  const gruppen = Math.max(1, new Set(inZeile.map((k, i) => (k.gruppe?.trim() ? `g:${k.gruppe.trim()}` : `k:${i}`))).size);
  return {
    rezept: {
      ...rezept,
      kacheln: [...rezept.kacheln, {
        art: 'sitzung', sessionId: berater.sitzung, accountId: berater.konto,
        name: berater.titel ? `💬 ${berater.titel}` : '💬 Berater',
        zeile, breite: gruppen * Math.round(100 / (gruppen + 1)),
      }],
    },
    berater,
    ohne: null,
  };
}

/**
 * DIE ABLOESUNGSKETTE ZWISCHEN BUEHNEN — Grundlage fuer den Vor/Zurueck-Knopf.
 *
 * Rafael, 15.09.2026: „ein direkter button wo ich von einer buehne auf die
 * naechste gehen kann mit nach vor und zurueck fehlt noch". Die Kette gibt es
 * am Objekt schon (`server/lib/guide-buehnen.ts`, `BuehneSchema.vorgaenger`/
 * `nachfolger`/`weitere_nachfolger`) — nur der Knopf fehlte. Eine reine
 * Ableitung aus der schon geladenen Buehne, damit sie ohne Netzwerk und ohne
 * Mounten der ganzen Flaeche testbar ist.
 *
 * `zurueck` kann mehr als eine Kennung tragen (Vereinigung zweier Buehnen,
 * `vereinige()`), `vor` ebenso (1:N-Abloesung, `weitere_nachfolger`) — beide
 * Richtungen bleiben deshalb Listen statt einer einzelnen Kennung.
 */
export function benachbarteBuehnen(b: BuehneAnsicht): { zurueck: string[]; vor: string[] } {
  return {
    zurueck: b.vorgaenger ?? [],
    vor: [b.nachfolger, ...(b.weitere_nachfolger ?? [])].filter((x): x is string => !!x),
  };
}

/**
 * EINE ABGELOESTE BUEHNE OEFFNET IHRE NACHFOLGERIN (19.10.2026).
 *
 * Gemessen: ein Partner klickte eine abgeloeste Buehne an (Deep-Link, Angebot) — ohne Archivfoto
 * scheiterte das Oeffnen in `buehneOeffnen` mit `stage_open_failed`/`unavailable`, und der einzige
 * Weg zur Nachfolgerin war ein Knopf IN der offenen Buehne, die nie aufging. Jetzt folgt das
 * Oeffnen der Kette `nachfolger` (bzw. der ersten von `weitere_nachfolger`) bis zur ersten Buehne,
 * die nicht mehr abgeloest weitergereicht wird.
 *
 * KREISSCHUTZ: eine Kennung zweimal oder mehr als `max` Schritte ist ein benannter Fehler, kein
 * endloses Laden. Ein Lesefehler unterwegs ebenso — nie still die alte Buehne stehen lassen.
 */
export async function folgeAbloesung(
  start: BuehneAnsicht,
  lese: (id: string) => Promise<BuehneAnsicht>,
  max = 20,
): Promise<{ ziel: BuehneAnsicht; kette: string[] } | { fehler: string }> {
  const kette: string[] = [];
  const gesehen = new Set([start.id]);
  let jetzt = start;
  while (jetzt.abschluss?.art === 'abgeloest') {
    const naechste = benachbarteBuehnen(jetzt).vor[0];
    if (!naechste) break;
    if (gesehen.has(naechste)) {
      return { fehler: `„${start.titel}" wurde abgelöst, aber die Ablösungskette läuft im Kreis (${[start.id, ...kette, naechste].join(' → ')}). Bitte dem Assistenten melden.` };
    }
    if (kette.length >= max) {
      return { fehler: `„${start.titel}" wurde abgelöst, die Kette ist länger als ${max} Schritte (${[start.id, ...kette].join(' → ')} …). Bitte dem Assistenten melden.` };
    }
    try { jetzt = await lese(naechste); }
    catch (err) {
      return { fehler: `„${start.titel}" wurde abgelöst, die Nachfolgerin „${naechste}" ließ sich nicht laden: ${err instanceof Error ? err.message : String(err)}` };
    }
    gesehen.add(naechste);
    kette.push(naechste);
  }
  return { ziel: jetzt, kette };
}

/** Sichtbarer Satz, wenn eine abgeschlossene Buehne weder Nachfolgerin noch Archivfoto hat. */
export function hinweisOhneFotoUndNachfolger(b: BuehneAnsicht): string {
  const art = b.abschluss?.art === 'abgeloest' ? 'wurde abgelöst' : 'ist abgeschlossen';
  const fehlt = b.bild && 'fehlt' in b.bild && b.bild.fehlt ? ` (${b.bild.fehlt})` : '';
  return `„${b.titel}" ${art} und lässt sich nicht mehr öffnen: es gibt weder eine Nachfolgerin noch ein Archivfoto${fehlt}.`;
}

export function layoutAusRezept(rezept: Layoutrezept, alt: any, auswahl?: BuehnenAuswahl, haus?: 'dev' | 'partner'): { doc: any; sitzungenNeu: string[] } {
  const { doc, sitzungen } = baueBuehne(rezept.kacheln as BauKachel[], alt, auswahl);
  if (haus) vorschauImHausDerBuehne(doc, haus);
  return { doc, sitzungenNeu: sitzungen };
}

/**
 * VORSCHAU-KACHELN LESEN IM HAUS DER BUEHNE (25.10.2026, b-engelmann-phasen-haendisch-20261025).
 * `inhalt.datei` ist ein Pfad auf dem Rechner, in dem die Buehne lebt; das Fenster kann im
 * anderen Haus laufen (ein Guide fuer beide). Das Haus geht in die Kachel-config, NACH dem Bau:
 * die Kachel-Kennung (Abdruck von `inhalt`) bleibt dieselbe, die auch der Server rechnet.
 */
function vorschauImHausDerBuehne(knoten: any, haus: 'dev' | 'partner'): void {
  if (!knoten || typeof knoten !== 'object') return;
  if (knoten.type === 'tab' && knoten.component === 'preview') knoten.config = { ...(knoten.config ?? {}), haus };
  for (const wert of Object.values(knoten)) {
    if (Array.isArray(wert)) wert.forEach(k => vorschauImHausDerBuehne(k, haus));
    else if (wert && typeof wert === 'object') vorschauImHausDerBuehne(wert, haus);
  }
}

/**
 * Welcher Reiter fuer DIESE Karte vorn stehen soll, wenn ihre Buehne mit
 * Geschwistern geteilt ist (siehe `BuehnenAuswahl` in shared/guide-layout.ts).
 * Kommt aus der Karte selbst (`grundlage`, `wartet_auf`), nicht aus dem Rezept
 * — das Rezept ist bei Geschwistern absichtlich identisch.
 */
export function auswahlFuerKarte(karte: { grundlage?: string | null; wartet_auf?: string[] } | null | undefined): BuehnenAuswahl | undefined {
  if (!karte) return undefined;
  // `grundlage` ist ein Pfad/Dateiname (wie im Tor, guide.ts), keine URL — anders
  // als die Browser-Kachel-Adresse, die `seitenDateiname` sonst zerlegt.
  const seite = karte.grundlage ? karte.grundlage.split('/').pop()?.toLowerCase() ?? null : null;
  return { seite, sitzungen: karte.wartet_auf };
}


/*
 * DIE BUEHNENREIHE IST DIE SPALTE GEWORDEN (11.09.2026).
 *
 * Hier stand `BuehnenReihe`: eine Chip-Zeile ueber der alten Themenwelt. Sie war der
 * erste Schritt — ein Klick zu jeder Buehne —, aber sie lag OBEN AUF der alten
 * Haelfte (Karten-Reihe, "Alle Boards", Bloecke), und Rafael sah zwei Welten
 * uebereinander. Die Anordnung, die er wollte (Zeitstrahl, Vor dir, die vier Raeume,
 * Filterleiste), steht jetzt in `GuideHof.tsx` und IST der Rumpf der Spalte.
 *
 * Der Typ der Zeile wandert mit: er beschreibt, was `/api/guide/buehnen` liefert, und
 * gehoert damit neben die Ansicht, die ihn liest — nicht in diese Datei.
 */

/* ══════════════════ DIE ENTSCHEIDUNG AN DER BUEHNE ══════════════════
 *
 * Rafaels Trennung vom 11.09.: es gibt das KONTEXT-HTML (die Buehne, zum Lesen) und
 * die ENTSCHEIDUNG (zum Klicken). Trennlinie: klickt er das, oder liest er das?
 * Hier wird geklickt. Alles, was erklaert, steht rechts im hergerichteten Fenster.
 *
 * WAS TROTZDEM MITKOMMT, ist das, was er zum Klicken BRAUCHT und nirgends sonst
 * nachschlagen soll: die Frage, je Option ihre Folge, die Empfehlung mit Begruendung,
 * das staerkste Gegenargument, und was passiert, wenn er gar nicht antwortet. Genau
 * diese Felder misst der Hof-Test an seiner guten Fixture — eine Option ohne Folge
 * ist dort "eine Umfrage, keine Entscheidung".
 */
/* ══════════════════ WAS GETIPPT IST, UEBERLEBT DEN NEUAUFBAU ══════════════════
 *
 * Rafael, 13.09.2026 ~05:5xZ: *"ausserdem haengt er oft nach dem ,wird gesendet' fuer
 * ewig statt dass er mir die naechste buehne vorlegt"*. Was danach passiert, hat er am
 * selben Morgen erlebt: Auswahl und Notizen zu vier Fragen waren getippt, der Aufruf
 * lief in seinen Zeitausstieg, das Panel baute neu auf — und die Flaeche war leer. Die
 * Zustandsfelder lagen ausschliesslich im `useState` der Fragenliste, und die stirbt
 * mit jedem Neuaufbau: zurueck zu den Raeumen, Panel-Wechsel, Fehlschlag mit Nachladen.
 *
 * DER ENTWURF GEHOERT ZUR BUEHNE, NICHT ZUM FENSTER. Deshalb liegt er unter einem
 * Schluessel je Buehne im `localStorage` und nicht in einem React-Zustand: er soll
 * genau so lange leben, wie die Frage offen ist, und keine Sekunde laenger.
 *
 * ER VERSCHWINDET NICHT BEIM ABSCHICKEN, SONDERN BEIM ANKOMMEN. Zwei Wege, und beide
 * werden gebraucht: die Sende-Funktionen raeumen die Fragen weg, deren Antwort der
 * Server ANGENOMMEN hat (ein 502 zaehlt dazu — "gespeichert, nur nicht zugestellt";
 * ein Zeitausstieg nicht), und `gueltig` raeumt beim naechsten Oeffnen alles weg, was
 * der Server nicht mehr als offene Frage fuehrt. Ohne das Erste bliebe der Entwurf im
 * Browser liegen, bis Rafael dieselbe Buehne noch einmal oeffnet (gemessen im
 * Beweislauf 13.09.); ohne das Zweite bliebe er stehen, wenn die Antwort anderswo
 * hereinkam — etwa aus einem zweiten Fenster.
 *
 * KEIN STILLES SCHLUCKEN, ABER AUCH KEIN ABSTURZ: ist `localStorage` nicht
 * beschreibbar (voller Speicher, gesperrter Kontext), steht das in der Konsole und die
 * Eingabe funktioniert weiter — nur eben ohne Netz. Ein Fehlerbanner dafuer wuerde die
 * Flaeche blockieren, um vor dem Verlust von etwas zu warnen, das noch gar nicht
 * getippt ist.
 */
const ENTWURF_PRAEFIX = 'guide-entwurf';

function useEntwurf(
  schluessel: string,
  gueltig: readonly string[],
): [Record<string, string>, React.Dispatch<React.SetStateAction<Record<string, string>>>, (ids: string[]) => void] {
  const lies = useCallback((): Record<string, string> => {
    try {
      const roh = window.localStorage.getItem(schluessel);
      if (!roh) return {};
      const geparst: unknown = JSON.parse(roh);
      /* Der Typ wird geprueft, nicht geraten: ein fremder/alter Eintrag unter demselben
         Schluessel darf nicht als Entwurf durchgehen und spaeter im Absenden landen. */
      if (!geparst || typeof geparst !== 'object' || Array.isArray(geparst)) return {};
      return Object.fromEntries(
        Object.entries(geparst as Record<string, unknown>).filter(([, v]) => typeof v === 'string'),
      ) as Record<string, string>;
    } catch (err) {
      console.error(`[guide] Entwurf "${schluessel}" nicht lesbar — es wird leer begonnen:`, err);
      return {};
    }
  }, [schluessel]);
  const [wert, setWert] = useState<Record<string, string>>(lies);
  /* Die Sendeschlange belegt nach einer umgezogenen Frage die neue Fassung vor
     (`vorbelegeEntwurf`). Ist die Buehne dabei offen, muss die Wahl hier sichtbar werden —
     sonst ueberschriebe der naechste Tastendruck den vorbelegten Speicher wieder. */
  useEffect(() => {
    const neu = (ev: Event) => { if ((ev as CustomEvent<{ schluessel?: string }>).detail?.schluessel === schluessel) setWert(lies()); };
    window.addEventListener(ENTWURF_VORBELEGT, neu);
    /* Ein ZWEITES Fenster derselben Buehne (gemeinsamer Speicher): aendert es den Entwurf — auch die Quittung leert ihn —, laedt dieses ihn neu. */
    const fremd = (ev: StorageEvent) => { if (ev.key === schluessel || ev.key === null) setWert(lies()); };
    window.addEventListener('storage', fremd);
    return () => { window.removeEventListener(ENTWURF_VORBELEGT, neu); window.removeEventListener('storage', fremd); };
  }, [schluessel, lies]);

  useEffect(() => {
    try {
      if (Object.keys(wert).length === 0) window.localStorage.removeItem(schluessel);
      else window.localStorage.setItem(schluessel, JSON.stringify(wert));
    } catch (err) {
      console.error(`[guide] Entwurf "${schluessel}" nicht speicherbar — er ueberlebt den naechsten Neuaufbau NICHT:`, err);
    }
  }, [schluessel, wert]);

  /* Aufraeumen an EINER Stelle: was der Server nicht mehr als offene Frage fuehrt,
     ist beantwortet (oder zurueckgezogen) und braucht keinen Entwurf mehr. Der
     Vergleich laeuft ueber eine stabile Zeichenkette, weil ein Feld bei jedem
     Aufbau eine neue Identitaet haette und die Wirkung sonst endlos liefe. */
  const gueltigMarke = [...gueltig].sort().join('\u0000');
  useEffect(() => {
    const erlaubt = new Set(gueltigMarke ? gueltigMarke.split('\u0000') : []);
    setWert(alt => {
      const gefiltert = Object.fromEntries(Object.entries(alt).filter(([k]) => erlaubt.has(k)));
      return Object.keys(gefiltert).length === Object.keys(alt).length ? alt : gefiltert;
    });
  }, [gueltigMarke]);

  /** Was angekommen ist, braucht keinen Entwurf mehr — gezielt, nicht pauschal:
   *  eine Frage, deren Antwort der Server abgewiesen hat, behaelt ihre Eingabe. */
  const vergiss = useCallback((ids: string[]) => {
    if (!ids.length) return;
    setWert(alt => {
      if (!ids.some(id => id in alt)) return alt;
      const naechst = { ...alt };
      for (const id of ids) delete naechst[id];
      return naechst;
    });
  }, []);

  return [wert, setWert, vergiss];
}

/**
 * VERGANGENES WIRD ANGEMERKT, NICHT AUSGEBREITET.
 *
 * Rafael, 13.09.2026: *„das entshcieudngsfenster soll nur neue sachen anzeigen -
 * vergangen sollen hoechstens angemekrt und ueber die buehnen screenshots angezeigt
 * werden"*, und tags zuvor schon: *„es sollen immer nur die aktuellen Fragen
 * angezeigt werden"*.
 *
 * BIS HEUTE STAND DAS GEGENTEIL DA. Beantwortete und zurueckgezogene Fragen wurden
 * als volle `EntscheidungsBlock` gezeichnet, mit Sachlage, allen Optionen und
 * Empfehlung — und wegen der Reihenfolge OBERHALB der lebenden. Gemessen am 13.09.
 * an `b-hofstaat-guide-20260913`: 11 Entscheidungen, 3 offen, 8 erledigt. Rafael
 * scrollte an acht toten Bloecken vorbei, um die drei zu finden, die ihn angehen.
 *
 * (Ein Kommentar am `EntscheidungsBlock` behauptete seit dem 11.09., der Aufrufer
 * halte Zurueckgezogenes „ohnehin aus dieser Ansicht heraus (siehe
 * ZurueckgezogeneZeile)". Diese Komponente gab es nie. Der Satz stimmt erst jetzt.)
 *
 * EINE ZEILE, AUFKLAPPBAR — nicht weg. Was Rafael entschieden hat, bleibt
 * nachlesbar; es beansprucht nur nicht mehr die Flaeche der offenen Frage. Beim
 * Aufklappen steht je Frage EINE Zeile: Titel und was daraus wurde. Die volle
 * Sachlage steht auf der Entscheidungsseite der Buehne, nicht hier.
 *
 * OBEN, NICHT UNTEN: Rafael, 12.09. 15:12Z, mit Bild — *„es macht keinen sinn das
 * unter dem absenden noch was steht"*. Sein Absenden-Knopf bleibt das Ende.
 */
function ErledigteAnmerkung({ erledigt }: { erledigt: BuehnenEntscheidung[] }) {
  const [offen, setOffen] = useState(false);
  if (erledigt.length === 0) return null;
  const beantwortet = erledigt.filter(e => e.antwort).length;
  const umgezogen = erledigt.filter(e => e.umgezogen).length;
  const zurueck = erledigt.length - beantwortet - umgezogen;
  const zuletzt = erledigt
    .map(e => e.antwort?.am ?? e.zurueckgezogen?.am ?? e.umgezogen?.am ?? e.gestellt_am)
    .filter(Boolean)
    .sort()
    .at(-1) ?? null;
  /* "woanders weitergestellt" ist NICHT "zurückgezogen" — die Frage wartet weiter auf
   * eine Antwort, nur an einer anderen Buehne. Beides in eine Zahl zu werfen hiesse,
   * Rafael Erledigung zu zeigen, wo noch etwas auf ihn wartet. */
  const teile = [
    beantwortet ? `${beantwortet} beantwortet` : null,
    zurueck ? `${zurueck} zurückgezogen` : null,
    umgezogen ? `${umgezogen} woanders weitergestellt` : null,
  ].filter(Boolean).join(', ');
  return (
    <div data-ai-id="guide-buehne-erledigtes" style={{ ...box, marginBottom: 8, fontSize: 11, color: 'var(--tn-text-muted)' }}>
      <button
        type="button"
        onClick={() => setOffen(o => !o)}
        style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit',
          cursor: 'pointer', textAlign: 'left', width: '100%' }}
      >
        {offen ? '▾' : '▸'} Vorher entschieden: {teile}
        {zuletzt ? ` · zuletzt ${new Date(zuletzt).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' })}` : ''}
      </button>
      {offen && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {erledigt.map(e => (
            <div key={e.id} data-ai-id={`guide-buehne-erledigt-${e.id}`} style={{ lineHeight: 1.4 }}>
              <span style={{ color: 'var(--tn-text)' }}>{e.titel}</span>
              {' — '}
              {e.antwort
                ? `deine Antwort: ${e.antwort.kuerzel ? `${e.antwort.kuerzel} · ` : ''}${e.antwort.text}`
                : e.umgezogen
                  ? `steht jetzt an ${e.umgezogen.buehne} (${e.umgezogen.grund})`
                  : `zurückgezogen von ${e.zurueckgezogen?.von ?? 'unbekannt'}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * DIE LUECKE ZWISCHEN "ZUGESTELLT" UND "ER ARBEITET WIRKLICH" (Kanzler, 17.09.2026
 * ~14:5xZ, im Auftrag zur sofortigen Bewegung nach Rafaels Klick): eine bestaetigte
 * Zustellung heisst nur, dass die Nachricht die Sitzung erreicht hat — nicht, dass
 * der Prozess (nach Kaltstart oder Wiederaufnahme) sie schon verarbeitet. Der Server
 * broadcastet `cui-state: processing` genau in dem Moment, in dem das passiert
 * (claude-cli.ts) — SessionStore.tsx haelt das schon als `sessionStates` je
 * Sitzungs-ID. Keine neue Warteschlange, kein Tor: nur dieses vorhandene Signal
 * sichtbar gemacht, dort wo bisher nur "Verarbeitung noch offen" stand.
 */
function useBeraterArbeitetZeile(buehne: BuehneAnsicht): string | null {
  const { sessionStates } = useSessionStore();
  /*
   * DIESELBE QUELLE WIE DIE REIHE (Bauauftrag B, 21.10.2026). Bis hierher las diese Zeile den Live-Zustand des
   * Chats (`sessionStates`) und schrieb „Berater arbeitet gerade", während die Reihe aus `buehnenLage` am Server
   * dieselbe Bühne unter „Wartet auf dich" führte — Rafael 20.10. 09:02Z: „da steht berater abreitt gerade aber die
   * sessiun steht nicht im ostzingsaaö". Der Chat kann für eine ANDERE Bühne arbeiten (Bindung, hof-arbeitsgang.ts)
   * oder die Messung der Liste ist noch nicht nach; in beiden Fällen gilt, was die Reihe sagt. Der Live-Zustand
   * liefert nur noch das Werkzeug als Zusatz, wenn der Server Arbeit an DIESER Bühne misst.
   *
   * AUSNAHME, DIE BLEIBT (Zweitprüfung 21.10.2026, B4): hängt der Berater dieser Bühne an einer Rückfrage oder
   * Freigabe (`needs_attention`), ist das für Rafael die eine Information, die erklärt, warum im Sitzungssaal
   * nichts weitergeht — sie steht, solange die Bühne im Saal ist, unabhängig vom gemessenen Bereich.
   */
  if (buehne.raum !== 'sitzungssaal') return null;
  const sid = aktuellerBerater(buehne)?.sitzung;
  const stand = sid ? sessionStates.get(sid) : undefined;
  if (stand?.state === 'needs_attention') return '● Berater wartet auf eine Rückmeldung.';
  if (buehne.saal_bereich !== 'arbeitet') return null;
  const werkzeug = stand?.state === 'working' ? stand.toolInfo?.toolName : undefined;
  return werkzeug ? `● Berater arbeitet gerade (${werkzeug}) …` : '● Berater arbeitet gerade …';
}

function EntscheidungsBlock({ buehne, e, onFehler, onNachladen }: {
  buehne: BuehneAnsicht;
  e: BuehnenEntscheidung;
  onFehler: (t: string | null) => void;
  onNachladen: () => void;
}) {
  const { enqueue } = useGuideSend();
  const [laeuft, setLaeuft] = useState<string | null>(null);
  /*
   * DIE NOTIZ LIEGT IM ENTWURF DIESER BUEHNE, nicht im Fenster (siehe `useEntwurf`).
   * Derselbe Schluessel wie in `EntscheidungenListe`: welche der beiden Ansichten
   * gerade rendert, haengt an der Zahl der offenen Fragen und kann sich mitten in
   * Rafaels Runde aendern — ein getippter Satz darf dabei nicht verschwinden.
   */
  const offeneIds = buehne.entscheidungen.filter(istOffeneFrage).map(x => x.id);
  const [notizen, setNotizen, vergissNotiz] = useEntwurf(`${ENTWURF_PRAEFIX}:${buehne.id}:notiz`, offeneIds);
  const notiz = notizen[e.id] ?? '';
  const setNotiz = useCallback((t: string) => setNotizen(n => {
    const naechst = { ...n };
    if (t) naechst[e.id] = t; else delete naechst[e.id];
    return naechst;
  }), [e.id, setNotizen]);
  const [notizSendend, setNotizSendend] = useState(false);
  /* Nur fuer den Hover-Rahmen der Optionen unten — siehe dort. */
  const [hoverKuerzel, setHoverKuerzel] = useState<string | null>(null);

  /*
   * AUSWAEHLEN, DANN ABSENDEN - AUCH BEI EINER EINZIGEN FRAGE.
   *
   * Rafael, 20.09.2026: *"es ist immer noch so dass entscheidungen teilweise nach dem
   * klicken einer option weggeschickt werden statt wenn ich unten auf alle senden
   * klicke."* Bis heute sendete ein Options-Klick hier SOFORT.
   *
   * Das war seine eigene Regel vom 12.09. ("ausser es ist nur eine frage - dann wird
   * direkt abgeschickt"), nur zielte sie auf eine Buehne, die EINE Frage HAT - nicht
   * auf eine, bei der eine Frage UEBRIG IST. Die Zahl der offenen Fragen schrumpft
   * aber, waehrend er antwortet: drei Fragen stehen im Sammel-Modus, er beantwortet
   * zwei, und die dritte kippt still in den Sofort-Modus. Der naechste Klick ist weg,
   * ohne dass sich sichtbar etwas geaendert haette. Gemessen 20.09.: 10 von 29 Buehnen
   * standen im Sofort-Modus, VIER davon waren erst hineingerutscht. Der Kommentar am
   * Entwurfsschluessel oben wusste das bereits ("kann sich mitten in Rafaels Runde
   * aendern") - er rettete nur den getippten Satz, nicht den Klick.
   *
   * Sein Wort von heute ist das juengere und gilt: ein Klick waehlt, der Knopf sendet.
   *
   * DERSELBE ENTWURFSSCHLUESSEL WIE IN `EntscheidungenListe` (`:wahl`). Kippt die
   * Ansicht mitten in der Runde, bleibt die getroffene Wahl stehen - genauso wie die
   * Notiz daneben es seit 13.09. tut.
   */
  const [wahlen, setWahlen] = useEntwurf(ENTWURF_PRAEFIX + ':' + buehne.id + ':wahl', offeneIds);
  const wahl = wahlen[e.id] ?? '';

  /* EIN Absenden-Knopf je Buehne (Phase 2 der Guide-Flaeche): diese Frage meldet nur, was bei ihr gewaehlt/notiert ist und wie es eingereiht wird; abgeschickt wird mit dem
     einen Knopf unten (oder Enter) — zusammen mit dem freien Wort. */
  const absende = useAbsenden();
  const sperreFrage = e.antwort !== undefined || e.zustand === 'zurueckgezogen' || e.zustand === 'umgezogen';
  useAbsendeAnbieter(`antwort:${e.id}`, {
    art: 'antwort', anzahl: sperreFrage || (!wahlen[e.id] && !notiz.trim()) ? 0 : 1,
    beschreibung: `„${e.titel}“ → ${wahlen[e.id] ? `${wahlen[e.id]})` : 'dein Satz'}${wahlen[e.id] && notiz.trim() ? ' + Notiz' : ''}`,
    einreihen: () => {
      const kuerzel = wahlen[e.id];
      const text = notiz.trim();
      enqueue(buehne, [kuerzel ? { eid: e.id, kuerzel, ...(text ? { notiz: text } : {}) } : { eid: e.id, freitext: text }]);
      onFehler(null);
    },
  });

  const arbeitetZeile = useBeraterArbeitetZeile(buehne);
  const beantwortet = e.antwort !== undefined;
  /*
   * GESPERRT IST MEHR ALS BEANTWORTET. Der Zustand einer Entscheidung ist mehrere
   * unabhaengige Felder (antwort, zurueckgezogen, zustellung, stufe) — hier zaehlt
   * fuer "darf noch geklickt werden" nur, ob sie noch OFFEN ist. Vorher pruefte dieser
   * Block ausschliesslich `beantwortet` (= e.antwort gesetzt); eine zurueckgezogene
   * Frage hat NIE eine Antwort, blieb also klickbar und lief garantiert in das 409
   * von `beantworte()` (guide-buehnen.ts). Gemessen an b-kundenbericht-bacher-20260911
   * (11.09., Meldung von adbda171): genau dieser Fall, an der einzigen Buehne, die
   * gerade vor Rafael steht. Der Aufrufer (GuidePanel) haelt zurueckgezogene Fragen
   * inzwischen ohnehin aus dieser Ansicht heraus (siehe ZurueckgezogeneZeile) — diese
   * Pruefung bleibt trotzdem, weil eine Komponente ihre eigene Klick-Sicherheit nicht
   * von der Sorgfalt ihres Aufrufers abhaengig machen sollte.
   */
  /* Seit 13.09.2026 auch `umgezogen`: die Frage steht woanders, ein Klick hier lief
   * in dasselbe 409 wie bei der zurueckgezogenen. */
  const gesperrt = beantwortet || e.zustand === 'zurueckgezogen' || e.zustand === 'umgezogen';
  return (
    <div data-ai-id={`guide-buehne-entscheidung-${e.id}`} data-guide-zustand={e.zustand} style={{ ...box, marginBottom: 8, fontSize: 11.5 }}>
      <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {/* DIE ZAHL ALLEIN SAGT NICHTS — Rafael fragte, was Stufe 1/2 bedeuten,
            obwohl sie hier stand. STUFE_WORT ist dieselbe Erklaerung wie im Hof. */}
        Stufe {e.stufe} ({STUFE_WORT[e.stufe] ?? 'unbekannt'}) · {e.zustand}
      </div>
      <div style={{ fontWeight: 600, margin: '4px 0 2px', lineHeight: 1.35 }}>{e.titel}</div>
      <div style={{ marginBottom: 8, lineHeight: 1.45 }}>{e.entscheidung}</div>

      {e.optionen.map(o => {
        const gewaehlt = e.antwort?.kuerzel === o.kuerzel || wahl === o.kuerzel;
        const sendet = laeuft === o.kuerzel;
        const hover = hoverKuerzel === o.kuerzel && !gesperrt && laeuft === null;
        /*
         * SICHTBAR ALS KNOPF, NICHT ALS TEXTKASTEN (Rafael, 12.09.: "es gibt keine
         * sauberen buttons"). Vorher trug ein unausgewaehlter Knopf `border:
         * var(--tn-border)` — dieselbe Randfarbe wie jeder reine Info-Kasten auf
         * dieser Buehne (`box`-Konstante) und `background: var(--tn-bg)`, kaum
         * unterscheidbar von `var(--tn-surface)` darunter. Klickbar war er immer,
         * NUR nicht erkennbar. Jetzt traegt jeder unbeantwortete Knopf durchgehend
         * einen Akzent-Rand plus einer eigenen Flaeche; Hover hebt ihn zusaetzlich.
         *
         * RUECKMELDUNG SOFORT NACH DEM KLICK (Rafael, 12.09., zweite Runde: "ich
         * wuerd erwarten dass ich da reinschreib und dann wird das gruen oder
         * irgendwie"). Vorher aenderte sich am Knopf zwischen Klick und Server-
         * Antwort NICHTS Sichtbares — nur `disabled` griff, Cursor blieb 'pointer'.
         * Zwischen Klick und Antwort (laeuft === o.kuerzel) zeigt der Knopf jetzt
         * sofort einen eigenen Zustand ("wird gesendet …"), unabhaengig davon, wie
         * lange die Anfrage braucht.
         */
        return (
          <button
            key={o.kuerzel}
            type="button"
            data-ai-id={`guide-entscheidung-${e.id}-${o.kuerzel}`}
            disabled={gesperrt || laeuft !== null}
            onClick={() => setWahlen(w => {
              if (w[e.id] === o.kuerzel) { const n = { ...w }; delete n[e.id]; return n; }
              return { ...w, [e.id]: o.kuerzel };
            })}
            onMouseEnter={() => setHoverKuerzel(o.kuerzel)}
            onMouseLeave={() => setHoverKuerzel(null)}
            title={o.folge}
            style={{
              display: 'block', width: '100%', textAlign: 'left', marginBottom: 5, padding: '7px 9px',
              borderRadius: 7, fontSize: 11.5, lineHeight: 1.4,
              cursor: gesperrt ? 'default' : sendet ? 'wait' : 'pointer',
              background: gewaehlt || sendet
                ? 'var(--tn-accent,var(--tn-link))33'
                : gesperrt
                  ? 'var(--tn-bg)'
                  : hover ? 'var(--tn-accent,var(--tn-link))33' : 'var(--tn-accent,var(--tn-link))11',
              border: `1px solid ${gewaehlt || sendet || !gesperrt ? 'var(--tn-accent,var(--tn-link))' : 'var(--tn-border)'}`,
              boxShadow: hover ? '0 0 0 1px var(--tn-accent,var(--tn-link))' : 'none',
              color: gesperrt && !gewaehlt ? 'var(--tn-text-muted)' : 'var(--tn-text)',
              opacity: gesperrt && !gewaehlt ? 0.55 : (laeuft !== null && !sendet ? 0.4 : 1),
            }}
          >
            <b>{o.kuerzel})</b> {o.text}{sendet ? ' — wird gesendet …'
              : wahl === o.kuerzel ? ' — gewählt, noch nicht gesendet (nochmal klicken: abwählen)' : ''}
            {/* DIE FOLGE STEHT MIT DA, NICHT NUR IM title. Ohne sie waere der Klick
                eine Wahl zwischen Buchstaben — der Hof-Test nennt das eine Umfrage. */}
            <div style={{ color: 'var(--tn-text-muted)', marginTop: 3 }}>→ {o.folge}</div>
          </button>
        );
      })}

      {!gesperrt && (
        <input
          data-ai-id={`guide-entscheidung-notiz-${e.id}`}
          value={notiz}
          onChange={ev => setNotiz(ev.target.value)}
          /*
           * ENTER DARF NICHT INS LEERE LAUFEN (Rafael, 12.09.: er tippte hier einen
           * ganzen Satz statt zu klicken, und der Satz waere ohne ein zufaellig
           * mitgeschicktes Bild spurlos weg gewesen — dieses Feld sendet nur zusammen
           * MIT einem Options-Klick). Dieselbe Falle war am 11.09. schon einmal im
           * Freitextfeld unten aufgetreten und wurde dort mit Enter-sendet behoben
           * (siehe FreiesWort). Hier fehlte die gleiche Behebung. Jetzt: Enter schickt
           * den Text SOFORT als eigene Nachricht an den Assistenten, unabhaengig davon,
           * ob danach noch eine Option angeklickt wird — kein getippter Satz geht mehr
           * verloren, nur weil keine Option dazu passte.
           */
          onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); absende?.sendeAlles(); } }}
          disabled={notizSendend}
          placeholder="Notiz zu deiner Wahl (geht mit, wenn du unten absendest) — oder Enter: dein Satz IST die Antwort"
          style={{ width: '100%', boxSizing: 'border-box', marginBottom: 6, padding: '5px 7px', fontSize: 11,
            borderRadius: 6, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)' }}
        />
      )}

      <div style={{ color: 'var(--tn-text-muted)', lineHeight: 1.45 }}>
        <div><b>Empfehlung:</b> {e.empfehlung}</div>
        <div><b>Dagegen spricht:</b> {e.gegenargument}</div>
        <div><b>Ohne Entscheidung:</b> {e.ohne_entscheidung}</div>
      </div>

      {/*
        DER KNOPF IST DAS ENDE DER FRAGE - dieselbe Rolle und dieselbe Beschriftung
        wie der Absenden-Knopf in `EntscheidungenListe`, damit eine Buehne nicht je
        nach Zahl der offenen Fragen anders bedient wird.
      */}
      <GuideAbsendeFehler buehneId={buehne.id} />

      {e.antwort && (
        <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)' }}>
          {/* EINE FREIE ANTWORT HAT KEIN KUERZEL — „" gewählt" waere hier eine leere
              Behauptung ueber eine Option, die es nie gab. Sein Satz steht stattdessen da. */}
          {e.antwort.art === 'freitext'
            ? <>{e.antwort.von === 'rafael' ? 'Du hast' : `${e.antwort.von} hat`} frei geantwortet: „{e.antwort.text}"</>
            : <>{e.antwort.von === 'rafael' ? 'Du hast' : `${e.antwort.von} hat`} „{e.antwort.kuerzel}" gewählt.</>}
          {e.antwort.notiz && <div>Notiz: {e.antwort.notiz}</div>}
          {/* KEIN ZUSTELLSTAND AN SEINER ANTWORT (Rafael, 18.10.2026): „Nicht zugestellt“ samt
              „nochmal zustellen“ und „Zustellung bestätigt“ sind Hofsache; der Server wiederholt
              und meldet ein Scheitern in den Sitzungssaal. Was der Berater gerade tut, bleibt. */}
          {arbeitetZeile && <div style={{ color: 'var(--tn-accent,var(--tn-link))', marginTop: 2 }}>{arbeitetZeile}</div>}
          {e.verarbeitet && <div>Verarbeitet: {e.verarbeitet.betroffene.length
            ? `${e.verarbeitet.betroffene.length} Berater arbeiten nach.`
            : 'niemand war betroffen (geprüft).'}</div>}
        </div>
      )}
    </div>
  );
}

/* ══════════════════ MEHRERE OFFENE FRAGEN: ERST SAMMELN, DANN ABSENDEN ══════════════════
 *
 * Rafael, 12.09. (Praezisierung zu "Abschicken-Knopf ganz unten"): "ja aber die buehne
 * geht nicht automatisch - ich klicke alles an und dann klicke ich auf absenden - ausser
 * es ist nur eine frage - dann wird direkt abgeschickt." Bei GENAU EINER offenen Frage
 * bleibt EntscheidungsBlock unveraendert (Klick sendet sofort). Erst ab ZWEI offenen
 * Fragen kommt diese Ansicht zum Einsatz: ein Klick auf eine Option waehlt sie nur LOKAL
 * (kein POST), bleibt umstellbar, und erst der Absenden-Knopf ganz unten schickt alle
 * gestageten Antworten ab — seit 12.09. in EINEM Aufruf an `/entscheidungen/antworten`
 * statt in N einzelnen (Rafael, 15:27Z, siehe `absenden()` unten). Das Backend
 * (guide-buehnen-routen.ts, seit Commit 317ebe35a) schickt die Buehne bei der ERSTEN
 * ankommenden Antwort schon von selbst in den Sitzungssaal — diese Komponente muss dafuer
 * nichts zusaetzlich tun (GUIDE.md Abschnitt 34).
 *
 * KEIN AUTO-ABSCHLUSS: dieser Knopf ruft NIE `/buehnen/:id/abschluss` auf, auch nicht,
 * wenn dabei alle Fragen beantwortet wurden. Rafaels eigenes Beispiel beschrieb nur den
 * Raum-Wechsel ("3 von 7 beantwortet, Absenden gedrueckt -> Sitzungssaal"), keine
 * Vollschliessung — und `art:'erledigt'` darf serverseitig ohnehin nur er selbst
 * (`wirklichRafael()`), ein Klick von hier wuerde also nichts schliessen, obwohl der
 * Knopf es verspraeche. Die tatsaechliche Schliessung bleibt Sache des Beraters, der die
 * Buehne im Sitzungssaal uebernimmt.
 */
function EntscheidungenListe({ buehne, offene, onFehler, onNachladen }: {
  buehne: BuehneAnsicht;
  offene: BuehnenEntscheidung[];
  onFehler: (t: string | null) => void;
  onNachladen: () => void;
}) {
  const { enqueue } = useGuideSend();
  /*
   * AUSWAHL UND NOTIZEN LIEGEN IM ENTWURF DIESER BUEHNE (siehe `useEntwurf` oben),
   * nicht im Fenster. Rafael, 13.09.: alles zu vier Fragen war getippt, der Aufruf
   * lief in den Zeitausstieg, das Panel baute neu auf — und die Flaeche war leer.
   * Beide Felder verschwinden erst, wenn der Server die Frage nicht mehr als offen
   * fuehrt, also wenn die Antwort wirklich angekommen ist.
   */
  const offeneIds = offene.map(e => e.id);
  const [gewaehlt, setGewaehlt, vergissWahl] = useEntwurf(`${ENTWURF_PRAEFIX}:${buehne.id}:wahl`, offeneIds);
  const [sendet, setSendet] = useState(false);
  /**
   * DAS FELD, DAS HIER GEFEHLT HAT (Rafael, 12.09.: "es muss immer moeglich
   * sein auch bei jeder einzelnen frage eine anmerkung zu machen"). Gemessen an
   * einer Buehne mit zwei offenen Fragen: `EntscheidungsBlock` (unten in dieser
   * Datei) hat ein Notizfeld pro Frage laengst seit `1738214d5`, aber diese
   * Komponente rendert nur ab ZWEI offenen Fragen (siehe der Verzweigungs-
   * Kommentar am Aufrufer weiter unten) — und hatte gar kein eigenes Notizfeld,
   * nur den EINEN generischen Freitext-Knopf unter der ganzen Buehne
   * (`FreiesWort`). Rafaels Wortlaut verlangt die Anmerkung "bei jeder
   * einzelnen Frage", nicht nur einmal pro Buehne. Derselbe `data-ai-id`-Name
   * wie in `EntscheidungsBlock` (`guide-entscheidung-notiz-${e.id}`), damit ein
   * Zaehl-Check auf beide Ansichten gleich greift, unabhaengig davon, ob gerade
   * eine oder mehrere Fragen offen sind.
   */
  const [notizen, setNotizen, vergissNotiz] = useEntwurf(`${ENTWURF_PRAEFIX}:${buehne.id}:notiz`, offeneIds);
  const [notizSendend, setNotizSendend] = useState<string | null>(null);

  /**
   * EINE FREIE ANTWORT ZAEHLT MIT — der Fehler, den Rafael am 13.09.2026 gemeldet hat:
   * *"wenn ich bei drei entscheidungen a und b beantworte und bei c eine freie antwort
   * gebe sagt er 2 von 3 — das muss natuerlich 3 von 3 sein."* Vorher zaehlte diese
   * Zeile nur die angeklickten Kuerzel, und der geschriebene Satz ging als separate
   * Nachricht daneben raus. Jetzt ist beantwortet, was eine Option ODER einen Satz
   * traegt — genau das, was auch ins Buendel geht.
   */
  const beantwortet = (e: BuehnenEntscheidung) => Boolean(gewaehlt[e.id] || notizen[e.id]?.trim());
  const anzahlGewaehlt = offene.filter(beantwortet).length;

  /**
   * EIN KLICK, EINE ZUSTELLUNG (Rafael, 12.09. 15:27Z: "die entscheidungen sollen
   * immer erst am schluss mit einer einzigen bestaetigung zum assistenten gehen -
   * nicht einzeln - sonst wird der assistent unnoetig oft geprompted").
   *
   * ALLE ANTWORTEN EINES KLICKS GEHEN IN EINEM AUFRUF an `/entscheidungen/antworten`;
   * der Server stellt daraus eine einzige Nachricht zu.
   *
   * SEIT 13.09. GEHOERT AUCH DER FREIE SATZ DAZU. Vorher liefen hier zwei Wege
   * nebeneinander: eine Frage MIT Kuerzel ging ins Buendel, eine Notiz OHNE Kuerzel
   * ging als eigene `/nachricht` raus — sie kam an, beantwortete aber nichts, zaehlte
   * nirgends mit und liess die Frage offen ("2 von 3"). Rafaels Satz dazu: *"eine freie
   * antwort ... das muss natuerlich 3 von 3 sein."* Jetzt ist beides dieselbe Sache:
   *   Option (ggf. + Notiz als Zusatz) -> { eid, kuerzel, notiz? }
   *   nur ein Satz                     -> { eid, freitext }
   *
   * DAS BUENDEL IST ATOMAR. Nimmt der Server eine Antwort nicht an (zurueckgezogen,
   * inzwischen beantwortet, Stufe zu hoch), ist KEINE gespeichert — die Fehlermeldung
   * sagt das ausdruecklich, damit niemand glaubt, ein Teil sei durch. Der Entwurf
   * bleibt dann vollstaendig stehen; er wird nicht hier weggeworfen, sondern faellt
   * weg, sobald der Server die Frage nicht mehr als offen fuehrt (siehe `useEntwurf`).
   * Genau das war der Verlust, den Rafael am 12.09. gemeldet hat.
   */
  /* EIN Absenden-Knopf je Buehne (Phase 2): die Liste meldet nur, was gewaehlt/notiert ist und wie es gebuendelt eingereiht wird; abgeschickt wird mit dem einen Knopf unten. */
  const absende = useAbsenden();
  const gebuendelt = () => {
    const g: { eid: string; kuerzel?: string; freitext?: string; notiz?: string }[] = [];
    for (const e of offene) {
      const kuerzel = gewaehlt[e.id];
      const notiz = notizen[e.id]?.trim();
      if (kuerzel) g.push({ eid: e.id, kuerzel, ...(notiz ? { notiz } : {}) });
      else if (notiz) g.push({ eid: e.id, freitext: notiz });
    }
    return g;
  };
  useAbsendeAnbieter('antworten-liste', {
    art: 'antwort', anzahl: anzahlGewaehlt,
    beschreibung: offene.filter(beantwortet).map(e => `„${e.titel}“ → ${gewaehlt[e.id] ? `${gewaehlt[e.id]})` : 'dein Satz'}`).join(' · '),
    einreihen: () => { const g = gebuendelt(); if (!g.length) return; enqueue(buehne, g); onFehler(null); },
  });

  return (
    <div data-ai-id="guide-buehne-entscheidungen-liste">
      {offene.map(e => (
        <div key={e.id} data-ai-id={`guide-buehne-entscheidung-staged-${e.id}`} style={{ ...box, marginBottom: 8, fontSize: 11.5 }}>
          <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Stufe {e.stufe} ({STUFE_WORT[e.stufe] ?? 'unbekannt'}) · {e.zustand}
          </div>
          <div style={{ fontWeight: 600, margin: '4px 0 2px', lineHeight: 1.35 }}>{e.titel}</div>
          <div style={{ marginBottom: 8, lineHeight: 1.45 }}>{e.entscheidung}</div>
          {e.optionen.map(o => {
            const ausgewaehlt = gewaehlt[e.id] === o.kuerzel;
            return (
              <button
                key={o.kuerzel}
                type="button"
                data-ai-id={`guide-entscheidung-${e.id}-${o.kuerzel}-staged`}
                disabled={sendet}
                /* NOCHMAL KLICKEN = ABWAEHLEN. Kein zusaetzliches Bedienelement (ein
                   Knopf im Knopf ist ungueltiges HTML), sondern derselbe Klick zurueck.
                   Gebraucht wird er, seit ein freier Satz allein als Antwort zaehlt:
                   ohne Abwahl bliebe ein versehentlich geklicktes Kuerzel die Antwort,
                   und der danach geschriebene Satz waere nur noch Notiz dazu. */
                onClick={() => setGewaehlt(g => {
                  if (g[e.id] === o.kuerzel) { const n = { ...g }; delete n[e.id]; return n; }
                  return { ...g, [e.id]: o.kuerzel };
                })}
                title={o.folge}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: 5, padding: '7px 9px',
                  borderRadius: 7, fontSize: 11.5, lineHeight: 1.4,
                  cursor: sendet ? 'wait' : 'pointer',
                  /* BLAU STATT AKZENT: eine nur LOKAL gewaehlte, noch nicht gesendete
                     Option muss sich von einer bereits BEIM SERVER angekommenen
                     Antwort (Akzentfarbe in EntscheidungsBlock) unterscheiden lassen —
                     sonst sieht eine ungesendete Auswahl wie eine erledigte aus. */
                  background: ausgewaehlt ? 'var(--tn-blue)33' : 'var(--tn-accent,var(--tn-link))11',
                  border: `1px solid ${ausgewaehlt ? 'var(--tn-blue)' : 'var(--tn-accent,var(--tn-link))'}`,
                  color: 'var(--tn-text)',
                  opacity: sendet ? 0.5 : 1,
                }}
              >
                <b>{o.kuerzel})</b> {o.text}{ausgewaehlt ? ' — gewählt, noch nicht gesendet (nochmal klicken: abwählen)' : ''}
                <div style={{ color: 'var(--tn-text-muted)', marginTop: 3 }}>→ {o.folge}</div>
              </button>
            );
          })}
          <input
            data-ai-id={`guide-entscheidung-notiz-${e.id}`}
            value={notizen[e.id] ?? ''}
            onChange={ev => setNotizen(n => {
              const naechst = { ...n };
              if (ev.target.value) naechst[e.id] = ev.target.value; else delete naechst[e.id];
              return naechst;
            })}
            onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); absende?.sendeAlles(); } }}
            disabled={notizSendend === e.id}
            placeholder={gewaehlt[e.id]
              ? 'Notiz zu deiner Wahl (geht mit, wenn du unten auf Absenden klickst)'
              : 'Antworte in Sätzen, wenn keine Option passt — zählt als Antwort. Enter: sofort, sonst mit Absenden'}
            style={{ width: '100%', boxSizing: 'border-box', marginBottom: 6, padding: '5px 7px', fontSize: 11,
              borderRadius: 6, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)' }}
          />
          <div style={{ color: 'var(--tn-text-muted)', lineHeight: 1.45 }}>
            <div><b>Empfehlung:</b> {e.empfehlung}</div>
            <div><b>Dagegen spricht:</b> {e.gegenargument}</div>
          </div>
        </div>
      ))}
      <GuideAbsendeFehler buehneId={buehne.id} />
    </div>
  );
}

/**
 * VORSCHLAEGE — das leichte Gegenstueck zu `EntscheidungenListe` (Rafael,
 * 17.09.2026 ~12:4xZ: „Er hat wiederum links nichts vorgeschlagen"; Kanzler-
 * Freigabe 12:57Z + Bedienungs-Korrektur 13:0xZ, drei Bedingungen: ueberspringbar,
 * auf einen Blick unterscheidbar, rueckwaertsvertraeglich).
 *
 * DIESELBE MECHANIK WIE BEI MEHREREN ENTSCHEIDUNGEN — AUSWAEHLEN, DANN ABSENDEN,
 * NIE SOFORT — und aus genau demselben Grund, den Rafael am 17.09. ~13:0xZ selbst
 * gemeldet hat: „ich wollte die fragen anklicken aber sobald ich die erste
 * geklickt hab ist er schon weggesprungen". Das war keine neue Fehlerursache,
 * sondern das gewollte Verhalten der ECHTEN Entscheidung (sofort in den
 * Sitzungssaal, siehe `EntscheidungenListe.absenden` weiter oben) an der falschen
 * Stelle benutzt — fuenf echte Entscheidungen statt fuenf leichter Vorschlaege.
 * Ein Vorschlag geht deshalb NIE einzeln raus: er wird hier nur LOKAL ausgewaehlt
 * (`gewaehlt`, wie bei `EntscheidungenListe`), nochmal klicken waehlt ab, und erst
 * der Absenden-Knopf schickt alle gesetzten Antworten in EINEM Aufruf — der die
 * Buehne, anders als eine Entscheidung, nicht bewegt (siehe die Route
 * `/vorschlaege/antworten`, server/routes/guide-buehnen-routen.ts).
 *
 * VISUELL LEICHT, NICHT NUR FUNKTIONAL: kein Stufen-Kopf, keine Empfehlung, kein
 * Gegenargument, kleinere Schrift, eigene Randfarbe — auf einen Blick erkennbar,
 * dass hier Orientierung steht, keine Pflicht.
 */
function VorschlaegeListe({ buehne, offene, onFehler }: {
  buehne: BuehneAnsicht;
  offene: Vorschlag[];
  onFehler: (t: string | null) => void;
}) {
  const { enqueue } = useGuideSend();
  const offeneIds = offene.map(v => v.id);
  const [gewaehlt, setGewaehlt] = useEntwurf(`${ENTWURF_PRAEFIX}:${buehne.id}:vorschlag`, offeneIds);
  const anzahlGewaehlt = offene.filter(v => gewaehlt[v.id]).length;
  /* EIN Absenden-Knopf je Buehne: die Vorschlags-Antworten sind ein eigener Auftrag der durablen Sendewarteschlange (eigene Kennung, Route `/vorschlaege/antworten`); den Entwurf leert erst die Quittung. */
  useAbsendeAnbieter('vorschlaege', {
    art: 'vorschlag', anzahl: anzahlGewaehlt,
    beschreibung: offene.filter(v => gewaehlt[v.id]).map(v => `„${v.text.length > 50 ? `${v.text.slice(0, 49)}…` : v.text}“ → ${gewaehlt[v.id]})`).join(' · '),
    einreihen: () => {
      const antworten = offene.filter(v => gewaehlt[v.id]).map(v => ({ vid: v.id, kuerzel: gewaehlt[v.id] }));
      if (!antworten.length) return;
      enqueue(buehne, undefined, undefined, false, undefined, antworten);
      onFehler(null);
    },
  });

  return (
    <div data-ai-id="guide-buehne-vorschlaege-liste" style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 9.5, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
        Vorschläge und Auswahlmöglichkeiten
      </div>
      {offene.map(v => (
        <div key={v.id} data-ai-id={`guide-buehne-vorschlag-${v.id}`}
          style={{ ...box, marginBottom: 6, padding: '7px 9px', fontSize: 11, borderStyle: 'dotted' }}>
          <div style={{ marginBottom: 6, lineHeight: 1.4 }}>{v.text}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {v.optionen.map(o => {
              const ausgewaehlt = gewaehlt[v.id] === o.kuerzel;
              return (
                <button
                  key={o.kuerzel}
                  type="button"
                  data-ai-id={`guide-vorschlag-${v.id}-${o.kuerzel}`}
                  onClick={() => setGewaehlt(g => {
                    if (g[v.id] === o.kuerzel) { const n = { ...g }; delete n[v.id]; return n; }
                    return { ...g, [v.id]: o.kuerzel };
                  })}
                  style={{
                    padding: '4px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                    background: ausgewaehlt ? 'var(--tn-blue)33' : 'transparent',
                    border: `1px solid ${ausgewaehlt ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
                    color: 'var(--tn-text)',
                  }}
                >{o.kuerzel}) {o.text}</button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * DAS FREIE WORT — jede Buehne ist ansprechbar, auch ohne Frage.
 *
 * Rafael, 11.09.: "Ich muss auf jeder Buehne eine Antwort geben koennen. Auch wenn
 * keine Frage da steht, muss ich zumindest eine Nachricht verschicken koennen." Es
 * darf keinen Zustand geben, in dem er vor einem Berater steht und stumm ist.
 *
 * DERSELBE WEG WIE EINE ANTWORT: an den Berater dieser Buehne, mit demselben
 * Zustellvermerk und demselben 502; der Assistent bekommt es gesammelt dazu
 * (Rafael, 13.09.2026). Zwei Wege waeren der Anfang des naechsten Verlusts.
 */
/*
 * ZWEI RUECKRUFE, NICHT EINER (Rafael, 14.10.2026 08:14Z: „ich sehe nie bühnen- der assistent
 * sagt mir es kommt eine"). Bis dahin hiess beides `onNachladen`, und der Aufrufer reichte
 * `fragenAnDich > 0 ? sofortWeiter : buehneNachladen` herein. Die 500-ms-Abfrage unten (eigenes
 * Wort noch ohne Zustellung) rief damit „Weiter nach Eingabe" auf — gemessen 08:13:14Z: der
 * Assistent oeffnete den Energy-Wizard, im selben Takt advance_after_input, Flaeche leer. Jede
 * Bühne mit offener Frage und unzugestelltem eigenem Wort wurde so beim Oeffnen sofort
 * weggeschoben. Jetzt: `onGesendet` nur nach dem eigenen Absenden, `onNachladen` liest nur.
 */
function FreiesWort({ buehne, onFehler, onGesendet, onNachladen }: {
  buehne: BuehneAnsicht; onFehler: (t: string | null) => void; onGesendet: () => void; onNachladen: () => void;
}) {
  const { enqueue } = useGuideSend();
  const [entwurf, setEntwurf] = useEntwurf(`${ENTWURF_PRAEFIX}:${buehne.id}:freies-wort`, ['text']);
  const text = entwurf.text ?? '';
  const setText = (value: React.SetStateAction<string>) => setEntwurf((previous): Record<string, string> => {
    const next = typeof value === 'function' ? value(previous.text ?? '') : value;
    return next ? { text: next } : {};
  });
  const [laeuft, setLaeuft] = useState(false);
  const arbeitetZeile = useBeraterArbeitetZeile(buehne);

  /* EIN Absenden-Knopf je Buehne (Phase 2): das freie Wort meldet nur seinen Text und wie er eingereiht wird; Enter und der eine Knopf unten senden ALLES zusammen. */
  const absende = useAbsenden();
  useAbsendeAnbieter('nachricht:freies-wort', {
    art: 'nachricht', anzahl: text.trim() ? 1 : 0,
    beschreibung: text.trim() ? `Nachricht: „${text.trim().length > 70 ? `${text.trim().slice(0, 69)}…` : text.trim()}“` : undefined,
    einreihen: () => { enqueue(buehne, undefined, text.trim()); onFehler(null); },
  });

  /*
   * NUR RAFAELS EIGENE NACHRICHTEN ZEIGEN FEHLERZEILEN AN DIESER STELLE (Kanzler,
   * 17.09.2026 12:45Z, Rafaels Wort: "Da stehen links immer noch Fehler, Fehler,
   * Fehler, Zustimmung nachholen"). Gemessen: 276 der 282 unzugestellten Nachrichten
   * systemweit sind Berater-Standmeldungen an den (bewusst abgeschalteten)
   * Assistenten — `n.von` ist dann eine Sitzungs-ID, nicht `'rafael'`. Sie laufen
   * durch denselben `buehne.nachrichten`-Strom wie sein eigenes Wort, tragen aber an
   * SEINER Fläche eine Fehlerzeile fuer einen Vorgang, bei dem er weder Absender noch
   * Empfaenger ist — und den er mit "Zustellung nachholen" auch nicht reparieren
   * kann (der Auftrag geht an den Assistenten-Schalter, nicht an den Berater; siehe
   * `guide-durable-jobs.ts::ZUSTELLFEHLER`/`nachholeBlockierte`). Gefiltert auf
   * `n.von === 'rafael'`, weil genau das der Marker ist, den auch die Zustellung
   * selbst verwendet (`stelleNachrichtZu`, guide-buehnen-routen.ts).
   */
  /* Seit 25.09.2026 auch das Wort der Eigentuemerin (`partner:<eigentuemer>`, so zeichnet
   * das Partner-Tor ihre Nachricht) — sie steht vor demselben Fenster und muss ebenso sehen,
   * ob ihr Wort beim Berater angekommen ist (Befund Vizekanzler Partner). */
  const eigentuemerFeld = (buehne as { eigentuemer?: unknown }).eigentuemer;
  const eigentuemerVon = typeof eigentuemerFeld === 'string' ? `partner:${eigentuemerFeld}` : null;
  /* Und das Wort der Menschen, mit denen die Buehne geteilt ist (Rafael 30.10.2026) — unter ihrem Namen. */
  const geteiltFeld = (buehne as { geteilt_mit?: unknown }).geteilt_mit;
  const geteiltVon = new Set(Array.isArray(geteiltFeld)
    ? (geteiltFeld as Array<{ nutzer?: unknown; entzogen_am?: unknown }>).filter(f => typeof f?.nutzer === 'string' && !f.entzogen_am).map(f => `partner:${f.nutzer as string}`)
    : []);
  const eigeneNachrichten = buehne.nachrichten.filter(n => n.von === 'rafael' || (eigentuemerVon !== null && n.von === eigentuemerVon) || geteiltVon.has(n.von));
  const wartetAufZustellung = eigeneNachrichten.some(n => !n.zustellung);
  useEffect(() => {
    if (!wartetAufZustellung) return;
    /* 500ms statt 2000ms (Kanzler-Freigabe 17.09.2026 ~17:3xZ): das ist die Stelle, an
     * der RAFAEL wartet, bis er seine eigene Zustellbestaetigung sieht, nicht die
     * Maschine — ein paar Abfragen mehr sind billig, die Wartezeit dagegen zaehlt. */
    const timer = setInterval(onNachladen, 500);
    return () => clearInterval(timer);
  }, [wartetAufZustellung, onNachladen]);
  return (
    <div data-ai-id="guide-buehne-freies-wort" style={{ ...box, fontSize: 11.5 }}>
      {/*
        DIESES FELD GEHT AN DEN BERATER — Rafael, 13.09.2026, 16:58:27.497Z.
        
        Er stand vor genau dieser Beschriftung: „wieso steht da das hier ? ich dachte die
        nachricht geht direkt an den berater- der berater msus die standard empfaenger
        sein - der assistent bekommt das nur bei seinen runden nochmal mit", eine Minute
        spaeter „die nachrichten muessen an den berater gehen !!!!".
        
        HIER STAND DAS GEGENTEIL, und es war einmal richtig: solange „der Berater dieser
        Buehne" nicht eindeutig war, musste der Assistent den Empfaenger bestimmen. Rafael
        hat den Grund selbst aufgeloest: „das ist erst seit ich die neue zuordnung hab das
        ein berater = buehne - der assistent bekommts nachher nochmal". Seit der 1:1-Regel
        ist der Empfaenger eindeutig, und der alte Satz ist das Fossil einer geloesten
        Schwierigkeit — deshalb ersetzt, nicht uebersteuert.
        
        WAS DER SERVER TUT: die Nachricht geht sofort an den einen Berater der Buehne UND
        in die Sammlung des Assistenten (guide.ts `stelleDemBeraterZu`). Beides, damit
        nichts verlorengeht, wenn die Direktzustellung ausfaellt.
      */}
      <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>
        Nachricht an den Berater dieser Bühne
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--tn-text-muted)', marginBottom: 5, lineHeight: 1.4 }}>
        Geht sofort an den Berater dieser Bühne. Der Assistent sieht es in seiner
        nächsten Runde ebenfalls. Sein Chat steht <b>rechts</b>.
      </div>
      {/*
        DER BILD-KNOPF, DEN RAFAEL HIER VERMISST HAT (13.09.2026: *"der bild button
        waere hier im guide bei den antworten auch sinnvoll, nicht nur bei den
        chats"*). Dasselbe Bauteil wie im Chat und wie beim Karten-Freitext — nicht
        nachgebaut, sondern dieselbe `BildEinfuegeZone`: sie legt das Bild ab und
        schreibt den Verweis ins Feld daneben, den die empfangende Sitzung ohnehin
        oeffnen kann.
      */}
      <BildEinfuegeZone
        hoehe={38}
        onFehler={onFehler}
        onText={verweis => setText(t => (t ? `${t}\n` : '') + verweis)}
      />
      <textarea
        data-ai-id="guide-buehne-nachricht-feld"
        value={text}
        onChange={ev => setText(ev.target.value)}
        /* Enter sendet, Shift+Enter macht eine Zeile — wie im Chat daneben und wie im
         * Freitextfeld darunter. Vorher brauchte es Cmd/Ctrl+Enter; Rafael am 11.09.:
         * „ich wuerde gerne per Enter antworten koennen - nicht mit der komischen
         * Kombination". Zwei Eingabefelder im selben Fenster mit verschiedenen Tasten
         * sind ohnehin eine Falle. */
        onKeyDown={ev => { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); absende?.sendeAlles(); } }}
        rows={3}
        /*
         * DER PLATZHALTER MUSS SAGEN, WAS DAS FELD TUT — und was es NICHT tut.
         *
         * Bis zum 11.09. stand hier „Passt keine der Optionen? Schreib es hier." Rafael
         * hat es viermal an diesem Tag als ANTWORT benutzt, in Saetzen, wie ein Mensch
         * antwortet — und die Frage blieb jedes Mal offen stehen, die Buehne blieb vor
         * ihm, nichts rueckte nach. Sein Satz: *„ich hatte schon geantwortet, aber es ist
         * nie vom Vorraum verschwunden - es ist auch nichts nachgerueckt."*
         *
         * ICH HATTE DARAUF ZUERST GESCHRIEBEN „beantwortet die Frage NICHT". Rafael hat
         * das sofort kassiert: *„das passt ja, dass ich eine Nachricht gegeben hab — das
         * sollte der Assistent aufnehmen und daraus Auftraege an die Berater machen,
         * oder?"* Er hat recht: in Saetzen zu antworten ist der normale Weg, und die
         * Maschine hat sich danach zu richten, nicht er. Das Feld sagt deshalb, was
         * DANACH passiert, statt ihm einen Fehler zu unterstellen.
         */
        placeholder="Schreib in Sätzen. Der Berater verarbeitet deine Antwort und führt den Auftrag weiter."
        style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 11.5, lineHeight: 1.45,
          borderRadius: 6, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)',
          resize: 'vertical', fontFamily: 'inherit' }}
      />
      <AbsendenKnopf titel={buehne.titel} />
      <GuideAbsendeFehler buehneId={buehne.id} />

      {eigeneNachrichten.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 7, borderTop: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)', lineHeight: 1.45 }}>
          {/* NUR SEIN WORT, KEIN ZUSTELLSTAND (Rafael, 18.10.2026). Hier stand je Nachricht
              „Gespeichert · Zustellung noch nicht bestätigt“, „⚠ Nicht zugestellt“ und ein Knopf
              „Zustellung nachholen“. Die Zustellung wiederholt der Server selbst und meldet ein
              endgültiges Scheitern in den Sitzungssaal — nicht an Rafael. */}
          {eigeneNachrichten.slice(-3).map(n => (
            <div key={n.id} data-ai-id={`guide-buehne-nachricht-${n.id}`}>
              {n.text.length > 120 ? `${n.text.slice(0, 120)}…` : n.text}
            </div>
          ))}
          {arbeitetZeile && <div style={{ color: 'var(--tn-accent,var(--tn-link))', marginTop: 2 }}>{arbeitetZeile}</div>}
        </div>
      )}
    </div>
  );
}


/**
 * DAS NOTIZENFELD UND DER NEUSTART — Rafaels zwei Bitten vom 12.09.2026, 14:12Z.
 *
 * Sein Wort: *"Ich brauche noch ein Notizenfeld im Guide, wo ich allgemeine Notizen
 * eingeben kann. Dann moechte ich nicht unterschiedliche Stuecke ... nutzen, um mir
 * aufzuschreiben ... Und ich muss natuerlich auch meine Sitzung neu starten koennen
 * und so. Das sollte dann, glaube ich, an den Assistenten auch direkt gehen."*
 *
 * WARUM UNTEN, KLEIN UND ZUGEKLAPPT. Dieselbe Begruendung wie beim Rueckweg-Streifen
 * darunter, und dieselbe, mit der er zweimal etwas aus dem Kopf der Spalte hat werfen
 * lassen (09.09.: *"da stehen tausend sachen statt dass das wesentliche angezeigt
 * wird"*). Ein dauerhaft offenes Textfeld waere eine dritte Sache ueber dem, was er
 * lesen will. Zugeklappt kostet es eine Zeile und ist mit einem Klick da.
 *
 * WARUM AUSSERHALB DER VERZWEIGUNG oben (offene Buehne ODER Hof): ein Einfall kommt
 * nicht danach, ob gerade ein Berater vor ihm steht. Genau das meint *"nicht
 * unterschiedliche Stuecke nutzen"* — EIN Ort, immer derselbe, egal wo er ist.
 *
 * WARUM DIE SPALTE KEINE EMPFAENGER-KENNUNG MITSCHICKT: sie loest der Server im
 * Augenblick des Sendens auf (`POST /guide/notiz`). Die Adresse des Assistenten
 * rotiert mit jeder Abloese; eine beim letzten Laden gelesene Kennung waere
 * irgendwann eine geschlossene Sitzung — und das Senden saehe trotzdem gelungen aus.
 * Deshalb kommt der Empfaenger erst in der ANTWORT zurueck und wird erst dann
 * genannt: was dort steht, hat ihn wirklich bekommen.
 */
type NotizArt = null | 'notiz' | 'neustart' | 'berater';

/*
 * SEIT DER ARBEITSLISTE (03.10.2026) HAT DIESER BLOCK KEINE EIGENE KNOPFLEISTE MEHR.
 * Die drei Ausloeser stehen oben: „＋ Neue Bühne" als grosser Primaerknopf in der
 * Kopfzeile (Rafaels haeufigster Handgriff — als Woertchen in der Fussleiste wirkte er
 * versteckt), „Notiz" und „Sitzung neu starten" im „⋯"-Menue. Die Formulare stehen
 * direkt unter der Kopfzeile und gelten weiter unabhaengig davon, ob eine Buehne offen ist.
 */
function NotizUndNeustart({ offen, setOffen, onBuehneAngelegt }: {
  offen: NotizArt; setOffen: (a: NotizArt) => void; onBuehneAngelegt: () => void;
}) {
  /* ZWEI FELDER, ZWEI STATES (Rafael 12.09. 15:17Z: "die Notiz soll erst bei
   * 'Sitzung neu starten' liegen und dies gehoert nicht zur Buehne. So eine Notiz
   * ist was Allgemeines"). Bis hierher teilten sich Notizfeld und Neustart-Zusatz
   * EINEN `text`-State — wer tippte und dann umschaltete, fand seinen Text im
   * jeweils ANDEREN Kasten wieder, und ein Klick auf "Ja, Abloese anstossen"
   * verschickte ihn als Abloese-Zusatz statt als eigenstaendige Notiz. Sein Bild
   * (1789225940438_pic8.png) zeigt genau das. */
  const [textNotiz, setTextNotiz] = useState('');
  const [textNeustart, setTextNeustart] = useState('');
  /* Die drei Felder des neuen Beraters — eigene States aus demselben Grund wie oben:
   * wer tippt und umschaltet, findet seinen Text sonst im falschen Kasten wieder. */
  const [satz, setSatz] = useState('');
  /* Konto und Modell: leer = Server entscheidet (bisheriges Verhalten, 'auto' +
   * 'sonnet'). Erst seit 17.09.2026 nimmt die Route die Felder ueberhaupt an. */
  const [konto, setKonto] = useState('');
  const [modell, setModell] = useState('');
  /* IN WELCHEM HAUS DIE NEUE BUEHNE ENTSTEHT — dieselbe Wahl wie oben im Guide
   * („Server: Dev | Partner"). Rafael, 06.10.2026: *„wenn ich den partner anklicke, am
   * partner arbeiten und wenn ich am dev bin, am dev"*. Bis dahin schickte dieses Formular
   * die Wahl nicht mit, und jede Buehne entstand im Haus des Fensters. Nur der Betreiber
   * waehlt ein Haus; ein Partner bleibt im eigenen (sein Tor ignoriert das Feld ohnehin).
   * `fensterHaus` kommt aus der Arbeitsbereich-Abfrage des Zielhauses: liegt das Ziel in einem anderen Haus,
   * gehoert ihm auch die Kontowahl (CLAUDE.md „Kein Konto im Sendeweg"). */
  const { user: nutzer, authEnabled: anmeldung } = useAuth();
  const betreiber = !anmeldung || nutzer?.role === 'admin';
  const hausfilter = useGuideHausfilter();
  const zielHaus = betreiber ? hausfilter : null;
  const [fensterHaus, setFensterHaus] = useState<'dev' | 'partner' | null>(null);
  const fremdesHaus = !!zielHaus && !!fensterHaus && zielHaus !== fensterHaus;
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);
  /* Jedes Aufklappen beginnt ohne alte Meldung. Beim Formular „Neue Bühne" und bei jedem
   * Hauswechsel wird nur noch das Haus des Fensters erfragt (fuer die Kontowahl) — eine
   * Arbeitsbereich-Auswahl gibt es seit 16.10.2026 nicht mehr. */
  useEffect(() => {
    if (offen === null) return;
    setFehler(null); setErfolg(null);
    if (offen === 'berater' && fensterHaus === null) void ladeFensterHaus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);
  useEffect(() => {
    setFensterHaus(null);
    if (offen === 'berater') void ladeFensterHaus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zielHaus]);
  useEffect(() => { if (fremdesHaus) setKonto(''); }, [fremdesHaus]);

  const senden = async (art: 'notiz' | 'sitzung-neustart') => {
    if (laeuft) return;
    const text = art === 'notiz' ? textNotiz : textNeustart;
    if (art === 'notiz' && !text.trim()) return;
    setLaeuft(true); setFehler(null); setErfolg(null);
    try {
      const r = await fetch(`${API}/guide/notiz`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ art, ...(text.trim() ? { text: text.trim() } : {}) }),
        signal: AbortSignal.timeout(30000),
      });
      const d = await r.json().catch(() => null) as { error?: string; an?: string; an_label?: string } | null;
      if (!r.ok) { setFehler(d?.error ?? `Nicht angenommen (HTTP ${r.status}).`); return; }
      if (art === 'notiz') setTextNotiz(''); else setTextNeustart('');
      setOffen(null);
      setErfolg(art === 'notiz'
        ? `✓ Notiz ist bei ${d?.an_label ?? 'deinem Assistenten'} (${(d?.an ?? '').slice(0, 8)}).`
        : `✓ ${d?.an_label ?? 'Dein Assistent'} (${(d?.an ?? '').slice(0, 8)}) bereitet die Abloese vor — er meldet sich im neuen Chat.`);
    } catch (err) {
      setFehler(`Ging nicht raus: ${err instanceof Error ? err.message : String(err)}`);
    } finally { setLaeuft(false); }
  };

  /*
   * DER PROMINENTE KNOPF — Rafael, 13.09.2026 09:4xZ: *„wenn ich das mache soll der nachher
   * mitsamt buehne auftauchen"*.
   *
   * EIN FELD, EIN KNOPF (Rafael, 16.10.2026, mit Bild dieses Formulars): *„diese ganzen
   * Arbeitsbereich-Auswahl und auch 'dem Assistenten mitgeben' ... ich will das eher ohne
   * Arbeitsbereich machen — es sind halt einfach refs und er waehlt dann beim Start den
   * richtigen Bereich aus — dann ist das nicht so verwirrend fuer neue User."* Arbeitsbereich
   * und Thema schickt das Formular nicht mehr; der Server startet den Berater im benannten
   * Startbereich und der Berater traegt den passenden selbst ein (`buehne-ohne-wahl.ts`).
   * Der Notiz-Weg an den Assistenten bleibt ueber „📝 Notiz" erreichbar.
   */
  /*
   * NUR NOCH DAS HAUS DES FENSTERS: liegt das Zielhaus woanders, gehoert ihm die Kontowahl
   * (Server weist ein fremdes Konto mit 400 ab). Scheitert die Abfrage, bleibt `fensterHaus`
   * leer — die Kontowahl bleibt dann offen und der Server meldet einen Hausfehler laut.
   */
  const ladeFensterHaus = async () => {
    if (!zielHaus) return;
    try {
      // trust-boundary:ok — derselbe Abruf wie vorher in ladeBereiche; gelesen wird nur `fenster`, eng geprueft (dev|partner, sonst null)
      const r = await fetch(`${API}/guide/buehnen/arbeitsbereiche?haus=${zielHaus}`, { signal: AbortSignal.timeout(15000) }); // trust-boundary:ok
      const d: unknown = await r.json().catch(() => null);
      if (!r.ok) throw new Error((d as { error?: string } | null)?.error ?? `HTTP ${r.status}`);
      const f = (d as { fenster?: unknown } | null)?.fenster;
      setFensterHaus(f === 'dev' || f === 'partner' ? f : null);
    } catch (err) { // silent-ok: nur die Kontowahl haengt daran, der Server prueft das Haus selbst und meldet laut
      console.warn(`[guide] Haus des Fensters nicht abrufbar: ${err instanceof Error ? err.message : String(err)}`);
      setFensterHaus(null);
    }
  };

  const einsetzen = async () => {
    if (laeuft || !satz.trim()) return;
    setLaeuft(true); setFehler(null); setErfolg(null);
    try {
      const r = await fetch(`${API}/guide/buehnen/berater-einsetzen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          von: 'rafael', auftrag: satz.trim(),
          ...(zielHaus ? { haus: zielHaus } : {}),
          ...(konto && !fremdesHaus ? { accountId: konto } : {}), ...(modell ? { model: modell } : {}),
        }),
        signal: AbortSignal.timeout(150000),
      });
      const d = await r.json().catch(() => null) as { error?: string; sitzung?: string; buehne?: { id?: string; titel?: string } } | null;
      if (!r.ok) { setFehler(d?.error ?? `Nicht angenommen (HTTP ${r.status}).`); return; }
      onBuehneAngelegt();
      setSatz(''); setOffen(null);
      setErfolg(`✓ Berater ${(d?.sitzung ?? '').slice(0, 8)} arbeitet an „${d?.buehne?.titel ?? d?.buehne?.id}"${zielHaus ? ` — im Haus ${zielHaus === 'dev' ? 'Dev' : 'Partner'}` : ''}.`);
    } catch (err) {
      /* Nach 150 s ohne Antwort weiss der Client nicht, ob der Server die Buehne noch
       * anlegt. Das sagt er so — und wo Rafael nachsehen kann. */
      const timeout = err instanceof Error && err.name === 'TimeoutError';
      setFehler(timeout
        ? 'Keine Antwort nach 150 s. Ob Bühne und Berater trotzdem entstanden sind, zeigt der Hof nach dem Neuladen; der Klick steht im Eingabestrom (GET /api/audit/inputs, type guide-berater-einsetzen).'
        : `Ging nicht: ${err instanceof Error ? err.message : String(err)}`);
    } finally { setLaeuft(false); }
  };

  const zeile: React.CSSProperties = {
    flexShrink: 0, borderBottom: '1px solid var(--tn-border)', padding: '6px 10px',
    fontSize: 11.5, color: 'var(--tn-text-muted)', maxHeight: '55vh', overflowY: 'auto',
    background: 'var(--tn-surface)',
  };
  const knopf: React.CSSProperties = {
    background: 'transparent', border: 0, padding: 0, fontSize: 11,
    color: 'var(--tn-blue)', cursor: 'pointer', textDecoration: 'underline',
  };

  if (offen === null && !erfolg && !fehler) return null;
  return (
    <div data-ai-id="guide-notiz-streifen" style={zeile}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {offen && (
          <span style={{ fontWeight: 600, color: 'var(--tn-text)', fontSize: 12 }}>
            {offen === 'berater' ? '＋ Neue Bühne' : offen === 'notiz' ? '📝 Notiz' : '↻ Sitzung neu starten'}
          </span>
        )}
        {erfolg && <span data-ai-id="guide-notiz-erfolg" style={{ color: 'var(--tn-green,var(--tn-green))' }}>{erfolg}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" data-ai-id="guide-notiz-schliessen" className="guide-touch-knopf"
          onClick={() => { setOffen(null); setErfolg(null); setFehler(null); }}
          aria-label="Schließen" title="Schließen — Eingetipptes bleibt erhalten"
          style={{ ...knopf, textDecoration: 'none', fontSize: 13, color: 'var(--tn-text-muted)' }}>✕</button>
      </div>

      {offen === 'notiz' && (
        <div style={{ marginTop: 5 }}>
          <textarea
            data-ai-id="guide-notiz-feld"
            autoFocus
            value={textNotiz}
            onChange={ev => setTextNotiz(ev.target.value)}
            /* Enter sendet, Shift+Enter macht eine Zeile — wie im freien Wort an der
             * Buehne und wie im Chat daneben. Rafael, 11.09.: "ich wuerde gerne per
             * Enter antworten koennen - nicht mit der komischen Kombination." */
            onKeyDown={ev => { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); void senden('notiz'); } }}
            rows={3}
            placeholder="Was dir gerade einfaellt — gehoert zu keiner Buehne. Geht an deinen Assistenten, der macht Auftraege daraus."
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 11.5, lineHeight: 1.45,
              borderRadius: 6, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)',
              resize: 'vertical', fontFamily: 'inherit' }}
          />
          <button
            type="button"
            data-ai-id="guide-notiz-senden"
            disabled={!textNotiz.trim() || laeuft}
            onClick={() => { void senden('notiz'); }}
            style={{ marginTop: 4, marginBottom: 4, fontSize: 11, padding: '4px 11px', borderRadius: 6,
              cursor: textNotiz.trim() && !laeuft ? 'pointer' : 'default',
              background: textNotiz.trim() && !laeuft ? 'var(--tn-accent,var(--tn-link))22' : 'transparent',
              color: textNotiz.trim() && !laeuft ? 'var(--tn-text)' : 'var(--tn-text-muted)',
              border: '1px solid var(--tn-border)' }}
          >{laeuft ? 'geht raus …' : 'An den Assistenten (⏎)'}</button>
        </div>
      )}

      {offen === 'neustart' && (
        <div data-ai-id="guide-sitzung-neustart-rueckfrage" style={{ marginTop: 5, lineHeight: 1.5 }}>
          <div style={{ color: 'var(--tn-text)' }}>
            Dein Assistent startet dir einen frischen Chat, gibt ihm deinen Stand mit und sagt dir
            drin Bescheid. <b>Dieser hier bleibt offen</b>, bis der neue steht — beendet wird nichts.
          </div>
          <textarea
            data-ai-id="guide-sitzung-neustart-zusatz"
            value={textNeustart}
            onChange={ev => setTextNeustart(ev.target.value)}
            rows={2}
            placeholder="Optional: was der neue Chat als Erstes wissen soll."
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '6px 8px', fontSize: 11.5,
              lineHeight: 1.45, borderRadius: 6, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)',
              color: 'var(--tn-text)', resize: 'vertical', fontFamily: 'inherit' }}
          />
          <button
            type="button"
            data-ai-id="guide-sitzung-neustart-senden"
            disabled={laeuft}
            onClick={() => { void senden('sitzung-neustart'); }}
            style={{ marginTop: 4, marginBottom: 4, fontSize: 11, padding: '4px 11px', borderRadius: 6,
              cursor: laeuft ? 'default' : 'pointer', background: 'var(--tn-accent,var(--tn-link))22',
              color: 'var(--tn-text)', border: '1px solid var(--tn-border)' }}
          >{laeuft ? 'geht raus …' : 'Ja, Ablöse anstoßen'}</button>
        </div>
      )}

      {offen === 'berater' && (
        <div data-ai-id="guide-berater-einsetzen-formular" style={{ marginTop: 5, lineHeight: 1.5 }}>
          <div style={{ color: 'var(--tn-text)' }}>
            Beschreibe den Auftrag — dein Berater startet mit einer eigenen Bühne und sucht sich den passenden Arbeitsbereich selbst.
          </div>
          {zielHaus && (
            <div data-ai-id="guide-berater-einsetzen-haus" style={{ marginTop: 3, color: 'var(--tn-text)' }}>
              Bühne und Berater entstehen im Haus <b>{zielHaus === 'dev' ? 'Dev' : 'Partner'}</b> — wie oben unter „Server" gewählt.
            </div>
          )}
          <textarea
            data-ai-id="guide-berater-einsetzen-satz"
            autoFocus
            value={satz}
            onChange={ev => setSatz(ev.target.value)}
            rows={2}
            placeholder="Woran soll er arbeiten? Ein Satz."
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '6px 8px', fontSize: 11.5,
              lineHeight: 1.45, borderRadius: 6, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)',
              color: 'var(--tn-text)', resize: 'vertical', fontFamily: 'inherit' }}
          />
          {/* Konto und Modell: bewusst NICHT Pflichtfelder — leer laesst den Server
              entscheiden (bisheriges Verhalten). Eingeklappt, weil ein neuer Nutzer sie
              nie braucht (Rafael, 16.10.2026: „dann ist das nicht so verwirrend"). */}
          <details data-ai-id="guide-berater-einsetzen-erweitert" style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--tn-text-muted)' }}>Konto und Modell</summary>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <select
              data-ai-id="guide-berater-einsetzen-konto"
              value={konto}
              disabled={fremdesHaus}
              title={fremdesHaus ? `Im Haus ${zielHaus === 'dev' ? 'Dev' : 'Partner'} wählt der Server das Konto selbst.` : undefined}
              onChange={ev => setKonto(ev.target.value)}
              style={{ flex: '1 1 140px', minWidth: 0, padding: '4px 6px', fontSize: 11, borderRadius: 6,
                background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)' }}
            >
              <option value="">{fremdesHaus ? `Konto: wählt Haus ${zielHaus === 'dev' ? 'Dev' : 'Partner'}` : 'Konto: automatisch'}</option>
              {ACCOUNTS.filter(a => a.id !== 'local' && a.provider !== 'codex' && a.id !== 'auto')
                .map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <select
              data-ai-id="guide-berater-einsetzen-modell"
              value={modell}
              onChange={ev => setModell(ev.target.value)}
              style={{ flex: '1 1 140px', minWidth: 0, padding: '4px 6px', fontSize: 11, borderRadius: 6,
                background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)' }}
            >
              <option value="">Modell: sonnet (Vorgabe)</option>
              <option value="opus">opus</option>
              <option value="sonnet">sonnet</option>
              <option value="haiku">haiku</option>
              <option value="fable">fable</option>
            </select>
          </div>
          </details>
          <button
            type="button"
            data-ai-id="guide-berater-einsetzen-senden"
            disabled={laeuft || !satz.trim()}
            onClick={() => { void einsetzen(); }}
            style={{ marginTop: 5, marginBottom: 4, fontSize: 11, padding: '4px 11px', borderRadius: 6,
              cursor: laeuft || !satz.trim() ? 'default' : 'pointer',
              background: !laeuft && satz.trim() ? 'var(--tn-accent,var(--tn-link))22' : 'transparent',
              color: !laeuft && satz.trim() ? 'var(--tn-text)' : 'var(--tn-text-muted)',
              border: '1px solid var(--tn-border)' }}
          >{laeuft ? 'Berater startet … (das dauert)' : 'Bühne mit Berater anlegen'}</button>
        </div>
      )}

      {fehler && (
        <div data-ai-id="guide-notiz-fehler" style={{ marginTop: 4, color: 'var(--tn-red)', lineHeight: 1.45 }}>{fehler}</div>
      )}
    </div>
  );
}

/**
 * AUF SPAETER — Rafaels eigener Knopf in den Pausenraum.
 *
 * Er, 21.09.2026, woertlich: *"Dort wäre auch ein Button für „in den Pausenraum
 * verschieben", praktisch auch im normalen Guide, damit ich wirklich auch händisch in
 * den Pausenraum verschieben kann, wenn ich Sachen auf später verschiebe."* — und es
 * war ihm das Wichtigste der Runde: *"Also ich brauche einfach nur einen Button"*.
 *
 * WARUM ER UEBER DIE SENDEWARTESCHLANGE GEHT und nicht als nackter `fetch`: bei genau
 * der Serverlast, die am selben Tag die roten "signal timed out"-Zeilen erzeugt hat,
 * waere ein direkter Aufruf spurlos weg. Ueber die Warteschlange ueberlebt er einen
 * Tab-Wechsel, einen Neustart und eine langsame Antwort — und er zeigt seinen Zustand.
 *
 * WARUM DER GRUND AUSFORMULIERT IST: `hof-lifecycle.ts` haelt fest, dass der Pausenraum
 * "nur auf ausdrueckliche Anweisung" beschickt wird — frueher zwang der Zugschutz jeden
 * Berater ohne offene Frage automatisch dorthin, und Rafael hat das am 17.09.2026
 * ausdruecklich beendet. Sein Klick IST die Anweisung; der Satz im Verlauf macht spaeter
 * nachpruefbar, dass hier kein Automatismus abgelegt hat.
 */
function BuehneAufSpaeter({ buehne, onFehler, kompakt }: {
  buehne: BuehneAnsicht; onFehler: (text: string | null) => void; kompakt?: boolean;
}) {
  const { enqueue, jobs, queue } = useGuideSend();
  const [error, setError] = useState<string | null>(null);
  const pending = jobs.find(j => j.stage.id === buehne.id && j.raum === 'pausenraum');
  const schonDa = (buehne.raum_gesetzt ?? buehne.raum) === 'pausenraum';
  /* Kurz, weil er in der Kopfzeile neben Brotkrumen und Erledigt steht (03.10.2026);
     der ganze Satz steht im Tooltip. */
  /* Kein Sendestatus am Knopf (Rafael, 18.10.2026) — ein zweiter Klick stoesst nur still erneut an. */
  const beschriftung = schonDa ? 'Liegt auf später' : 'Auf später';
  const zustandSatz = schonDa ? 'Diese Bühne liegt schon auf später (Pausenraum) — du findest sie in der Übersicht.'
    : 'Auf später legen: die Bühne kommt beim Durchblättern nicht mehr vor und wartet in der Übersicht unter „Pausenraum“, bis du sie wieder öffnest.';
  return <><button type="button" data-ai-id="guide-buehne-pausenraum"
    data-guide-pausenraum-zustand={schonDa ? 'liegt' : pending?.state ?? 'frei'}
    disabled={schonDa}
    title={zustandSatz} aria-label={zustandSatz}
    onClick={() => {
      try {
        if (schonDa) return;
        if (!pending) enqueue(buehne, undefined, undefined, false, 'pausenraum');
        else if (pending.state !== 'sending') queue?.retry(pending.id);
        onFehler(null); setError(null);
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    }}
    style={{ cursor: schonDa ? 'default' : 'pointer', opacity: schonDa ? 0.55 : 1, color: 'var(--tn-text-muted)' }}>
    {beschriftung}
  </button>
  {error && !kompakt && <div role="alert" data-ai-id="guide-buehne-pausenraum-fehler" style={{ fontSize: 11, color: 'var(--tn-red)', flexBasis: '100%' }}>{error}</div>}</>;
}

function BuehneAbschliessen({ buehne, onAbgeschlossen, onFehler }: {
  buehne: BuehneAnsicht; onAbgeschlossen: () => void; onFehler: (text: string | null) => void;
}) {
  const { enqueue, jobs, queue } = useGuideSend();
  /*
   * NIE WORTLOS — Rafael, 16.09.2026: „ich hab bei der aussenkommunikation fenster schon
   * zehn mal auf schliessen geklickt aber es geht einfach nicht weg". Der Knopf zeigte
   * fuer JEDEN nicht-blockierten Auftrag „Abschluss laeuft", auch wenn der Server nie
   * geantwortet hatte (Zustand `uncertain`, Fehler nur in der eingeklappten
   * Sendestatus-Liste), und jeder weitere Klick tat nichts. Jetzt steht der Zustand des
   * Auftrags am Knopf und darunter sein Fehlertext; ein Klick auf einen haengenden oder
   * abgewiesenen Auftrag stoesst ihn erneut an (`queue.retry`), statt zu verpuffen.
   */
  const pending = jobs.find(j => j.stage.id === buehne.id && j.close);
  useEffect(() => { if (buehne.abschluss) onAbgeschlossen(); }, [buehne.abschluss, onAbgeschlossen]);
  const [error, setError] = useState<string | null>(null);
  /* Kurz, weil er in der Kopfzeile steht (03.10.2026); der ganze Satz im Tooltip, und
     jeder Zwischenstand steht ohnehin ausgeschrieben darunter (`stand`). */
  /* Kein Sendestatus am Knopf (Rafael, 18.10.2026): kein „unbestätigt“, kein „erneut“, keine
     Zustandszeile darunter. Ein weiterer Klick stoesst einen haengenden Abschluss still erneut an. */
  const beschriftung = pending ? '✓ schließt …' : '✓ Erledigt';
  const zustandSatz = 'Als erledigt abschließen: die Bühne ist fertig und verschwindet aus der Übersicht; es geht mit der nächsten weiter.';
  return <><button type="button" data-ai-id="guide-buehne-erledigt" data-guide-abschluss-zustand={pending?.state ?? 'frei'}
    title={zustandSatz} aria-label={zustandSatz}
    onClick={() => {
      try {
        if (!pending) enqueue(buehne, undefined, undefined, true);
        else if (pending.state !== 'sending') queue?.retry(pending.id);
        onFehler(null); onAbgeschlossen();
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    }}
    style={{ color: 'var(--tn-green)' }}>
    {beschriftung}
  </button>
  {error && <div role="alert" data-ai-id="guide-buehne-abschluss-fehler" style={{ flexBasis: '100%' }}>{error}</div>}</>;
}

/**
 * DAS ZULETZT ANGENOMMENE WORT EINER BUEHNE (Technikpaket 29.10.2026, Rafael e-beratungsqualitaet-aenderungspaket-20261029 a):
 * beantwortete Frage, beantworteter Vorschlag oder Rafaels freies Wort — das juengste. Dieselbe Auswahl wie
 * `angenommenesWort` in server/lib/buehnen-kettenleiste.ts, damit links und rechts dasselbe Wort steht.
 */
function kurz(t: string, n: number): string { const x = t.replace(/\s+/g, ' ').trim(); return x.length > n ? `${x.slice(0, n - 1)}…` : x; }
function angenommenesWortDerBuehne(b: Pick<BuehneAnsicht, 'entscheidungen' | 'vorschlaege' | 'nachrichten'>): { text: string; am: string; vonRafael: boolean } | null {
  const wahl = (a: { art?: string; kuerzel?: string; text: string }) => a.art === 'freitext' ? `„${kurz(a.text, 160)}“` : a.kuerzel ? `${a.kuerzel}) ${kurz(a.text, 140)}` : kurz(a.text, 140);
  const k: { text: string; am: string; vonRafael: boolean }[] = [];
  for (const e of b.entscheidungen ?? []) if (e.antwort) k.push({ text: `${kurz(e.titel, 100)} → ${wahl(e.antwort)}`, am: e.antwort.am ?? '', vonRafael: !e.antwort.von || e.antwort.von === 'rafael' });
  for (const v of b.vorschlaege ?? []) if (v.antwort) k.push({ text: `${kurz(v.text, 100)} → ${wahl(v.antwort)}`, am: v.antwort.am ?? '', vonRafael: !v.antwort.von || v.antwort.von === 'rafael' });
  for (const n of b.nachrichten ?? []) if (n.von === 'rafael') k.push({ text: `„${kurz(n.text, 160)}“`, am: n.am, vonRafael: true });
  return k.length ? k.reduce((x, y) => (y.am > x.am ? y : x)) : null;
}
function wortZeit(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
/** Titel einer Nachfolgerin nachlesen — Beiwerk: ohne ihn steht die Kennung, der Knopf geht trotzdem. */
function useBuehnenTitel(id: string | undefined): string | undefined {
  const [titel, setTitel] = useState<{ id: string; titel: string } | null>(null);
  useEffect(() => {
    if (!id) return;
    let weg = false;
    leseBuehne(id).then(b => { if (!weg) setTitel({ id, titel: b.titel || id }); })
      .catch(e => console.warn('[guide-nachfolger-titel] nicht lesbar', id, e));
    return () => { weg = true; };
  }, [id]);
  return id ? (titel?.id === id ? titel.titel : id) : undefined;
}

/**
 * „DEIN WORT IST ANGEKOMMEN. WEITER AUF …" (B2, Technikpaket 29.10.2026). Steht, wenn die offene Bühne
 * inzwischen abgelöst wurde. Kein Auto-Navigieren (Rafael 28.10.: „Kein … Auto-Navigieren ohne Rafaels Wort“) —
 * er klickt selbst.
 */
function WeiterAufNachfolgerin({ von, nachfolger, wort, onOeffnen, onWeg }: {
  von: string; nachfolger: string; wort: { text: string; am: string; vonRafael: boolean } | null;
  onOeffnen: (id: string) => void; onWeg?: () => void;
}) {
  const titel = useBuehnenTitel(nachfolger) ?? nachfolger;
  return <div data-ai-id="guide-buehne-weiter-auf" data-buehne-id={von} data-nachfolger={nachfolger} role="status"
    style={{ ...box, color: 'var(--tn-text)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
    <span>{wort?.vonRafael ? <>Dein Wort ist angekommen{wortZeit(wort.am) ? ` (${wortZeit(wort.am)})` : ''}. </> : 'Diese Bühne wurde abgelöst. '}
      Weiter auf: <b>{titel}</b></span>
    <button type="button" data-ai-id="guide-buehne-weiter-auf-oeffnen" onClick={() => onOeffnen(nachfolger)}
      title={`Nachfolgerin öffnen (${nachfolger})`}
      style={{ padding: '4px 10px', fontSize: 11, borderRadius: 5, cursor: 'pointer',
        border: '1px solid var(--tn-accent,var(--tn-link))', background: 'var(--tn-accent,var(--tn-link))', color: 'var(--tn-tief)' }}>
      öffnen
    </button>
    {onWeg && <button type="button" data-ai-id="guide-buehne-weiter-auf-weg" onClick={onWeg}
      style={{ padding: '4px 10px', fontSize: 11, borderRadius: 5, cursor: 'pointer',
        border: '1px solid var(--tn-border)', background: 'var(--tn-bg)', color: 'var(--tn-text-muted)' }}>
      Später
    </button>}
  </div>;
}

/**
 * „DEINE FRAGE WIRD HIER BEANTWORTET: <Y> ÖFFNEN" (Rafael 29.10.2026 10:15:11Z, v-frontend-freie-frage-verweis-20261029 a).
 * Je Ergebnisbühne, die Rafaels Wort an dieser Bühne beantwortet, ohne sie abzulösen, ein Satz mit Knopf auf genau
 * diese Bühne. Kein Auto-Navigieren.
 */
function FrageBeantwortetAuf({ buehne, onOeffnen }: { buehne: BuehneAnsicht; onOeffnen: (id: string) => void }) {
  const liste = buehne.beantwortet_auf ?? [];
  if (!liste.length) return null;
  return <>{liste.map(y => (
    <div key={y.id} data-ai-id="guide-buehne-frage-beantwortet" data-buehne-id={buehne.id} data-antwort-buehne={y.id} role="status"
      style={{ ...box, color: 'var(--tn-text)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, borderColor: 'var(--tn-blue)' }}>
      <span>Deine Frage wird hier beantwortet: <b>{y.titel || y.id}</b>{y.angelegt_am && wortZeit(y.angelegt_am) ? ` (angelegt ${wortZeit(y.angelegt_am)})` : ''}</span>
      <button type="button" data-ai-id="guide-buehne-frage-beantwortet-oeffnen" onClick={() => onOeffnen(y.id)}
        title={`Ergebnisbühne öffnen (${y.id})`}
        style={{ padding: '4px 10px', fontSize: 11, borderRadius: 5, cursor: 'pointer',
          border: '1px solid var(--tn-accent,var(--tn-link))', background: 'var(--tn-accent,var(--tn-link))', color: 'var(--tn-tief)' }}>
        öffnen
      </button>
    </div>
  ))}</>;
}

/** Eine historische Bühne montiert nur ihr gespeichertes PNG, nie alte Sessions. */
function GeteilteBuehneBearbeiten({buehne,onFehler,onGestartet}:{buehne:BuehneAnsicht;onFehler:(text:string|null)=>void;onGestartet:()=>void}) {
  const [busy,setBusy]=useState(false);
  if(!buehne.geteilt_von?.shareId)return null;
  return <><span style={{fontSize:11,color:'var(--tn-text-muted)'}}>Geteilt von {buehne.geteilt_von.name}</span>
    {!buehne.sitzungen.length && <button disabled={busy} onClick={async()=>{
      setBusy(true);onFehler(null);
      try {
        await httpBoundary({name:"cui.geteilte-buehne.berater",url:`${API}/guide/buehnen/berater-einsetzen`,method:"POST",
          body:{buehne:buehne.id,auftrag:`Hilf mir, meine eigene Bühne „${buehne.titel}“ zu bearbeiten. Lies zuerst ihre Grundlage und Unterlagen. Warte dann auf meinen konkreten Arbeitsauftrag.`,von:"rafael"},
          schema:z.object({sitzung:z.string().min(1),buehne:z.object({id:z.string().min(1)})}),timeoutMs:150000});
        onGestartet();
      } catch(e){onFehler((e as Error).message);} finally{setBusy(false);}
    }}>{busy?'Berater startet …':'Mit eigenem Berater bearbeiten'}</button>}
  </>;
}

function HistorischeBuehne({ buehne, onZurueck, onOeffnen }: { buehne: BuehneAnsicht; onZurueck: () => void; onOeffnen?: (id: string) => void }) {
  const bildUrl = archivBildUrl(buehne);
  /* B1 (Technikpaket 29.10.2026): das Foto rechts ist eingefroren und kann „Neueste – du bist hier“ oder
     offene Wahlen zeigen. Hier steht sichtbar, was daraus wurde — Nachfolgerin, angenommenes Wort, und
     dass damalige Aufgaben an Rafael Archiv sind. */
  const nachfolger = buehne.abschluss?.art === 'abgeloest' ? buehne.nachfolger : undefined;
  const nachfolgerTitel = useBuehnenTitel(nachfolger);
  const wort = angenommenesWortDerBuehne(buehne);
  const plan = buehne.arbeitsplan;
  const planAnRafael = plan && (plan.zustaendig === 'rafael' || /\brafael\b/i.test(plan.naechsterSchritt)) ? plan : null;
  return <>
    {onOeffnen && <BuehnenKettenZeile buehne={buehne} onOeffnen={onOeffnen} />}
    <button type="button" data-ai-id="guide-buehne-schliessen" onClick={onZurueck}
      style={{ marginBottom: 8, fontSize: 11, padding: '3px 9px', borderRadius: 6, cursor: 'pointer', background: 'transparent', color: 'var(--tn-blue)', border: '1px solid var(--tn-border)' }}>
      ← zurück zur Screenshot-Galerie
    </button>
    <div data-ai-id="guide-buehne-historisch" data-buehne-id={buehne.id} style={{ ...box, fontSize: 11 }}>
      <b>Archivfoto · {buehne.titel}</b>
      <div style={{ color: 'var(--tn-text-muted)', marginTop: 3 }}>
        {buehne.abschluss?.art === 'abgeloest' ? 'Abgelöste Bühne' : 'Erledigte Bühne'} · {buehne.abschluss?.von} · {buehne.abschluss?.am ? new Date(buehne.abschluss.am).toLocaleString('de-AT') : ''}
      </div>
      {nachfolger && <div data-ai-id="guide-buehne-historisch-nachfolger" style={{ marginTop: 6, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--tn-orange)', color: 'var(--tn-text)' }}>
        Abgelöst durch <b>{nachfolgerTitel ?? nachfolger}</b>
        {onOeffnen && <> — <button type="button" data-ai-id="guide-buehne-historisch-nachfolger-oeffnen" onClick={() => onOeffnen(nachfolger)}
          style={{ padding: '1px 8px', fontSize: 11, borderRadius: 5, cursor: 'pointer', border: '1px solid var(--tn-border)', background: 'transparent', color: 'var(--tn-blue)' }}>
          aktuelle Fassung öffnen</button></>}
      </div>}
      {onOeffnen && <FrageBeantwortetAuf buehne={buehne} onOeffnen={onOeffnen} />}
      {wort && <div data-ai-id="guide-buehne-historisch-wort" style={{ marginTop: 6, color: 'var(--tn-text)' }}>
        {wort.vonRafael ? 'Deine angenommene Antwort' : 'Angenommene Antwort'}{wortZeit(wort.am) ? ` (${wortZeit(wort.am)})` : ''}: <b>{wort.text}</b>
      </div>}
      <div data-ai-id="guide-buehne-historisch-archiv" style={{ marginTop: 6, color: 'var(--tn-orange)' }}>
        Archiv: Fragen, Wahlen und Aufgaben im Foto sind der Stand von damals{wort ? ', beantwortet' : ''} — nichts davon wartet noch auf dich.
        {planAnRafael && <> Damals geplant: „{kurz(planAnRafael.naechsterSchritt, 140)}“{planAnRafael.zustaendig === 'rafael' ? ' (zuständig damals: Rafael)' : ''}.</>}
      </div>
      <div style={{ color: 'var(--tn-text-muted)', marginTop: 5 }}>
        Rechts steht der gespeicherte letzte Stand. Das ist ein Foto, keine wieder gestartete Session und nicht mehr bearbeitbar.
      </div>
      {bildUrl ? <a href={bildUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 7, color: 'var(--tn-blue)' }}>Bild separat öffnen</a>
        : <div role="alert" style={{ marginTop: 7, color: 'var(--tn-orange)' }}>{buehne.bild && 'fehlt' in buehne.bild ? `Kein Bild: ${buehne.bild.fehlt}` : 'Kein Archivfoto vorhanden.'}</div>}
    </div>
  </>;
}

function GuidePanelInhalt({ assistentModus = false, oeffnungsAuftrag, onAbdeckungRaeumen, onOeffnenWorkspace, onOeffnenSitzung, onSchliessen, onBuehne, onBuehnenLayout, onBeraterBuehne, onBeraterOeffnen, onArbeit, breitDargestellt, startThema, startKarte, startBuehne }: Props) {
  /* Wer ist das? Gebraucht, um einem Partner die Betriebsdiagnostik wegzulassen,
   * die ihn nichts angeht (Rafael, 22.09.2026: „Weglassen, was ihm nicht gehoert"). */
  const { user, authEnabled } = useAuth();
  const istBetreiber = !authEnabled || user?.role === 'admin';
  const [board, setBoard] = useState<Board | null>(null);
  /*
   * Das gewaehlte Thema ueberlebt ein Neuladen — sonst landet Rafael nach jedem
   * Frontend-Push zwar wieder im Guide (App merkt sich den Modus), aber auf der
   * Buehne statt bei dem Thema, an dem er gerade war.
   */
  /*
   * KEIN THEMA MEHR (11.09.2026). Die Themenansicht ist aus der Spalte verschwunden;
   * damit gibt es keinen Weg mehr, `themaId` zu setzen — auch nicht aus einem alten
   * localStorage-Eintrag, der Rafael sonst nach dem naechsten Neuladen in eine
   * Ansicht zurueckwerfen wuerde, die es nicht mehr gibt. Der Zustand steht noch, weil
   * die Themen-Bausteine weiter unten in dieser Datei liegen und ein Ausbau in EINEM
   * Schritt zu viel auf einmal waere; erreichbar ist er nicht.
   */
  const [themaId, setThemaId] = useState<string | null>(null);
  const [sicht, setSicht] = useState<ThemaSicht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const { probleme: leseprobleme, beginneLesen, meldeLesefehler, bestaetigeLesen } = useGuideLesefehler();
  const [hausHinweis, setHausHinweis] = useState<string | null>(null);
  /** B2 (Technikpaket 29.10.2026): die offene Bühne wurde abgelöst — Satz mit Knopf, kein Auto-Navigieren. */
  const [weiterAuf, setWeiterAuf] = useState<{ von: string; nachfolger: string; wort: { text: string; am: string; vonRafael: boolean } | null } | null>(null);
  /*
   * DIE BEANTWORTETE BUEHNE BLEIBT STEHEN, BIS DIE NAECHSTE DA IST (Kanzler Dev auf Rafaels
   * Klage vom 14.10.2026 07:23–07:27Z: „seh keine bühne", „wenn du sagst du zeigst mir eine
   * bühne kommt einfach nichts"). Gemessen: nach seiner Antwort leerte der Vorschub die Flaeche
   * (07:23:15), der Assistent oeffnete die naechste erst 31 s spaeter — dazwischen stand nur
   * „Der Guide hat das Wort". Jetzt bleibt die Buehne stehen, oben ein Satz mit Uhrzeit; nach
   * ASSISTENT_FRIST_MS ohne naechste Buehne steht laut da, dass der Assistent nicht antwortet.
   */
  const [wartetAufNaechste, setWartetAufNaechste] = useState<{ buehneId: string; seit: number; gespeichert: number | null; art: 'antwort' | 'nachricht' } | null>(null);
  const [wartenUhr, setWartenUhr] = useState(() => Date.now());
  const wartetAufNaechsteRef = useRef(wartetAufNaechste);
  wartetAufNaechsteRef.current = wartetAufNaechste;
  /*
   * DER SONDERFALL „ARBEITSBEREICH FEHLT IN DIESEM HAUS" — eigener Zustand seit der
   * Arbeitsliste (03.10.2026). Er stand als grosser Kasten ganz oben, auch noch, als die
   * Buehne laengst wieder zu war. Jetzt: bei offener Buehne eine kleine Zeile ueber ihr
   * (dort erklaert er, warum die Panels darunter nicht zur Buehne gehoeren), sonst im
   * „⋯"-Menue mit Punkt.
   */
  const [arbeitsbereichHinweis, setArbeitsbereichHinweis] = useState<string | null>(null);
  /** Das „⋯ Mehr"-Menue der Kopfzeile und sein Portalziel fuer die Betriebsleiste des Hofs. */
  const [mehrOffen, setMehrOffen] = useState(false);
  const [mehrZiel, setMehrZiel] = useState<HTMLDivElement | null>(null);
  const [hofWarnung, setHofWarnung] = useState<string | null>(null);
  /** Welches Formular unter der Kopfzeile offen ist (Neue Bühne, Notiz, Neustart). */
  const [notizArt, setNotizArt] = useState<NotizArt>(null);
  /*
   * EIN OEFFNUNGSAUFTRAG, DER NICHT AUSGEFUEHRT WURDE, WEIL RAFAEL LIEST.
   * Kein Hinweis-Text, sondern ein Angebot mit Knopf — siehe die Begruendung am
   * Auftrags-Effekt weiter unten. Es ueberschreibt sich selbst: kommt waehrend des
   * Lesens ein zweiter Auftrag, steht der juengere da. Zwei Angebote uebereinander
   * waeren eine Warteschlange, die er abarbeiten muesste, und genau die soll es hier
   * nicht geben.
   */
  const [angebot, setAngebot] = useState<{ buehneId: string; titel: string } | null>(null);
  /** Arbeitsbereiche, deren Layout gerade hergerichtet ist (Rueckweg moeglich). */
  const [sicherungen, setSicherungen] = useState<Record<string, { gesichert_am: string }>>({});
  /** Was beim letzten Herrichten aus dem Blick fiel — laeuft weiter, sieht man nur nicht. */
  const [ausDemBlick, setAusDemBlick] = useState<{ anzahl: number; laufen: number } | null>(null);
  /** Gesetzt, wenn ein Rezept vorliegt, aber (noch) nicht als geprueft gilt. */
  /** Thema angeklickt, hinter dem gerade nichts liegt — bewusst NICHT hingeschaltet. */
  /**
   * ZWEI ZUSTAENDE, NICHT DREI.
   *
   * Rafael, 06.09. 15:2xZ, mit Bild: "Ein Stand ist nicht vorbereitet kann es
   * verdammt nochmal nicht geben. Es gibt nur 'Wird vorbereitet' oder das fertige
   * Layout. Was heisst: Fenster nicht hergerichtet?"
   *
   * Also traegt dieser Zustand ab jetzt die ARBEITENDE SITZUNG, nicht eine
   * Begruendung. Arbeitet jemand daran, ist das Thema "wird vorbereitet" und der
   * Klick fuehrt in deren Chat. Arbeitet niemand daran, gibt es nichts zu klicken —
   * dann ist das Thema kein Angebot, sondern eine Zielzeile.
   *
   * Unsere Innenbegriffe (Rezept, geprueft, angewendet, hergerichtet) stehen nirgends
   * mehr in seiner Anzeige; das Pruef-Tor bleibt, sein Wortlaut geht ihn nichts an.
   */
  /*
   * Eine Sitzung, die Rafael im Zustand "wird vorbereitet" angeklickt hat und die jetzt als
   * Buehne (nur ihr Chat) steht. Rafael, 07.09. 11:0x: "Ich habe schon 100 Mal geklickt auf
   * den Achter, den wir hier anzeigen. Da zeigt man immer nur, dass der Guide das Wort hat,
   * ohne dass die Buehne da ist." Vorher oeffnete der Klick den Chat im Arbeitsbereich UNTER
   * der Flaeche "Der Guide hat das Wort" — unsichtbar, weil `leer` die Flaeche oben hielt.
   */
  const handWahl = useRef(0);
  const [sitzungsBuehne, setSitzungsBuehne] = useState<{ id: string; name: string } | null>(null);
  /** Die Buehne, deren Fenster gerade hergerichtet ist. Hier oben deklariert, weil
   *  der Vorhang-Effekt weiter unten sie schon braucht. */
  const [offeneBuehne, setOffeneBuehne] = useState<BuehneAnsicht | null>(null);
  /**
   * WAS RAFAEL IN DIESER SITZUNG SCHON VOR SICH HATTE — sofort, nicht beim naechsten Abruf.
   *
   * Seine Klage, 21.09.2026: *"Vor allem durch das, dass er immer noch braucht, statt
   * dass er das sofort verschiebt, landen da Sachen, die ich eigentlich gerade
   * beantwortet habe, auf Bühne vor, Bühne zurück."* Der Serverstand allein reicht
   * dafuer nicht: `vorrat` kommt aus einem Abruf, der erst spaeter wieder laeuft, und
   * genau diese Luecke hat er gesehen. Deshalb steht das Gedaechtnis auch hier, lokal,
   * und wird mit dem Serverfeld `rafael_gesehen` vereinigt.
   */
  const [schonVorgelegt, setSchonVorgelegt] = useState<ReadonlySet<string>>(() => new Set());
  /*
   * WAS VORHER OFFEN WAR - damit `buehneOeffnen` es zurueckstellen kann, wenn die
   * rechte Seite nicht nachkommt (siehe dort). Ein Ref und kein State: der Wert wird
   * nur im Verlauf eines Klicks gelesen, nie gerendert, und darf `buehneOeffnen`
   * nicht bei jedem Buehnenwechsel neu erzeugen.
   */
  const offeneBuehneRef = useRef<BuehneAnsicht | null>(null);
  useEffect(() => { offeneBuehneRef.current = offeneBuehne; }, [offeneBuehne]);
  /* Im Assistent-Modus steuert der Assistent, was offen ist — siehe Öffnungsauftrag und
   * `sofortWeiter`. Als Ref, weil beide Wege ihn erst nach dem Rendern lesen. */
  const abdeckungRaeumenRef = useRef(onAbdeckungRaeumen);
  abdeckungRaeumenRef.current = onAbdeckungRaeumen;
  const assistentModusRef = useRef(assistentModus);
  assistentModusRef.current = assistentModus;
  /**
   * MARKIEREN, NIE LEEREN (Rafael, 19.09.2026, Kanzler-Buendel Block 1 "Buehnenverfall").
   * Der 20s-Poll unten zeigte bei Fetch-Fehler den letzten Snapshot bisher stumm weiter
   * — kein Feld sagte, dass er veraltet sein koennte. `null` = zuletzt erfolgreich,
   * sonst der Zeitpunkt des ERSTEN von ggf. mehreren aufeinanderfolgenden Fehlern
   * (nicht bei jedem einzelnen neu gesetzt, sonst wandert die Uhrzeit alle 20s weiter
   * und sieht wie ein frischer Fehler statt eines andauernden aus).
   */
  const [buehnenAktualisierungsproblem, setBuehnenAktualisierungsproblem] = useState<{seit: number; grund: string} | null>(null);
  const buehneVeraltetSeit = buehnenAktualisierungsproblem?.seit ?? null;
  const meldeAktualisierungsproblem = useCallback((error: unknown) => {
    setBuehnenAktualisierungsproblem(alt => ({seit: alt?.seit ?? Date.now(), grund: stageRefreshHinweis(error)}));
  }, []);
  const [historieOffen, setHistorieOffen] = useState(false);
  useStageUsage(historieOffen ? undefined : offeneBuehne?.id, offeneBuehne?.haus);
  // Das Warten endet mit jeder echten Öffnung (buehneOeffnen). Die Uhr läuft nur, solange die
  // wartende Bühne auch zu sehen ist — nicht nach „← Übersicht".
  const wartendSichtbar = !!offeneBuehne && wartetAufNaechste?.buehneId === offeneBuehne.id;
  useEffect(() => {
    if (!wartendSichtbar) return;
    setWartenUhr(Date.now());
    const t = setInterval(() => setWartenUhr(Date.now()), 2000);
    return () => clearInterval(t);
  }, [wartendSichtbar, wartetAufNaechste]);
  const [historieStart, setHistorieStart] = useState<string | null>(null);
  const { jobs: sendeAuftraege, queue: sendeQueue, enqueue: sendeEinreihen } = useGuideSend();
  const buehnenVorrat = useRef<BuehneAnsicht[]>([]);
  /* Warteschlange (Phase 1 der Guide-Flaeche): Rafaels Tagesreihenfolge aus „Heute dran“; gemerkt je Tag und Nutzer ueber useUiPreferences. */
  const heuteNutzer = user?.id ?? '__ohne_anmeldung__';
  const heuteTag = tagesSchluessel(Date.now());
  const heuteGespeichert = useHeuteWahl(heuteNutzer, heuteTag);
  const steuerZustand = useGuideAnsichtSteuerung();
  const queuePos = useRef<number | null>(null);
  const [queueEnde, setQueueEnde] = useState(false);
  /** „Erledigt → Weiter“ mit Ungesendetem im Feld fragt EINMAL nach: Id der Buehne + Zeitpunkt des ersten Klicks. Scharf gilt nur fuer die offene Buehne und hoechstens 5 s; der zweite Klick wird
   *  erst nach 700 ms angenommen (ein Doppelklick umgeht die Nachfrage nicht). Der Ref ist die Wahrheit (zwei Klicks im selben Tick), der State zeichnet nur neu. */
  const erledigtScharfRef = useRef<{ id: string; ab: number } | null>(null);
  const [, setScharfTick] = useState(0);
  const erledigtScharf = erledigtScharfRef.current;
  const scharfJetzt = !!offeneBuehne && erledigtScharf?.id === offeneBuehne.id && Date.now() - erledigtScharf.ab < 5000;
  const scharfGesperrt = scharfJetzt && erledigtScharf !== null && Date.now() - erledigtScharf.ab < 700;
  useEffect(() => {
    if (!erledigtScharf) return;
    const bereit = setTimeout(() => setScharfTick(n => n + 1), 720);
    const ab = setTimeout(() => { erledigtScharfRef.current = null; setScharfTick(n => n + 1); }, 5000 - Math.min(5000, Date.now() - erledigtScharf.ab));
    return () => { clearTimeout(bereit); clearTimeout(ab); };
  }, [erledigtScharf]);
  useEffect(() => { erledigtScharfRef.current = null; setScharfTick(n => n + 1); }, [offeneBuehne?.id]);   // Buehnenwechsel setzt zurueck: eine Rueckkehr fragt neu
  const abgegeben = useRef(new Set<string>());
  const hierHausRef = useRef<'dev' | 'partner' | null>(null);
  /*
   * DERSELBE VORRAT NOCHMAL ALS STATE. Das Ref oben genuegt fuer `sofortWeiter`,
   * das ihn nur im Verlauf eines Klicks liest. Die Vor/Zurueck-Knoepfe im Kopf der
   * offenen Buehne muessen sich aber MITZEICHNEN, wenn sich die Reihe aendert -
   * ein Ref loest kein Neuzeichnen aus, und die Knoepfe zeigten dann auf Nachbarn,
   * die es nicht mehr gibt. Der Vergleich auf die Kennungen haelt die Identitaet
   * stabil, solange sich die Reihe nicht wirklich geaendert hat: die Hofansicht
   * meldet alle 20 Sekunden, meist mit demselben Inhalt.
   */
  const [vorrat, setVorrat] = useState<BuehneAnsicht[]>([]);
  const merkeBestand = useCallback((b: BuehneAnsicht[], hierHaus: 'dev' | 'partner' | null) => {
    buehnenVorrat.current = b; hierHausRef.current = hierHaus;
    setVorrat(alt => (alt.length === b.length && alt.every((x, i) => x.id === b[i].id)) ? alt : b);
  }, []);
  /** Fuer den Vor/Zurueck-Knopf am offenen Kopf — siehe `benachbarteBuehnen`. */
  const { zurueck: buehneZurueck, vor: buehneVor } = useMemo(
    () => (offeneBuehne ? benachbarteBuehnen(offeneBuehne) : { zurueck: [], vor: [] }),
    [offeneBuehne],
  );
  useEffect(() => { onBeraterBuehne?.(offeneBuehne ? { buehne: offeneBuehne, hierHaus: hierHausRef.current } : null); }, [offeneBuehne, onBeraterBuehne]);
  useEffect(() => () => onBeraterBuehne?.(null), [onBeraterBuehne]);
  const guideInhaltRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (offeneBuehne && guideInhaltRef.current) guideInhaltRef.current.scrollTop = 0;
  }, [offeneBuehne?.id]);
  /**
   * DIE ENTSCHEIDUNGEN DER OFFENEN BUEHNE — links zum Klicken.
   *
   * Rafael, 11.09. 07:5xZ: "aber ist das dann das eine HTML, das jede Buehne hat?
   * Weil es gibt ja ein Kontext-HTML — das frei erklaert, worum es geht — und die
   * Entscheidungen wurden ja links im Guide eingeblendet, oder?"
   *
   * Er hat recht, und die Trennung laeuft entlang einer einzigen Frage: KLICKT er
   * das, oder LIEST er das?
   *   Die Entscheidung (Karte, hier in der Spalte): Frage, hoechstens drei Optionen
   *     mit ihrer Folge, Empfehlung, Gegenargument, was ohne Entscheidung passiert.
   *   Der Kontext (Textblock, rechts auf der Buehne): wo er ist, worum es geht, die
   *     Lage, Risiken, was NICHT passiert, was NICHT geprueft wurde, Quellen.
   * Ein Kontext-HTML braucht deshalb WEDER Optionen NOCH Empfehlung — die stehen
   * hier. Wer beides in eine Seite legt, bekommt eine, die nichts von beidem ist.
   */

  const [leer, setLeer] = useState<{
    thema: string;
    arbeitende: Sitzung[];
    /** Fuer den Not-Knopf: welche Karte, welches Rezept, welcher Arbeitsbereich. */
    karteId?: string | null;
    rezept?: Layoutrezept | null;
    zielProjekt?: string | null;
    /** Welcher Reiter fuer den Not-Knopf vorn stehen soll (siehe `auswahlFuerKarte`). */
    auswahl?: BuehnenAuswahl;
    /**
     * Die Karte, die hier noch NICHT fertig ist — mit Titel, seit wann, und was fehlt.
     * Rafael, 07.09. 08:38Z: er klickte eine Entscheidung an und bekam "wird
     * vorbereitet" mit leerer Buehne, ohne dass die Karte genannt wurde. Er las das
     * als "immer noch das gleiche Problem". Was hier vorbereitet wird, muss dastehen.
     */
    karteTitel?: string | null;
    karteSeit?: string | null;
    fehlt?: string[];
    /**
     * Rafael hat eine Karte des ANDEREN Hauses angeklickt: ihre Frage liegt vor,
     * ihre Buehne wird drueben gebaut. Nicht "noch nicht fertig" — sie wird hier
     * nie fertig, und ein Satz, der Warten nahelegt, waere gelogen.
     */
    karteFremd?: string | null;
    /**
     * Weder Karte noch eigenes Rezept — kein Fensterprobelm, sondern der
     * Normalfall eines Themas ohne offene Frage (Rafael, 09.09.2026 06:09Z,
     * Block "3 · Report": Klick fuehrte in eine leere Themenansicht). Haelt
     * `aktiv` (die Buehne bleibt Vollbild, siehe unten) auf true, ohne die
     * sparsame Box unten zu zeigen — ThemaAnsicht traegt hier den Kontext.
     */
    keineKarte?: boolean;
    /**
     * Das eigene Haus hat nichts, das andere schon (Server: `fremdes_haus`). Dann
     * ist "liegt nichts vor" schlicht falsch — die Entscheidungen stehen links in
     * der Fragenliste, nur ihre Buehne steht drueben. Siehe Grundtext unten.
     */
    fremd?: { anzahl: number; haus: 'dev' | 'partner'; titel: string } | null;
  } | null>(null);
  /**
   * NACH DER ANTWORT: AUSWAHL, KEIN SPRUNG.
   *
   * Rafael, 06.09.: nach einer Antwort soll die naechste Entscheidung nicht von
   * selbst aufspringen — er will sehen, was ansteht, und waehlen. Ein automatischer
   * Sprung setzt ihn vor eine Frage, die er nicht gewaehlt hat, und die eben
   * beantwortete Karte bleibt dabei gern noch einen Moment stehen.
   *
   * Der Klick geht danach durch `themaWaehlen` — also denselben Weg wie der
   * Themen-Klick, mitsamt Laden des vorbereiteten Fensters.
   */
  const [auswahl, setAuswahl] = useState<{ themen: BoardThema[]; ohneThema: number; offenGesamt: number } | null>(null);
  /** Wie viele offene Karten auf welcher Stufe stehen — macht den Kopf-Schalter ehrlich. */

  /*
   * HOECHSTENS EIN /board UNTERWEGS, EINER NACHGEHOLT (23.09.2026). Jede Eingabe irgendwo
   * sendet `guide-updated`; bei 1–14 s Antwortzeit lagen hier sonst mehrere Abrufe
   * gleichzeitig in der Leitung, jeder davon ein Platz, der Rafaels Sende-POST fehlte
   * (gemessen: bis 131 s Rueckstand, Fall b-muehl-bericht-staging-20260923). Was
   * waehrenddessen angefordert wird, holt EIN Abruf danach nach — nichts geht verloren.
   */
  const boardAbruf = useRef({ laeuft: false, nochmal: false });
  const ladeBoard = useCallback(async () => {
    // Rafaels Schaltpult — fuer einen Partner gesperrt, also gar nicht erst rufen.
    if (!istBetreiber) return;
    const abruf = boardAbruf.current;
    if (abruf.laeuft) { abruf.nochmal = true; return; }
    abruf.laeuft = true;
    try {
      do {
        abruf.nochmal = false;
        const start = performance.now();
        const leseAbruf = beginneLesen('board');
        let r: Response | null = null;
        try {
          // Hintergrundabfrage: Rafaels Eingaben gehen vor (siehe mitVorrang, GuideSendQueue.ts).
          /* MIT FRIST (31.10.2026): ohne sie hing ein Abruf unter Serverlast bis zum Server-Zeitablauf, und weil hoechstens
           * einer unterwegs ist, stand die ganze Spalte samt Fehlermeldung so lange still — der 20-s-Takt lief ins Leere. */
          r = await fetch(`${API}/guide/board`, { priority: 'low', signal: AbortSignal.timeout(20000) }); // trust-boundary:ok — Altbestand (Antwort ungeprueft wie bisher), hier nur die Frist ergaenzt; httpBoundary kennt kein priority:'low'
          if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
          setBoard(await r.json());
          bestaetigeLesen(leseAbruf);
        } catch (e) {
          // Laut scheitern: eine stumm leere Spalte sieht aus wie "nichts zu tun".
          meldeLesefehler(leseAbruf, `Guide-Stand nicht geladen: ${guideNetzFehler(e)} (${e instanceof Error || e instanceof DOMException ? e.message : String(e)}) — neuer Versuch spätestens in 20 s.`);
        } finally {
          meldeLadezeit('/guide/board', start, r);
        }
      } while (abruf.nochmal);
    } finally { abruf.laeuft = false; }
  }, [istBetreiber, beginneLesen, meldeLesefehler, bestaetigeLesen]);

  /**
   * WELCHE KARTE GILT, WENN DIE SPALTE SICH SELBST NEU LAEDT.
   *
   * Ohne diese Antwort verliert sie jeder Neulade-Pfad. Bis 09.09.2026 19:1xZ fuhr die
   * angeklickte Kennung nur in `themaWaehlen` mit; `ladeThema` — der Weg, den der
   * Effekt auf `themaId` und jedes `neuladen()` nehmen — fragte ohne sie. Folge waere
   * gewesen: die richtige Frage erscheint, und Sekunden spaeter steht wortlos wieder
   * die Rang-1-Karte des Themas da, sobald irgendeine fremde Sitzung eine Karte
   * einstellt ('guide-updated' -> neuladen). Bei einem Klick in ein ANDERES Thema
   * liefen sogar beide Abrufe gleichzeitig — ein Rennen, dessen Ausgang niemand
   * festgelegt hatte.
   *
   * Der Wunsch ist deshalb Zustand, nicht Parameter eines einzelnen Aufrufs. AN DAS
   * THEMA GEBUNDEN: eine Karte aus Thema A darf nicht an Thema B kleben, sonst
   * bekaeme Rafael beim naechsten Thema wieder etwas, das er nicht angeklickt hat.
   * Als Ref und nicht als State, weil ein Zustandswechsel hier den Effekt auf
   * `themaId` erneut ausloesen wuerde — der Wunsch soll Abrufe begleiten, keine
   * anstossen.
   */
  const kartenwunsch = useRef<{ thema: string; karte: string } | null>(
    /*
     * Aus dem Deep-Link vorbelegt — und zwar beim RENDERN, nicht in einem Effekt:
     * der Effekt auf `themaId` laeuft beim ersten Aufbau sofort los und ruft
     * `ladeThema`. Wuerde der Wunsch erst in einem Effekt gesetzt, kaeme er zu
     * spaet, und die Aufnahme zeigte wieder die Rang-1-Karte statt der benannten.
     */
    startThema && startKarte ? { thema: startThema, karte: startKarte } : null,
  );

  const ladeThema = useCallback(async (id: string) => {
    const leseAbruf = beginneLesen('thema');
    const wahl = handWahl.current;
    const start = performance.now();
    let r: Response | null = null;
    try {
      const wunsch = kartenwunsch.current?.thema === id ? kartenwunsch.current.karte : null;
      r = await fetch(`${API}/guide/thema/${encodeURIComponent(id)}`
        + (wunsch ? `?karte=${encodeURIComponent(wunsch)}` : ''));
      if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
      const d = await r.json() as ThemaSicht;
      if (wahl !== handWahl.current) return;
      /*
       * Die gewuenschte Karte gibt es nicht mehr (beantwortet, zurueckgestellt,
       * geloescht): den Wunsch fallen lassen, statt ihn ewig weiterzureichen. Sonst
       * traegt jeder kuenftige Abruf eine Kennung mit, die nur noch `gefunden: false`
       * erzeugt — und die Spalte haette einen Zustand, den sie nie wieder loswird.
       */
      if (d.karte_gewuenscht && !d.karte_gewuenscht.gefunden) kartenwunsch.current = null;
      setSicht(d);
      bestaetigeLesen(leseAbruf);
    } catch (e) {
      if (wahl === handWahl.current) meldeLesefehler(leseAbruf, String(e));
    } finally {
      meldeLadezeit(`/guide/thema/${id}`, start, r);
    }
  }, [beginneLesen, meldeLesefehler, bestaetigeLesen]);

  const ladeSicherungen = useCallback(async () => {
    /* Die Layout-Sicherung ist Rafaels Rueckweg-Knopf und fuer einen Partner gesperrt.
     * Nicht rufen statt die 403 hinterher wegzublenden (Rafael, 22.09.2026). */
    if (!istBetreiber) return;
    try {
      const r = await fetch(`${API}/guide/layout-sicherung`);
      if (r.ok) setSicherungen((await r.json()).sicherungen ?? {});
    } catch { /* der Rueckweg-Knopf fehlt dann; kein Grund, die Spalte zu blockieren */ }
  }, [istBetreiber]);

  /**
   * UNTERBRECHUNGSSCHUTZ, waehrend Rafael an einer Karte sitzt.
   *
   * Recherche 06.09. (stark belegt, Mark et al. 2005/2008): die Wiederaufnahme
   * nach einer Unterbrechung kostet Minuten und erzeugt Stress. Die Spalte
   * unterbricht sich bisher selbst — jede fremde Session, die eine Karte
   * einstellt oder einen Board-Schritt abhakt, loest hier ein Neuladen aus, und
   * mitten im Lesen springt die Karte weg oder die Auswahl verschwindet.
   *
   * Ab dem ersten Griff an eine Karte (Option gewaehlt, Modal offen) wird nicht
   * mehr neu geladen; die Aktualisierung wird gemerkt und laeuft nach, sobald er
   * fertig ist. Verworfen wird nichts — nur verschoben.
   */
  const inArbeit = useRef(false);
  const nachzuholen = useRef(false);

  const neuladen = useCallback(() => {
    if (inArbeit.current) { nachzuholen.current = true; return; }
    ladeBoard();
    ladeSicherungen();

    if (themaId) ladeThema(themaId);
  }, [ladeBoard, ladeSicherungen, ladeThema, themaId]);

  /** Meldet, ob gerade an einer Karte gearbeitet wird; loest den Nachzug aus. */
  const arbeitMelden = useCallback((aktiv: boolean) => {
    inArbeit.current = aktiv;
    // Dieselbe Meldung geht nach oben: App verschiebt damit ein faelliges Neuladen
    // der ganzen Oberflaeche. Der Guide hat seinen eigenen Nachzug schon, aber ein
    // window.location.reload() nimmt ihm auch den getippten Satz.
    onArbeit?.(aktiv);
    if (!aktiv && nachzuholen.current) { nachzuholen.current = false; neuladen(); }
  }, [neuladen, onArbeit]);

  const offeneAktualisierung = useRef<() => void>(() => {});
  /* Eine Welle von `guide-updated` (Rafael klickt schnell durch, mehrere Berater
   * schreiben zugleich) wird zu EINEM Nachladen 400 ms nach der letzten Meldung. */
  const buendelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const neuladenRef = useRef(neuladen);
  neuladenRef.current = neuladen;
  const neuladenGebuendelt = useCallback(() => {
    if (buendelTimer.current) return;
    buendelTimer.current = setTimeout(() => {
      buendelTimer.current = null;
      neuladenRef.current();
      offeneAktualisierung.current();
    }, 400);
  }, []);
  useEffect(() => () => {
    if (buendelTimer.current) clearTimeout(buendelTimer.current);
    buendelTimer.current = null;
  }, []);
  const guideVerbindung = useLiveChannel({
    label: 'Guide',
    onMessage: (msg: any) => {
      // Das Board haengt an BEIDEN Staenden: Karten leben im Guide-Store, Themen und
      // Meilensteine im Fokus-Board. Ein abgehakter Schritt muss hier ankommen.
      if (msg?.type === 'guide-updated' || msg?.type === 'partner-planning-updated') neuladenGebuendelt();
    },
    onResync: () => { neuladen(); offeneAktualisierung.current(); },
  });

  useEffect(() => { ladeBoard(); ladeSicherungen(); }, [ladeBoard, ladeSicherungen]);

  /*
   * DIE BUEHNE MELDEN.
   *
   * Rafael, 06.09.: "Der Guide faehrt von links in den bestehenden Workspace
   * hinein. Das ist eine eigene Sitzung, die nur den Coach betrifft — sie hat mit
   * der Arbeit im offenen Workspace nichts zu tun, und die Vermischung ist genau
   * das, was der Guide verhindern soll."
   *
   * Solange kein Thema gewaehlt ist, ist der Guide also nicht Beiwerk neben einem
   * fremden Arbeitsbereich, sondern die Flaeche selbst. Erst mit der Wahl eines
   * Themas wird er wieder zur Spalte neben dem Fenster, in das er fuehrt.
   */
  /*
   * DER VORHANG — und warum er ab jetzt nur noch EINE Frage stellt.
   *
   * Der Guide meldet ueber `onBuehne(aktiv)`, ob ER gerade die Flaeche ist; App legt
   * dann eine Ueberlagerung ueber den Arbeitsbereich (position absolute, zIndex 2).
   * Sie schaltet nichts um und schreibt kein Layout — darunter laeuft alles weiter.
   *
   * BIS HEUTE HING DIESE BEDINGUNG AM THEMA: `themaId === null || leer !== null`.
   * Das war die teuerste Zeile des ganzen Umbaus. Eine geoeffnete Buehne ist kein
   * Thema — also lag der Vorhang ueber dem frisch montierten Fenster: der DOM-Vertrag
   * war gruen, das Fenster leer, und gefunden hat es nicht der Test, sondern das
   * eigene Foto (W1, 11.09.). W1 hat die Buehne in die Bedingung nachgetragen; mit
   * dem Thema faellt jetzt auch der Rest weg.
   *
   * ES BLEIBT EINE FRAGE: steht gerade etwas Hergerichtetes rechts? Eine offene
   * Buehne oder ein als Buehne montierter Chat — dann hat der Guide NICHT das Wort.
   * Sonst doch.
   *
   * >>> WER HIER ETWAS AENDERT: JEDE neue Sicht, die etwas montiert, muss in dieser
   * >>> Bedingung stehen. Sonst ist sie unsichtbar und trotzdem "gruen". Danach
   * >>> selbst fotografieren, nicht nur Selektoren messen. <<<
   *
   * `grund` ist damit immer null: der frueher gebaute Satz erklaerte, warum es fuer
   * ein THEMA keine Buehne gibt, und Themen gibt es hier nicht mehr. Den Text fuer
   * den leeren Fall schreibt App (guide-buehne-grund) — an einer Stelle, nicht an
   * zweien.
   */
  useEffect(() => {
    onBuehne?.(sitzungsBuehne === null && offeneBuehne === null, null);
  }, [onBuehne, sitzungsBuehne, offeneBuehne]);
  /*
   * JEDER THEMENWECHSEL RAEUMT DIE BUEHNE AB — die der Karte UND die der Sitzung.
   *
   * Rafael, 07.09. 09:07Z (Bild b0l4): Klick auf "Privat", Spalte sagt "wird
   * vorbereitet", im Fenster daneben aber weiter die Buehne der Energy-Historie
   * (Seite + Chat) vom vorigen Thema. Bisher haengt das Abraeumen an dem Zweig, den
   * die Themenansicht nach dem Laden nimmt; hier haengt es am Wechsel selbst, und
   * damit an nichts, was erst noch geladen werden muss.
   */
  /*
   * NUR DER THEMENWECHSEL RAEUMT AB, NICHT EINE NEUE FUNKTIONSIDENTITAET (ASSIOEFFNEN,
   * 13.10.2026). `onBuehnenLayout` stand bis hierher in den Abhaengigkeiten. App.tsx baut
   * `guideBuehnenLayout` neu, sobald sich der Zustand des zweiten Bildschirms aendert
   * (`b2Vollbild`, `zweiterSenden`), und jedes Mal leerte dieser Effekt rechts die Buehne,
   * waehrend links `offeneBuehne` stehen blieb — links eine Buehne, rechts „Der Guide hat das
   * Wort". Gefunden im Sichtbeleg: ein Aufrufer mit wechselnder Funktion genuegte dafuer.
   */
  const buehnenLayoutRef = useRef(onBuehnenLayout);
  buehnenLayoutRef.current = onBuehnenLayout;
  const uebergebenesLayout = useRef<{ doc: unknown; projectId: string; bezug?: BuehnenBezug; rezeptStand?: string } | null>(null);
  const setzeBuehnenLayout = useCallback((doc: unknown | null, projectId: string | null, bezug?: BuehnenBezug, stand?: string) => {
    uebergebenesLayout.current = doc && projectId ? { doc, projectId, bezug, rezeptStand: stand } : null;
    buehnenLayoutRef.current?.(doc, projectId, bezug);
  }, []);
  useEffect(() => {
    // Ein direkter Beraterwechsel setzt das Thema zurück und hat seine Bühne
    // bereits montiert. Dieser Effekt darf sie nicht wieder entfernen.
    if (themaId === null && sitzungsBuehne !== null) return;
    setzeBuehnenLayout(null, null);
    setSitzungsBuehne(null);
  }, [themaId]);
  useEffect(() => () => { onBuehne?.(false); }, [onBuehne]);

  /*
   * Der Fertig-Zustand haengt auch daran, ob die wartende Sitzung noch LEBT — und
   * dafuer gibt es kein Ereignis: ein Prozess, der stirbt, schreibt nichts in den
   * Guide-Store und nichts ins Board. Ohne diesen Nachzug bliebe ein Thema gruen,
   * dessen Sitzung vor zehn Minuten ausgegangen ist, und Rafael klickte wieder ins
   * Leere. Zwanzig Sekunden sind der Kompromiss: schnell genug, dass er es nicht
   * merkt, selten genug, dass es niemandem wehtut.
   */
  useEffect(() => {
    const t = setInterval(() => ladeBoard(), 20000);
    return () => clearInterval(t);
  }, [ladeBoard]);

  /**
   * Baut die Bühnenschicht im Speicher und merkt ihre Übergabe. Das Workspace-Layout bleibt unangetastet.
   */
  const herrichten = useCallback((projectId: string, rezept: Layoutrezept, auswahl?: BuehnenAuswahl, bezug?: BuehnenBezug, haus?: 'dev' | 'partner'): boolean => {
    const unbekannt = rezept.kacheln
      .filter(k => k.art === 'panel' && !BEKANNTE_PANELS.has(k.component ?? ''))
      .map(k => k.component);
    if (unbekannt.length) {
      // Laut statt leere Kachel: eine Kachel, die nichts anzeigt, sieht aus wie ein
      // kaputtes Panel und schickt die Suche in die falsche Richtung.
      setFehler(`Rezept nennt unbekannte Panels: ${unbekannt.join(', ')}`);
      return false;
    }
    /*
     * DAS REZEPT IST DAS LAYOUT — es wird gebaut, nicht geschrieben.
     *
     * Bis 06.09.2026 wurde hier das Layout des Arbeitsbereichs ueberschrieben
     * (`POST /layouts/<projectId>`), vorher gesichert und hinterher zurueckgestellt.
     * Ein Arbeitsbereich hat aber EIN Layout: von N offenen Karten konnte immer nur
     * die zuletzt hergerichtete passen. An einem Abend stritten fuenf Karten um
     * `werking-report`, sechs Fenster-Bestaetigungen wurden dadurch falsch, und ein
     * Livelock blieb nur aus, weil Sitzungen sich weigerten, ihrem eigenen Anstoss zu
     * folgen. Rafael dazu: „jede Buehne ist ein eigenes Layout — dort werden nur
     * Sachen gemountet."
     *
     * Seither entsteht aus dem Rezept ein Dokument im Speicher, das der LayoutManager
     * als Ueberlagerer montiert. Nichts wird gespeichert, nichts geteilt, nichts
     * zurueckgestellt: Rafaels eigenes Layout wird gar nicht mehr angefasst, und damit
     * gibt es auch keine Sicherung, die verlorengehen, und keinen Besitzstand, um den
     * gestritten werden koennte.
     */
    const { doc } = layoutAusRezept(rezept, null, auswahl, haus);
    setzeBuehnenLayout(doc, projectId, bezug, rezeptStand(projectId, rezept, bezug, haus));
    setAusDemBlick(null);
    setFehler(null);
    return true;
  }, [setzeBuehnenLayout]);

  /**
   * EINE BUEHNE OEFFNEN — der Klick, den Rafael meint.
   *
   * Drei Faelle, und keiner davon schweigt:
   *  - Raum gesperrt (Warteraum): der Berater arbeitet gerade die letzte Antwort ein,
   *    sein alter Stand waere irrefuehrend. Der Grund kommt vom Server, nicht von hier.
   *  - kein Rezept: die Buehne ist unfertig. Der Server laesst sie deshalb gar nicht
   *    erst in einen fertigen Raum; in den Arbeitsraeumen ist das normal und wird gesagt.
   *  - kein Ziel-Arbeitsbereich: eine Buehne traegt (noch) keinen eigenen; hergeleitet
   *    wird er aus ihrer ersten Entscheidung. Findet sich keiner, wird NICHT geraten —
   *    ein Fenster ueber dem falschen Arbeitsbereich ist schlimmer als keines.
   */
  const buehneOeffnen = useCallback(async (b: BuehneAnsicht, art?: { ohneAbloesung?: boolean; automatisch?: boolean }): Promise<boolean> => {
    /*
     * RAFAELS EIGENER SCHRITT (26.10.2026, lib/rafaelNavigation.ts): jede Öffnung zählt als seine,
     * außer sie kommt aus einem Auftrag, dem Wiederherstellen nach dem Neuladen oder dem Vorschub.
     * Im Zweifel zählt sie — dann bietet ein Auftrag nur an, statt zu ersetzen.
     */
    if (!art?.automatisch) rafaelNavigiert();
    /*
     * ABGELOEST MIT NACHFOLGERIN: die Nachfolgerin oeffnen (`folgeAbloesung`). Nicht beim
     * bewussten Gang zur Vorgaengerin (Kettenzeile, Historie: `ohneAbloesung`) — sonst
     * sprange „vorher" sofort wieder nach vorn.
     */
    if (!art?.ohneAbloesung && b.abschluss?.art === 'abgeloest' && benachbarteBuehnen(b).vor.length > 0) {
      const wahl = handWahl.current;
      setHausHinweis(`„${b.titel}" wurde abgelöst — die Nachfolgerin wird geöffnet …`);
      const f = await folgeAbloesung(b, leseBuehne);
      if (wahl !== handWahl.current) return false;
      if ('fehler' in f) {
        usage('stage_open_failed', { stage_id: b.id, reason: 'successor_unavailable' });
        setHausHinweis(f.fehler);
        return false;
      }
      setHausHinweis(null);
      usage('stage_successor_followed', { stage_id: f.ziel.id, previous_stage_id: b.id, count: f.kette.length });
      return buehneOeffnen(f.ziel, { ...art, ohneAbloesung: true });
    }
    const usageStart = performance.now();
    usage('stage_open_requested', { stage_id: b.id, stage_house: b.haus, previous_stage_id: offeneBuehneRef.current?.id });
    const oeffnung = ++handWahl.current;
    // Jede echte Öffnung beendet das Warten — auch wenn der Assistent DIESELBE Bühne wieder
    // vorlegt: danach ist eine neue Eingabe dort eine neue, mit eigenem Weckruf. Scheitert die
    // Öffnung, kommt das Warten mit der vorigen Bühne zurück (Prüfurteil ASSIOEFFNEN3R P3-R2-2):
    // sonst stünde die beantwortete Bühne ohne Satz da, und die 60-s-Frist wäre still weg.
    const wartenVorher = wartetAufNaechsteRef.current;
    setWartetAufNaechste(null);
  /*
   * LINKS UND RECHTS GEHOEREN ZUSAMMEN - ODER KEINS VON BEIDEN.
   *
   * Rafael, 20.09.2026, mit Bild: *"da steht links etwas von kurt und die buehne
   * spricht von markus - das gibts ja nicht."* Gemessen: links stand
   * `b-erp-umstellung-versteht-kurt-die-szenari-20260920` (Haus PARTNER,
   * Arbeitsbereich `erp-migration`), rechts die Seite von
   * `b-diese-woche-plasser-mo-raiffeisen-do-fr--20260920` (Haus DEV, `koordination`).
   * Zwei Haeuser, zwei Arbeitsbereiche, ein Bildschirm.
   *
   * Die Ursache steht in dieser Funktion: die LINKE Seite wird gleich hier gesetzt
   * (`setOffeneBuehne(b)`, damit der Berater erreichbar ist, waehrend seine
   * Arbeitsflaeche noch entsteht), die RECHTE erst nach `herrichten()` - und dazwischen
   * liegen ein `await` und SIEBEN Stellen, an denen die Funktion aufgibt: kein
   * Archivfoto, kein Arbeitsbereich zum Foto, kein Ziel-Arbeitsbereich, ein Rezept mit
   * unbekannten Panels, ein zwischenzeitlich anderer Klick. KEINE davon nahm die linke
   * Seite zurueck. Sie blieb auf der neuen Buehne stehen, waehrend rechts die alte
   * weiterlief - und das sah aus wie ein Zusammenhang, den es nicht gab.
   *
   * `zurueck()` stellt den vorigen Stand wieder her, aber NUR wenn dieser Klick noch
   * der juengste ist: hat Rafael inzwischen etwas anderes angeklickt, gehoert ihm die
   * Ansicht, nicht diesem abgebrochenen Versuch.
   */
  const vorherige = offeneBuehneRef.current;
  const vorherigesLayout = uebergebenesLayout.current;
  const zurueck = (): false => {
    usage('stage_open_failed', { stage_id: b.id, reason: oeffnung === handWahl.current ? 'unavailable' : 'superseded', elapsed_ms: Math.round(performance.now() - usageStart) });
    if (oeffnung === handWahl.current) {
      setOffeneBuehne(vorherige); setWartetAufNaechste(wartenVorher);
      setzeBuehnenLayout(vorherigesLayout?.doc ?? null, vorherigesLayout?.projectId ?? null,
        vorherigesLayout?.bezug, vorherigesLayout?.rezeptStand);
    }
    return false;
  };
    setHausHinweis(null);
    setArbeitsbereichHinweis(null);
    // Der Berater ist auch erreichbar, solange seine Arbeitsflaeche noch entsteht.
    setOffeneBuehne(b);
    setzeBuehnenLayout(null, null);
    /*
     * EIN ARCHIVBILD IST EINE EIGENE RECHTE KACHEL. Das alte Rezept wird nie wieder
     * montiert, weil es vergangene Chats und externe Ansichten beleben würde — eine
     * abgeschlossene Buehne zeigt nur noch ihren gespeicherten letzten Stand.
     */
    if (b.schnellbuehne && b.rezept && b.workspace) {
      const ok = await herrichten(b.workspace, b.rezept, undefined, { id: b.id, textblock: b.textblock, haus: b.haus, schnellbuehne: b.schnellbuehne }, b.haus);
      if (!ok || oeffnung !== handWahl.current) return zurueck();
      onOeffnenWorkspace(b.workspace); setFehler(null); return true;
    }
    if (b.abschluss) {
      const bildUrl = archivBildUrl(b);
      if (!bildUrl) {
        /* Sichtbar, nicht nur unter „Details": hier endet der Klick sonst ohne erkennbare Wirkung. */
        const satz = hinweisOhneFotoUndNachfolger(b);
        setFehler(satz);
        const r = zurueck();
        setHausHinweis(satz);
        return r;
      }
      if (!b.workspace) {
        const satz = `„${b.titel}" hat ein Archivfoto, aber keinen Arbeitsbereich für die rechte Ansicht.`;
        setFehler(satz);
        const r = zurueck();
        setHausHinweis(satz);
        return r;
      }
      const { doc } = baueBuehne([{ art: 'panel', component: 'browser', name: `Archivfoto · ${b.titel}`, inhalt: { url: bildUrl } }], null);
      if (oeffnung !== handWahl.current) return zurueck();
      setzeBuehnenLayout(doc, b.workspace);
      setOffeneBuehne(b);
      setFehler(null);
      if (onOeffnenWorkspace(b.workspace) === false) setArbeitsbereichHinweis(hinweisFehlenderArbeitsbereich(b.workspace));
      usage('stage_ready', { stage_id: b.id, action: 'archive', elapsed_ms: Math.round(performance.now() - usageStart) });
      return true;
    }
    /*
     * HIER STAND EINE ABWEISUNG. Bis zum 11.09. brach der Klick auf eine
     * Warteraum-Buehne hier ab und zeigte nur den Grund. Rafael: *„dass die nicht
     * klickbar sind macht ja wieder keinen Sinn?"* — seither wird JEDE Buehne
     * geoeffnet. Geschuetzt wird nur noch das Entscheiden: `zugang.entscheiden`
     * steuert weiter unten, ob die Entscheidungs-Bloecke ueberhaupt erscheinen.
     */
    /*
     * OHNE REZEPT GIBT ES KEINE BUEHNE — und deshalb auch nichts zu oeffnen.
     *
     * Ich hatte hier am 11.09. kurzzeitig das Gegenteil gebaut: aufgehen lassen und oben
     * hinschreiben, dass das Fenster fehlt. Rafael hat es sofort kassiert: *„nein halt —
     * die Buehne besteht immer aus dem Skript, das das Fenster vorbereitet"*, und
     * *„ich brauche keine Buehne ohne Buehne"*.
     *
     * Er hat recht, und es ist die genauere Begriffsbestimmung: die Buehne IST das
     * Rezept. Titel und Kontext ohne Fenster sind kein halber Berater, sondern eine
     * Notiz — ihm die vorzusetzen und Buehne dazu zu sagen, waere eine Attrappe.
     *
     * Der Fehler liegt eine Stelle frueher: dass eine unfertige Buehne ueberhaupt wie
     * eine anklickbare aussieht. Das sagt jetzt die Kachel (siehe GuideHof), und die
     * Wache meldet dem Assistenten jede Buehne ohne Rezept.
     */
    if (!b.rezept) {
      /* Eine Auskunft, kein Fehler (Befund Vize Partner 31.10.2026): der rote Balken „Vorgang fehlgeschlagen" las
       * sich wie eine Stoerung, dabei ist die Buehne nur noch nicht fertig eingerichtet. */
      setHausHinweis(`„${b.titel}" ist noch im Bau: der Berater richtet die Fenster noch ein. `
        + 'Sobald sie stehen, öffnet sich die Bühne hier.');
      return zurueck();
    }
    /*
     * DAS ZIEL GEHOERT DER BUEHNE — und seit 11.09. gibt es dazu keinen Rueckfall
     * mehr. Der erste Entwurf holte es aus der ersten Entscheidung
     * (`karte.ziel_workspace`) und lief beim ersten echten Klick ins Leere, weil eine
     * frische Buehne gar keine traegt; W1 hat das Feld deshalb nachgetragen und die
     * Karte als Rueckfall stehen lassen. Der Rueckfall ist jetzt gegenstandslos: die
     * Entscheidungen gehoeren der Buehne, es gibt keine fremde Karte mehr zu fragen.
     * Eine Eigenschaft bei etwas zu borgen, das man nur TRAEGT, war ohnehin die
     * Ursache dafuer, dass der Hof nicht stand.
     */
    const zielProjekt: string | null = b.workspace ?? null;
    if (!zielProjekt) {
      setFehler(`„${b.titel}" nennt keinen Arbeitsbereich. Ich rate keinen — ein Fenster über dem `
        + 'falschen Arbeitsbereich ist schlimmer als keines. In den fertigen Räumen weist der '
        + 'Server das inzwischen ab; diese Bühne steht noch in Arbeit.');
      return zurueck();
    }
    /*
     * MIT SEINEM BERATER-CHAT (Rafael, 13.09. 10:27Z — siehe `rezeptMitBeraterchat`).
     * Der Grund, warum keiner dabei ist, wird GEZEIGT und nicht verschwiegen: eine
     * Buehne, die rechts nur ihre Seite hat, sieht sonst aus wie eine, deren Berater
     * einfach nicht laedt.
     */
    const ok = await herrichten(zielProjekt, b.rezept, undefined, { id: b.id, textblock: b.textblock, haus: b.haus, schnellbuehne: b.schnellbuehne }, b.haus);
    if (oeffnung !== handWahl.current) return zurueck();
    if (!ok) return zurueck();                       // herrichten() hat den Grund bereits gesetzt
    /*
     * KEIN NACHLADEN MEHR. Frueher wurde je Kennung eine Karte geholt, und eine
     * fehlende musste gemeldet werden ("nennt Entscheidungen, die es nicht mehr
     * gibt") — ein Loch, das es nur gab, weil die Buehne auf fremde Objekte zeigte.
     * Sie traegt ihre Entscheidungen jetzt selbst; was hier steht, ist da.
     */
    setOffeneBuehne(b);
    setFehler(null);
    /*
     * FEHLT DER ARBEITSBEREICH IM HAUS, BLEIBT DIE SEITE TROTZDEM — LAUT.
     * Partner-Bühnen aus `energy-entwicklung` gibt es auf Dev nicht als Projekt. Die
     * Bühnenschicht montiert dann der aktive Arbeitsbereich (App.tsx, `buehnenWirt`),
     * und hier steht der Grund, statt dass Rafael still Uploads/Tool Hub sieht.
     */
    if (onOeffnenWorkspace(zielProjekt) === false) setArbeitsbereichHinweis(hinweisFehlenderArbeitsbereich(zielProjekt));
    usage('stage_ready', { stage_id: b.id, elapsed_ms: Math.round(performance.now() - usageStart) });
    return true;
  }, [herrichten, onOeffnenWorkspace, setzeBuehnenLayout]);

  /*
   * EINE BUEHNE UEBER IHRE KENNUNG OEFFNEN — der Weg des angenommenen Angebots.
   *
   * Der Hof-Klick hat das Objekt schon in der Hand; hier ist nur die Kennung da,
   * weil der Auftrag von aussen kam und die Buehne beim Ablegen des Angebots
   * nicht mitgereicht wird (sie koennte sich bis zum Klick geaendert haben —
   * jemand haette sie abgeschlossen oder ins andere Haus uebergeben). Deshalb
   * wird hier frisch geholt und nicht zwischengespeichert.
   */
  const buehneOeffnenNachKennung = useCallback(async (id: string) => {
    setHausHinweis('Bühne wird geöffnet …');
    try {
      const b = await leseBuehne(id);
      setHausHinweis(null);
      await buehneOeffnen(b);
    } catch (e) {
      setHausHinweis(null);
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [buehneOeffnen]);

  const historieSchliessen = useCallback(() => {
    setHistorieOffen(false);
    setHistorieStart(null);
  }, []);

  /** Der Klick im Hof: eine abgeschlossene Buehne oeffnet die Historie statt eine
   *  alte Sitzung wiederzubeleben. */
  /**
   * Das Merkmal geht als einfacher Aufruf raus, NICHT ueber die Sendewarteschlange —
   * anders als jede echte Eingabe. Begruendung: geht es verloren, sieht Rafael eine
   * Buehne im Durchgang ein zweites Mal. Das ist die harmlose Richtung des Irrtums.
   * Die andere waere, eine Buehne faelschlich als gesehen zu markieren; dann fehlte sie
   * ihm stumm. Ein Auftrag, der nach einem Neustart nachfeuert, koennte genau das —
   * deshalb bewusst fluechtig.
   */
  const vorgelegtMerken = useCallback((buehne: BuehneAnsicht) => {
    /*
     * NUR RAFAEL LEGT SICH ETWAS VOR — Rafael, 22.09.2026: „Weglassen, was ihm nicht
     * gehoert." Dieses Merkmal heisst woertlich „Rafael hat die Buehne vor sich
     * gehabt" und nimmt sie ihm aus dem Durchgang. Der Server weist es fuer jeden
     * anderen zu Recht ab (403, gemessen an Herberts eigener Buehne) — der Client
     * hat es trotzdem bei jedem Oeffnen gerufen. Eine Anfrage, von der man weiss,
     * dass sie abgelehnt wird, gehoert nicht gestellt.
     */
    if (!istBetreiber || buehne.schnellbuehne) return;
    if (buehne.abschluss || buehne.rafael_gesehen) return;
    setSchonVorgelegt(alt => alt.has(buehne.id) ? alt : new Set([...alt, buehne.id]));
    void fetch(`/api/guide/buehnen/${encodeURIComponent(buehne.id)}/vorgelegt`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .catch(() => { /* siehe oben: ein verlorener Merker zeigt sie noch einmal, mehr nicht */ });
  }, [istBetreiber]);

  const hofBuehneOeffnen = useCallback((buehne: BuehneAnsicht) => {
    if (buehne.abschluss) {
      setHistorieStart(buehne.id);
      setHistorieOffen(true);
      return;
    }
    setHistorieOffen(false);
    setHistorieStart(null);
    /* Markiert wird beim OEFFNEN, nicht beim Beantworten: Rafaels Satz ist "sobald ich
     * die Bühne einmal gesehen habe" — und gerade die, die er bewusst NICHT beantwortet
     * ("die ein bisschen schwieriger sind"), sollen aus dem Durchgang fallen. */
    vorgelegtMerken(buehne);
    void buehneOeffnen(buehne);
  }, [buehneOeffnen, vorgelegtMerken]);

  /**
   * ENTLANG DER ABLOESUNGSKETTE WECHSELN — der Vor/Zurueck-Knopf (Rafael, 15.09.2026).
   *
   * Die Ziel-Buehne muss frisch geholt werden: `benachbarteBuehnen` liefert nur
   * Kennungen, und der Nachbar steht selten in `buehnenVorrat` (Vorgaenger sind meist
   * abgeschlossen, `Hofansicht` laedt standardmaessig nur Lebendige). Derselbe Weg wie
   * beim Wiederherstellen eines Fensterauftrags (`GET /guide/buehnen/:id` weiter oben).
   */
  /** `true` nur, wenn die Bühne danach offen ist (gebraucht vom Zurückholen nach dem Neuladen). */
  const buehneWechseln = useCallback(async (id: string, art?: { ohneAbloesung?: boolean }): Promise<boolean> => {
    const wahl = handWahl.current;
    setFehler(null);
    try {
      const b = await leseBuehne(id);
      if (wahl !== handWahl.current) return false;
      return await buehneOeffnen(b, art);
    } catch (err) {
      if (wahl === handWahl.current) {
        setFehler(`Bühne „${id}" ließ sich nicht laden: ${err instanceof Error ? err.message : String(err)}`);
      }
      return false;
    }
  }, [buehneOeffnen]);
  /* Deep-Link: genau einmal beim Aufbau oeffnen (siehe `startBuehne`). */
  const startBuehneGeoeffnet = useRef(false);
  useEffect(() => {
    const zurueck = () => {
      const s = offeneBuehneRef.current?.schnellbuehne;
      if (s) void buehneWechseln(s.nachBuehneId);
    };
    window.addEventListener('cui-schnellbuehne-zurueck', zurueck);
    return () => window.removeEventListener('cui-schnellbuehne-zurueck', zurueck);
  }, [buehneWechseln]);
  useEffect(() => {
    if (!startBuehne || startBuehneGeoeffnet.current) return;
    startBuehneGeoeffnet.current = true;
    void buehneWechseln(startBuehne);
  }, [startBuehne, buehneWechseln]);
  /*
   * NACH DEM NEULADEN STEHT DIESELBE BUEHNE WIEDER DA (Rafael, 14.10.2026 06:41Z) —
   * lib/fensterBuehne.ts. Nicht im Schatten-Client (der zeigt, was man ihm aufträgt) und nicht
   * bei einem Deep-Link (der sagt selbst, welche Bühne). Erst nach dem Zurückholen wird gemeldet.
   */
  /*
   * Zurueckholen nach dem Neuladen: eine inzwischen abgeschlossene Buehne wird still
   * uebersprungen und vergessen (Pruefurteil ASSIOEFFNEN2R P3-1) — sonst stuende nach dem
   * Neuladen ein Archivfoto, das niemand geoeffnet hat. `false` = nicht offen = vergessen.
   */
  const buehneWiederherstellen = useCallback(async (id: string): Promise<boolean> => {
    const wahl = handWahl.current;
    try {
      const b = await leseBuehne(id);
      if (b.abschluss || wahl !== handWahl.current) return false;
      return await buehneOeffnen(b, { automatisch: true });
    } catch (err) {
      if (err instanceof BuehnenLeseFehler && err.status === 404) return false;
      if (wahl === handWahl.current) setFehler(`Die zuletzt offene Bühne „${id}" ließ sich nicht laden: ${err instanceof Error ? err.message : String(err)}`);
      return false;
    }
  }, [buehneOeffnen]);
  useFensterBuehne({
    offeneId: offeneBuehne?.id ?? null,
    oeffnen: buehneWiederherstellen,
    aus: !!startBuehne || isShadowClient(),
    onFehler: setFehler,
  });

  /*
   * WECKRUF AN DEN ASSISTENTEN (Kanzler Dev, 13.10.2026). Im Assistent-Modus bleibt die Flaeche
   * nach Rafaels Antwort leer, bis der Assistent die naechste Buehne oeffnet; er bekommt dafuer
   * ein kurzes Ereignis (server/lib/assistent-weckruf.ts). Geweckt wird nur, wo die Flaeche
   * tatsaechlich geleert wurde, und erst, wenn der Server die Eingabe gespeichert hat. Eine
   * Zusatzmeldung: Rafaels Wort geht ueber die Sendeschlange an den Berater, unabhaengig davon.
   */
  const wartetAufWeckruf = useRef(new Set<string>());
  const assistentWecken = useCallback(async (buehneId: string, ereignis: { anlass: 'antwort' | 'erledigt' | 'fragen_erledigt'; auftragId?: string }) => {
    // fallback-ok: setFehler zeigt den Fehlschlag sichtbar als role=alert ("Guide: Vorgang fehlgeschlagen")
    try {
      // auftragId macht jedes Ereignis einzeln: „Erledigt" nach einer Antwort an derselben Buehne
      // ist ein neues Ereignis, kein Doppel (Pruefurteil ASSIOEFFNENR P2-4).
      const r = await fetch(`${API}/guide/coach/assistent/wecken`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ buehneId, ...ereignis }), signal: AbortSignal.timeout(35000),
      });
      const d = await r.json().catch(() => null) as { error?: string } | null;
      usage('assistant_wake', { stage_id: buehneId, status: r.status });
      if (!r.ok) setFehler(`Antwort gespeichert, aber der Assistent wurde nicht geweckt: ${d?.error ?? `HTTP ${r.status}`}. Schreib ihm kurz „weiter“.`);
    } catch (e) {
      setFehler(`Antwort gespeichert, aber der Assistent wurde nicht geweckt: ${e instanceof Error ? e.message : String(e)}. Schreib ihm kurz „weiter“.`);
    }
  }, []);
  useEffect(() => sendeQueue?.onGespeichert(job => {
    if (!wartetAufWeckruf.current.delete(job.stage.id) || !assistentModusRef.current) return;
    // Erst jetzt ist die Antwort am Server: ab hier zaehlt die Frist des Assistenten.
    setWartetAufNaechste(w => (w?.buehneId === job.stage.id && !w.gespeichert ? { ...w, gespeichert: Date.now() } : w));
    // Den beantworteten bzw. abgeschlossenen Stand sofort holen, nicht erst mit dem Live-Kanal.
    offeneAktualisierung.current();
    void assistentWecken(job.stage.id, { anlass: job.close ? 'erledigt' : 'antwort', auftragId: job.id });
  }), [sendeQueue, assistentWecken]);

  /** `art`: was Rafael abgegeben hat — eine Antwort auf Fragen/„Erledigt" oder ein freies Wort an den Berater. */
  const sofortWeiter = useCallback((id: string, art: 'antwort' | 'nachricht' = 'antwort') => {
    usage('advance_after_input', { stage_id: id });
    abgegeben.current.add(id);
    handWahl.current += 1;
    const naechste = buehnenVorrat.current.find(b => !abgegeben.current.has(b.id)
      && !sendeQueue?.jobs.some(j => j.stage.id === b.id)
      && !b.abschluss && (b.raum === 'warteraum' || b.raum === 'vor_dem_koenig' || b.raum === 'vorraum') && fragenAnDich(b) > 0 && b.zugang.entscheiden);
    /*
     * EIN STEUERER, NICHT ZWEI (Kanzler Dev auf Rafaels Wort, 13.10.2026). Im
     * Assistent-Modus waehlt der Vorschub keine eigene Buehne: gemessen am 13.10. oeffnete
     * er nach jeder Antwort die erste beantwortbare aus dem Vorrat, und der naechste
     * Auftrag des Assistenten traf darauf mit 409. Die Antwort ist gespeichert; was als
     * Naechstes vor Rafael kommt, zeigt der Assistent (POST /control/guide/oeffne-buehne).
     */
    if (assistentModusRef.current) {
      /*
       * Wartet diese Buehne schon auf die naechste, ist das hier ein zweiter Klick auf dieselbe
       * Bühne (Pruefurteil ASSIOEFFNEN3R P3-1): kein zweiter Weckruf, die Uhrzeit im Satz bleibt.
       */
      if (wartetAufNaechsteRef.current?.buehneId === id) return;
      // Geweckt wird erst, wenn der Server die Eingabe bestaetigt (onGespeichert oben).
      // Die Buehne bleibt stehen (siehe `wartetAufNaechste`) — keine leere Flaeche mehr.
      wartetAufWeckruf.current.add(id);
      setWartetAufNaechste({ buehneId: id, seit: Date.now(), gespeichert: null, art });
      return;
    }
    // Clear the submitted view before preparing the next stage over the network.
    handWahl.current += 1; offeneBuehneRef.current = null; setOffeneBuehne(null); setzeBuehnenLayout(null, null);
    if (naechste) { void buehneOeffnen(naechste, { automatisch: true }); return; }
    setHausHinweis('Eingabe lokal gesichert. Du kannst direkt weiterarbeiten.');
  }, [buehneOeffnen, setzeBuehnenLayout, sendeQueue]);

  // A human chat send is acknowledged by the owning stage store before the
  // adviser resumes. It must advance the visible stage just like a Guide answer.
  // Match the currently open stage: an unrelated chat or a manual navigation wins.
  useLiveChannel({
    label: 'Guide-Arbeitsbeginn',
    onResync: () => { if (offeneBuehne) void buehneNachladen(offeneBuehne.id, offeneBuehne.raum); },
    onMessage: (msg: any) => {
      if (offeneBuehne && msg?.type === 'guide-updated' && msg.anlass === 'menschliche-eingabe'
        && msg.raum === 'sitzungssaal' && msg.buehneId === offeneBuehne?.id
        && (offeneBuehne.raum === 'warteraum' || offeneBuehne.raum === 'vor_dem_koenig' || offeneBuehne.raum === 'vorraum')) {
        /*
         * HIER STAND `sofortWeiter(offeneBuehne.id)` — DER ZWEITE GRUND, WARUM IHM
         * BUEHNEN WEGSPRANGEN (Rafael, 20.09.2026).
         *
         * Die Absicht war richtig: schickt er einen Satz in den Chat der Buehne,
         * wandert sie in den Sitzungssaal, und danach soll es weitergehen wie nach
         * einer Antwort. Die Ausfuehrung war es nicht — `sofortWeiter` SCHLIESST die
         * offene Buehne und oeffnet die naechste. Wer waehrend des Lesens dem Berater
         * etwas schreibt, verliert damit genau das, was er gerade gelesen hat.
         *
         * Jetzt wird nur nachgeladen: der Raumwechsel ist sofort sichtbar, die Buehne
         * bleibt stehen. Weitergehen ist seine Sache — dafuer stehen seit heute die
         * Knoepfe "Bühne vor" und "Bühne zurück" im Kopf der offenen Buehne.
         */
        void buehneNachladen(offeneBuehne.id, offeneBuehne.raum);
      }
    },
  });

  const oeffnungsRef = useRef(buehneOeffnen);
  oeffnungsRef.current = buehneOeffnen;
  const letzterOeffnungsAuftrag = useRef<string | null>(null);
  useEffect(() => {
    const job = oeffnungsAuftrag;
    if (!job || letzterOeffnungsAuftrag.current === job.auftragId) return;
    letzterOeffnungsAuftrag.current = job.auftragId;
    /*
     * EINE OFFENE BUEHNE WIRD NICHT UEBERNOMMEN — SIE WIRD ANGEBOTEN.
     *
     * Rafael, 20.09.2026: *"mir springen immer noch die buehnen weg waehrend ich
     * drauf bin - so nervig."* Dieser Weg war einer der beiden Gruende. Jeder
     * Berater mit Admin-Recht kann POST /control/guide/oeffne-buehne rufen, und der
     * Guide gehorchte: er warf weg, was Rafael gerade las, und baute die fremde
     * Buehne auf.
     *
     * Die Sperre darunter (`handWahl.current !== wahl`) hat das NICHT verhindert.
     * Sie greift nur, wenn er waehrend des Netzabrufs selbst navigiert — wer bloss
     * LIEST, aendert `handWahl` nicht, und der Auftrag gewann. Genau deshalb traf es
     * ihn immer im ungeeignetsten Moment.
     *
     * Die Regel steht seit heute in refs/gemeinsam/hofbetrieb.md ("Was Rafael offen
     * hat, bleibt offen"): die Liste darf sich umschichten, die Seite vor ihm nicht.
     * Der Auftrag wird deshalb nicht verworfen, sondern hingelegt — mit Titel, damit
     * er sieht, was ihn erwartet, und mit einem Knopf, der es oeffnet, wenn ER so
     * weit ist. Der Auftraggeber bekommt eine ehrliche Absage, kein stilles "ok":
     * seine Buehne steht Rafael vor, sie wurde nur nicht aufgezwungen.
     *
     * NICHTS OFFEN, ODER DERSELBE OEFFNUNGSWUNSCH? Dann faehrt der Auftrag wie
     * bisher durch: dort nimmt er nichts weg.
     */
    /*
     * IM ASSISTENT-MODUS STEUERT DER ASSISTENT (Rafael, 13.10.2026: *„der assistent muss
     * das gui selber steuern und mir das vorlegen was er mir vorlegen will"*). Gemessen am
     * selben Tag: beide 409 „andere Bühne offen" trafen eine Bühne, die nicht Rafael
     * gewählt hatte, sondern der automatische Vorschub nach seiner Antwort (`sofortWeiter`).
     * Der Assistent konnte danach nie mehr zeigen, worüber er sprach. In diesem Modus
     * gewinnt sein Auftrag deshalb; nichts geht verloren, Entwürfe hängen je Bühne
     * (`useEntwurf`). Ausserhalb des Modus bleibt das Angebot unten unverändert.
     */
    const quittiere = (ergebnis: { ok: boolean; error?: string; grund?: string; offen?: string; rechts?: 'bildschirm1' | 'bildschirm2' }) => fetch(`${API}/control/guide/oeffnung-ergebnis`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      // handNav: Rafaels Navigationsstand bei der Quittung — Basis der Sichtprüfung (`rafael_navigiert`).
      body: JSON.stringify({ clientId: job.clientId, buehneId: job.buehneId, auftragId: job.auftragId, handNav: rafaelNavStand().n, ...ergebnis }),
      signal: AbortSignal.timeout(5000),
    });
    const schonOffen = offeneBuehneRef.current;
    /*
     * RAFAELS SPAETERER SCHRITT GEWINNT, AUCH IM ASSISTENT-MODUS (26.10.2026, Befund
     * local-storage/cui-buehnenwechsel-20261026/ERGEBNIS.md). Gemessen 08:37: Rafael öffnete
     * Ruckelshausen bewusst, 15 s später ersetzte ein Auftrag des Assistenten sie durch Mühl.
     * Hat er seit Auftragserteilung selbst navigiert, ist der Auftrag überholt; hat er binnen der
     * Schonfrist selbst eine andere Bühne gewählt, wird der Auftrag angeboten statt ersetzt.
     */
    const handNavAmAuftrag = job.handNav ?? rafaelNavStand().n;
    const gegenRafael = auftragGegenRafael(handNavAmAuftrag, schonOffen?.id ?? null, job.buehneId);
    // Ein reines Angebot nimmt nichts weg — es wird auch nach Rafaels Navigation hingelegt.
    if (gegenRafael === 'verwerfen' && !job.nurAngebot) {
      void quittiere({ ok: false, grund: 'rafael_navigiert', ...(schonOffen ? { offen: schonOffen.id } : {}),
        error: 'Rafael hat seit diesem Auftrag selbst navigiert. Die Ansicht wurde NICHT gewechselt — nicht erneut öffnen; bei Bedarf als Angebot im Gespräch nennen.' })
        .catch(e => console.error('[guide-oeffnen-rafael-navigiert]', e));
      return;
    }
    // nurAngebot: der Server hat einen wiederholten Auftrag nach Rafaels Wegnavigieren abgelehnt (409) und legt die Bühne nur noch hin.
    const alsAngebot = !!job.nurAngebot || gegenRafael === 'anbieten'
      || (!!schonOffen && schonOffen.id !== job.buehneId && !job.buehneId.startsWith('sb-') && !assistentModusRef.current);
    if (alsAngebot && (schonOffen || job.nurAngebot)) {
      void (async () => {
        let titel = job.buehneId;
        try {
          titel = (await leseBuehne(job.buehneId)).titel || titel;
        } catch (e) {
          // Der Titel ist Beiwerk. Das Angebot steht auch ohne ihn — aber still
          // verschwinden darf der Grund nicht, sonst sucht man ihn spaeter im Nichts.
          console.warn('[guide-angebot] Titel der angebotenen Bühne nicht lesbar', e);
        }
        setAngebot({ buehneId: job.buehneId, titel });
        await quittiere(job.nurAngebot || gegenRafael === 'anbieten' || !schonOffen ? {
          ok: false, grund: 'rafael_navigiert', ...(schonOffen ? { offen: schonOffen.id } : {}),
          error: `Rafael hat eben selbst ${schonOffen ? `eine andere Bühne gewählt (${schonOffen.id})` : 'navigiert'}. Die Ansicht wurde NICHT gewechselt — `
            + 'deine Bühne liegt ihm als Angebot vor und geht auf, wenn er sie anklickt. Nicht erneut öffnen.',
        } : {
          ok: false, grund: 'andere_buehne_offen', offen: schonOffen.id,
          error: `Rafael hat gerade eine andere Bühne offen (${schonOffen.id}). Die Ansicht wurde NICHT gewechselt — `
            + 'deine Bühne liegt ihm als Angebot vor und geht auf, wenn er sie anklickt.',
        }).catch(e => console.error('[guide-oeffnen-angebot]', e));
      })();
      return;
    }
    const wahl = handWahl.current;
    /*
     * SOFORT SICHTBAR, BEVOR DAS NETZ ANTWORTET (Rafael, 17.09.2026: sein Klick
     * darf nicht warten). Dieser Weg oeffnet ueber einen Server-Auftrag statt per
     * direktem Hof-Klick und holt daher erst per Netzwerk nach — bis zu 10s ohne
     * jedes Zeichen waren bisher von einem toten Klick nicht zu unterscheiden.
     * Derselbe Hinweiskanal wie bei `sofortWeiter`, keine neue Flaeche.
     */
    setHausHinweis('Bühne wird geöffnet …');
    void (async () => {
      let error: string | undefined;
      let grund: string | undefined;
      try {
        const b = await leseBuehne(job.buehneId);
        if (rafaelNavStand().n !== handNavAmAuftrag) { grund = 'rafael_navigiert'; throw new Error('Rafael hat inzwischen selbst navigiert; seine Ansicht bleibt. Nicht erneut öffnen.'); }
        if (Date.now() >= job.expiresAt || handWahl.current !== wahl) { grund = 'ueberholt'; throw new Error('Öffnungsauftrag überholt; deine Navigation bleibt erhalten.'); }
        if (b.schnellbuehne && schonOffen && schonOffen.id !== b.id && schonOffen.id !== b.schnellbuehne.nachBuehneId) { grund = 'andere_buehne_offen'; throw new Error('Schnellbühne gespeichert. Du bist inzwischen auf einer anderen Bühne; öffne sie bei Bedarf aus dem Zwischenfrage-Chat.'); }
        if (b.id !== job.buehneId || !b.rezept || !b.workspace) { grund = 'unvollstaendig'; throw new Error('Bühne hat kein vollständiges Fensterrezept oder keinen Arbeitsbereich.'); }
        // Erst jetzt, angenommen und vollständig: räumen. Eine Ablehnung davor lässt Mission
        // Control stehen; ungesicherter Text darin hält die Öffnung an (ASSIOEFFNENR P2-b).
        const nichtRaeumbar = abdeckungRaeumenRef.current?.() ?? null;
        if (nichtRaeumbar) { grund = 'entwurf_ungesichert'; throw new Error(nichtRaeumbar); }
        if (!await oeffnungsRef.current(b, { automatisch: true })) { grund = 'nicht_hergerichtet'; throw new Error('Das Fenster konnte nicht hergerichtet werden.'); }
        /*
         * OK NUR MIT ZEICHNUNGSBELEG (ASSIOEFFNEN, 13.10.2026). Bis hierher hiess ok:
         * „buehneOeffnen() lieferte true" — Zustand, nicht Bild. Jetzt wird nachgesehen,
         * ob links diese Bühne und rechts eine Bühnenschicht wirklich stehen. Der
         * Arbeitsbereichswechsel rendert etwas später, deshalb je Frame nachsehen. 5 s, weil
         * ein gekoppelter Bildschirm 2 erst an SEINEM DOM belegt und dann quittiert
         * (useZweiterBildschirm, Quittungsfrist 2,5 s plus Takt); bleibt er aus, holt
         * Bildschirm 1 die Bühne zurück, und der Beleg hier sieht sie dann bei sich.
         */
        const fehlt = await warteBisBelegt(() => buehneNichtGezeichnet(document, job.buehneId), 5000);
        if (fehlt) { grund = 'nicht_gerendert'; throw new Error(`Bühne geöffnet, aber nicht gezeichnet: ${fehlt}`); }
      } catch (e) {
        error = e instanceof Error ? e.message : String(e);
        // Seine eigene Navigation ist kein Fehler, den er sehen muss — die Absage geht nur an den Auftraggeber.
        if (grund !== 'rafael_navigiert') setFehler(error);
        setHausHinweis(null);
      }
      const ack = await quittiere(error ? { ok: false, error, grund: grund ?? 'fehler' }
        : { ok: true, rechts: rechtsAufBildschirm2(document, job.buehneId) ? 'bildschirm2' : 'bildschirm1' });
      if (!ack.ok) throw new Error(`Öffnungsbeleg nicht angenommen (HTTP ${ack.status}).`);
    })().catch(e => { console.error('[guide-oeffnen]', e); setFehler(String(e)); });
  }, [oeffnungsAuftrag]);

  /** Beide Hintergrundleser aktualisieren dieselbe offene Bühne samt ihrem übergebenen Rezept. */
  const aktualisiereOffeneBuehne = useCallback((frisch: BuehneAnsicht, wahl: number, abruf: GuideLeseAbruf) => {
    const vorher = offeneBuehneRef.current;
    if (wahl !== handWahl.current || vorher?.id !== frisch.id) return;
    try {
    const wartetAufAssistent = frisch.abschluss && frisch.haus === vorher.haus && assistentModusRef.current
      && wartetAufNaechsteRef.current?.buehneId === frisch.id;
    /* Nur ein NEU eingetretener Abschluss schliesst die Ansicht. Wer eine schon abgeschlossene Buehne bewusst
     * oeffnet (Kettenzeile, Historie, „beantwortet auf“), bleibt dort (Zweitpruefung R1 A1, 31.10.2026). */
    const neuAbgeschlossen = !!frisch.abschluss && !vorher.abschluss;
    if (!wartetAufAssistent && (frisch.haus !== vorher.haus || neuAbgeschlossen)) {
      bestaetigeLesen(abruf);
      handWahl.current += 1; offeneBuehneRef.current = null; setOffeneBuehne(null);
      setzeBuehnenLayout(null, null);
      if (frisch.abschluss?.art === 'abgeloest' && frisch.nachfolger) {
        setHausHinweis(null);
        setWeiterAuf({ von: frisch.id, nachfolger: frisch.nachfolger, wort: angenommenesWortDerBuehne(frisch) });
        return;
      }
      setHausHinweis(frisch.abschluss ? 'Diese Bühne ist inzwischen abgeschlossen.' : `Diese Bühne wurde an ${frisch.haus === 'dev' ? 'Dev' : 'Partner'} übergeben.`);
      return;
    }
    if (!frisch.abschluss && frisch.rezept && frisch.workspace) {
      const bezug = { id: frisch.id, textblock: frisch.textblock, haus: frisch.haus, schnellbuehne: frisch.schnellbuehne };
      const stand = rezeptStand(frisch.workspace, frisch.rezept, bezug, frisch.haus);
      // Ein schnelles Nachladen darf eine Rezeptänderung nicht für den periodischen Leser verbrauchen.
      if (uebergebenesLayout.current?.rezeptStand !== stand) {
        try {
          if (!herrichten(frisch.workspace, frisch.rezept, undefined, bezug, frisch.haus))
            throw new StageRefreshFehler('ansicht', 'Neuer Stand empfangen, aber die geänderte Ansicht konnte nicht aufgebaut werden.');
        } catch { throw new StageRefreshFehler('ansicht', 'Neuer Stand empfangen, aber die geänderte Ansicht konnte nicht aufgebaut werden.'); }
      }
    }
    setOffeneBuehne(aktuell => wahl === handWahl.current && aktuell?.id === frisch.id ? frisch : aktuell);
    setBuehnenAktualisierungsproblem(null);
    bestaetigeLesen(abruf);
    } catch (error) { meldeLesefehler(abruf, stageRefreshHinweis(error)); throw error; }
  }, [herrichten, setzeBuehnenLayout, bestaetigeLesen, meldeLesefehler]);

  // Nur Leser derselben Bühne teilen einen Abruf. Eine langsame alte Bühne darf
  // die gerade geöffnete nicht bis zum eigenen Timeout blockieren.
  const nachladeAbruf = useRef(new Map<string, Promise<{ frisch: BuehneAnsicht; abruf: GuideLeseAbruf }>>());
  const leseNachladung = useCallback((id: string): Promise<{ frisch: BuehneAnsicht; abruf: GuideLeseAbruf }> => {
    const aktiv = nachladeAbruf.current.get(id);
    if (aktiv) return aktiv;
    const abruf = beginneLesen('buehne');
    const wahl = handWahl.current;
    const promise = leseBuehne(id).then(frisch => ({ frisch, abruf })).catch(error => {
      if (wahl === handWahl.current && offeneBuehneRef.current?.id === id)
        meldeLesefehler(abruf, stageRefreshHinweis(error));
      throw error;
    }).finally(() => { nachladeAbruf.current.delete(id); });
    nachladeAbruf.current.set(id, promise);
    return promise;
  }, [beginneLesen, meldeLesefehler]);

  const aktualisierungAnfordern = useStageRefresh({
    id: offeneBuehne?.id, haus: offeneBuehne?.haus,
    read: async id => {
      const wahl = handWahl.current;
      return { ...await leseNachladung(id), wahl };
    },
    onData: ({ frisch, wahl, abruf }) => aktualisiereOffeneBuehne(frisch, wahl, abruf),
    onError: meldeAktualisierungsproblem,
  });
  offeneAktualisierung.current = aktualisierungAnfordern;

  // Neue Buehne angeschaut: der Fehlerstand der vorigen sagt ueber sie nichts.
  useEffect(() => { setBuehnenAktualisierungsproblem(null); }, [offeneBuehne?.id]);
  // Eine andere Bühne ist offen: der Weiter-Satz der abgelösten (B2) hat seinen Zweck erfüllt.
  useEffect(() => { if (offeneBuehne?.id) setWeiterAuf(null); }, [offeneBuehne?.id]);

  const buehneNachladen = useCallback(async (id: string, _vorherRaum?: string) => {
    const wahl = handWahl.current;
    const nochOffen = () => wahl === handWahl.current && offeneBuehneRef.current?.id === id;
    if (!nochOffen()) return;
    try {
      const { frisch, abruf } = await leseNachladung(id);
      if (!frisch || !nochOffen()) return;
      // Nachladen liest nur. Vorschub gehört zur eigenen Eingabe (sofortWeiter).
      aktualisiereOffeneBuehne(frisch, wahl, abruf);
    } catch (err) {
      if (nochOffen()) meldeAktualisierungsproblem(err);
    }
  }, [leseNachladung, aktualisiereOffeneBuehne, meldeAktualisierungsproblem]);

  /*
   * EIN CHAT ANSEHEN IST EINE ANSICHT — und darf Rafaels Arbeitsplatz nicht umbauen.
   *
   * Befund vom Koordinator (07.09. 10:56Z) und im Code nachverfolgt: der bisherige
   * Weg `onOeffnenSitzung` reicht einen Aktivierungsplan an den LayoutManager, der
   * den Chat per `model.doAction(addNode)` in Rafaels Layout einhaengt und am Ende
   * `saveLayout(model)` ruft. Der Klick, mit dem er nur nachsehen will, ob eine
   * Sitzung wirklich arbeitet, schreibt damit die Datei um, aus der er morgen frueh
   * seinen Arbeitsplatz laedt — eine Zeit-Unterdrueckung haette den ausdruecklichen
   * Speicheraufruf am Ende gar nicht erwischt.
   *
   * Die Buehne kann genau das schon: ein Dokument im Speicher, das der LayoutManager
   * als Ueberlagerer montiert, ohne etwas zu speichern. Ein Chat ist dafuer nur ein
   * Rezept mit einer einzigen Kachel. Also derselbe Weg wie fuer eine Karte, statt
   * eines zweiten daneben.
   */
  /*
   * HEBEL AUCH HIER — der Zweig, in dem Rafael tatsaechlich landet.
   *
   * Am 07.09. habe ich Anstossen/Anhalten zuerst nur in die ThemaAnsicht gebaut und
   * es fuer fertig gehalten. Beim ersten echten Blick durch den neuen Direktlink
   * (?panel=guide) stand im geoeffneten Thema aber dieser Zweig hier
   * ("... — daran wird nach deinem Auftrag gearbeitet"), und keiner meiner Knoepfe
   * war im DOM. Genau der haeufigste Fall: kein wartender Entscheid. Ohne den Blick
   * waere das als fertig durchgegangen.
   */
  const [hebelSatzOben, setHebelSatzOben] = useState<string | null>(null);
  const hebelOben = useCallback(async (art: 'anhalten' | 'schliessen', sessionId: string, name: string) => {
    if (art === 'schliessen') {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`„${name}" schließen? Der Chat bleibt lesbar, aber die Sitzung ist beendet.`)) return;
    }
    setHebelSatzOben(null);
    try {
      const r = await fetch(`${API}/guide/sitzung/${encodeURIComponent(sessionId)}/${art}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await r.json().catch(() => null);
      setHebelSatzOben(!r.ok
        ? (d?.error ?? `Fehlgeschlagen (${r.status})`)
        : art === 'anhalten'
          ? (d?.prozess_gestoppt ? 'Angehalten — deine nächste Nachricht startet sie wieder.' : 'Es lief kein Prozess; jetzt ist sie stumm gestellt.')
          : 'Geschlossen.');
      if (r.ok) neuladen();
    } catch (err) { setHebelSatzOben(`Fehlgeschlagen: ${err}`); }
  }, [neuladen]);

  const sitzungAlsBuehne = useCallback(async (projectId: string, sessionId: string, accountId: string, name?: string, themaBehalten = false) => {
    const ok = await herrichten(projectId, { kacheln: [{ art: 'sitzung', sessionId, accountId }] } as Layoutrezept);
    if (!ok) return;
    /*
     * DIE MARKIERUNG GEHOERT DAZU — ohne sie sieht Rafael seinen Klick nicht.
     *
     * `sitzungsBuehne` sagt der Flaeche, dass gerade der Chat einer Sitzung als
     * Buehne steht. Fehlt sie, gilt oben `aktiv = (themaId === null || leer) &&
     * sitzungsBuehne === null` — in der Uebersicht ist themaId null, also legt sich
     * die LEERE Buehne mit "hier ist nichts vorbereitet" ueber die eben montierte.
     *
     * Genau das ist am 07.09. passiert: ich habe die Klickwege auf diesen Helfer
     * umgehaengt (5f72aaefd/26eaaadfd) und die Markierung uebersehen, die das
     * bestehende Muster weiter unten mitsetzt. Rafael, ~13:1xZ, vor der
     * Coach-Uebersicht: "hier drin steht, dass es arbeitende Sessions gibt ... aber
     * ich komme auf jeden Fall nicht rein." Der Klick tat etwas — man sah es nur
     * nicht.
     *
     * Zurueckgesetzt wird sie an denselben Stellen wie die Buehne selbst
     * (Themenwechsel, erneuter Themenklick).
     */
    if (!themaBehalten) {
      handWahl.current += 1;
      kartenwunsch.current = null;
      setThemaId(null);
      setSicht(null);
      setLeer(null);
      setAuswahl(null);
    }
    setSitzungsBuehne({ id: sessionId, name: name || sessionId.slice(0, 8) });
    onOeffnenWorkspace(projectId);
    if (!themaBehalten) vorhangAus();
  }, [herrichten, onOeffnenWorkspace, setzeBuehnenLayout]);

  /**
   * REGEL 6, ZWEITE HAELFTE — der Klick auf eine Sitzung OHNE Berater.
   *
   * Steht die Sitzung in diesem Haus (`z.haus === z.eigenesHaus`), reicht derselbe
   * sichere Weg wie fuer jede andere Chat-Ansicht: `sitzungAlsBuehne` montiert sie als
   * Dokument im Speicher, ohne Rafaels Layout anzufassen. Steht sie im ANDEREN Haus,
   * gibt es dafuer schon eine eigene Route (`POST /guide/berater/:id/zeigen`,
   * gebaut 10./11.09. fuer genau diesen Fall): sie stoesst jenes Haus an, die
   * Sitzung in seinem EIGENEN Fenster zu montieren — ein `sitzung`-Kachel hier kennt
   * das fremde Transkript nicht und oeffnete sonst ein leeres Fenster.
   */
  const sitzungOhneBeraterOeffnen = useCallback(async (z: SitzungOhneBerater) => {
    if (z.haus && z.eigenesHaus && z.haus !== z.eigenesHaus) {
      try {
        const r = await fetch(`${API}/guide/berater/${encodeURIComponent(z.sessionId)}/zeigen`, { method: 'POST' });
        const d = await r.json().catch(() => null);
        if (!r.ok) { setFehler(d?.error ?? `Fehlgeschlagen (${r.status})`); return; }
        setHausHinweis(`„${z.name}" steht im ${d?.haus === 'partner' ? 'Partner' : 'Dev'}-Fenster bereit — wechsle den Reiter.`);
      } catch (err) { setFehler(`Fehlgeschlagen: ${err}`); }
      return;
    }
    if (!z.projectId || !z.accountId) {
      setFehler(`„${z.name}" hat keinen Arbeitsbereich oder kein Konto — sie laesst sich nicht aufmachen.`);
      return;
    }
    setHausHinweis(null);
    await sitzungAlsBuehne(z.projectId, z.sessionId, z.accountId, z.name);
  }, [sitzungAlsBuehne]);

  /** Rueckweg: sein Layout zurueckschreiben und die Sicherung aufloesen. */
  /**
   * Stellt den Arbeitsbereich auf Rafaels eigenes Layout zurueck.
   *
   * `stillWennKeine`: beim Aufraeumen einer stehengebliebenen Entscheidungsseite ist
   * "es gibt keine Sicherung" die Normallage — dann hat der Guide dieses Fenster nie
   * angefasst und es gibt nichts wegzuraeumen. Beim Knopf "Fenster zurueck" ist
   * dieselbe Lage dagegen eine Auskunft, die Rafael braucht.
   */
  const zurueckstellen = useCallback(async (projectId: string, stillWennKeine = false) => {
    try {
      const r = await fetch(`${API}/guide/layout-sicherung`);
      const alle = r.ok ? (await r.json()).sicherungen ?? {} : {};
      const gesichert = alle[projectId]?.layout;
      if (!gesichert) {
        if (!stillWennKeine) setFehler(`Fuer "${projectId}" gibt es keine Sicherung`);
        return;
      }
      const aktuell = await fetch(`${API}/layouts/${encodeURIComponent(projectId)}`);
      const jetzt = aktuell.ok ? await aktuell.json() : null;
      const doc = { ...gesichert, ...(typeof jetzt?._v === 'number' ? { _v: jetzt._v } : {}) };
      const post = await fetch(`${API}/layouts/${encodeURIComponent(projectId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(doc),
      });
      if (!post.ok) { setFehler(`Zuruecksetzen fehlgeschlagen (${post.status})`); return; }
      await fetch(`${API}/guide/layout-sicherung/${encodeURIComponent(projectId)}`, { method: 'DELETE' });
      setAusDemBlick(null);
      setFehler(null);
      ladeSicherungen();
    } catch (e) {
      setFehler(`Zuruecksetzen fehlgeschlagen: ${e}`);
    }
  }, [ladeSicherungen]);

  /**
   * ALLE umgebauten Fenster zurueck — nicht nur die des aktuellen Themas.
   *
   * WARUM (Rundreise 05d8a9f9, 18:2xZ: 18 von 24 Stationen mit Warnung)
   * Landet Rafael auf einem Thema ohne vorzeigbare Entscheidung, wurde bisher nur
   * `d.ziel_workspace` und der Workspace des Themas zurueckgestellt. Das Fenster der
   * VORIGEN Station steht aber in deren Arbeitsbereich — der gehoert nicht zu diesem
   * Thema und blieb deshalb umgebaut. Weil in diesem Zweig ausserdem bewusst NICHT
   * hingeschaltet wird, blieb genau dieses fremde Fenster sichtbar: links "hier ist
   * gerade nichts", rechts die Entscheidungsseite einer anderen Karte.
   *
   * Das ist derselbe dritte Zustand wie am 06.09. 16:53Z, nur eine Ebene hoeher.
   * Wenn der Guide nichts vorzuweisen hat, bekommt Rafael SEIN Layout zurueck —
   * ueberall dort, wo der Guide es angefasst hat.
   *
   * Kein stiller Fehlschlag: laesst sich die Liste nicht lesen, sagen wir es. Sonst
   * bliebe ein fremdes Fenster stehen und niemand wuesste, warum.
   */
  /*
   * WOHIN ZULETZT HINGESCHALTET WURDE.
   *
   * Nur DIESES Fenster kann noch vor Rafael stehen, wenn er auf einem Thema ohne
   * Entscheidung landet — die anderen umgebauten Arbeitsbereiche sieht er nicht.
   * Ohne den Merker wusste die Spalte nicht, welches es war, und hat vorsichtshalber
   * ALLE zurueckgestellt. Das raeumte auch Fenster weg, an denen fremde Karten
   * haengen (gemessen 06.09. 18:36:18Z: partner-server und werking-report in einem
   * Zug, samt aller Sicherungen). Aufraeumen soll das falsche Fenster treffen, nicht
   * jedes Fenster.
   */
  const zuletztGezeigt = useRef<string | null>(null);

  const fensterAufraeumen = useCallback(async (zielWorkspace: string | null, themenWorkspace: string | null) => {
    // Das zuletzt gezeigte Fenster steht moeglicherweise noch vor ihm; die beiden
    // anderen gehoeren zu diesem Thema und koennen von einem frueheren Besuch
    // umgebaut sein. Mehr nicht — fremde Fenster gehen uns hier nichts an.
    /*
     * DIE BUEHNE ABRAEUMEN IST DAS EIGENTLICHE AUFRAEUMEN.
     *
     * Seit das Rezept nicht mehr in das Layout des Arbeitsbereichs geschrieben wird,
     * gibt es nichts zurueckzustellen: Rafaels eigenes Fenster stand die ganze Zeit
     * unveraendert unter der Buehne und kommt zum Vorschein, sobald sie weg ist.
     * Genau deshalb kann der Fehler vom 06.09. 18:36:18Z hier nicht mehr auftreten,
     * bei dem ein Aufraeum-Lauf zwei fremde Arbeitsbereiche samt aller Sicherungen
     * in einem Zug zurueckgesetzt hat.
     */
    setzeBuehnenLayout(null, null);
    zuletztGezeigt.current = null;

    // Uebergang: Arbeitsbereiche, die VOR dieser Aenderung umgebaut und gesichert
    // wurden, bekommt er weiterhin zurueck. Wo es keine Sicherung gibt — kuenftig
    // der Normalfall — ist das still und tut nichts.
    const zu = [zielWorkspace, themenWorkspace]
      .filter((x): x is string => !!x)
      .filter((x, i, a) => a.indexOf(x) === i);
    for (const projectId of zu) await zurueckstellen(projectId, true);
  }, [zurueckstellen, setzeBuehnenLayout]);

  /** Beim Schliessen der Spalte bekommt er jeden umgebauten Arbeitsbereich zurueck. */
  const schliessen = useCallback(async () => {
    /*
     * ZUERST DIE BUEHNE ABRAEUMEN, sonst bleibt sie ueber dem Arbeitsbereich liegen.
     *
     * Sie ist ein Ueberlagerer ohne eigenen Schliessen-Knopf: wer die Spalte zumacht,
     * waehrend eine Karte offen ist, saehe sonst weiter das Fenster dieser Karte und
     * kaeme an seinen Arbeitsbereich nur noch per Neuladen heran.
     */
    setzeBuehnenLayout(null, null);
    for (const projectId of Object.keys(sicherungen)) await zurueckstellen(projectId);
    onSchliessen();
  }, [sicherungen, zurueckstellen, onSchliessen, setzeBuehnenLayout]);

  useEffect(() => {
    if (themaId) localStorage.setItem('guide-thema', themaId); else localStorage.removeItem('guide-thema');
    if (themaId) { setAuswahl(null); ladeThema(themaId); } else { setSicht(null); setLeer(null); }
  }, [themaId, ladeThema]);


  /**
   * Station 2 + 3 in einem Griff: Thema waehlen heisst, den Workspace aufzubereiten
   * und den Chat zu oeffnen, in dem die Arbeit haengt. Automatisch geoeffnet wird
   * NUR, wenn genau eine Sitzung wartet — bei mehreren waehlt Rafael, sonst reisst
   * ihm die Spalte ein Fenster auf, das er nicht bestellt hat.
   */
  const bereitsGeoeffnet = useRef<Set<string>>(new Set());

  /**
   * DER NOT-KNOPF: "Fenster neu herrichten".
   *
   * Rafael, 06.09. 17:10Z: "vielleicht waere ein Not-Knopf fuer Layout aktualisieren
   * sinnvoll, wenn er das nicht sauber aufbereitet hat". Er setzt den
   * Ziel-Arbeitsbereich auf GENAU das Rezept der Karte zurueck — nur die
   * Rezept-Kacheln, keine fremden Panels, keine Doppelungen; das erledigt
   * `herrichten` ohnehin, weil es das Layout neu baut statt zu ergaenzen.
   *
   * Jede Benutzung wird protokolliert, BEVOR gebaut wird: sie ist der Beleg, dass
   * eine Sitzung ihr Fenster nicht sauber aufbereitet hat, und dieser Beleg soll auch
   * dann bestehen, wenn das Herrichten danach scheitert.
   */
  const fensterNeu = useCallback(async (karteId: string, rezept: Layoutrezept | null, zielProjekt: string | null, auswahl?: BuehnenAuswahl) => {
    if (!rezept || !zielProjekt) {
      setFehler('Für diese Entscheidung ist kein Fenster hinterlegt — es gibt nichts neu herzurichten.');
      return;
    }
    await fetch(`${API}/guide/karten/${karteId}/fenster-neu`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grund: 'Rafael hat das Fenster von Hand neu herrichten lassen.' }),
    }).catch(e => console.warn(`[guide] Der Eintrag "fenster_neu_hergerichtet" liess sich nicht schreiben: ${e}. Das Herrichten laeuft trotzdem.`));
    const ok = await herrichten(zielProjekt, rezept, auswahl);
    if (!ok) { setFehler('Das Fenster liess sich nicht neu herrichten — dein Stand bleibt, wie er war.'); return; }
    setFehler(null);
    onOeffnenWorkspace(zielProjekt);
  }, [herrichten, onOeffnenWorkspace, setzeBuehnenLayout]);



  /*
   * HIER STAND DIE WEICHE DER COACH-UEBERSICHT — samt dem letzten Blockwechsel.
   *
   * Sie reichte Klicks aus der gelieferten HTML-Flaeche durch: Thema waehlen,
   * Arbeitsbereich oeffnen, Sitzung zeigen, BLOCK wechseln (PUT /guide/aktiver-block),
   * Karte vorziehen. Mit der Uebersicht ist ihr einziger Rufer verschwunden, und mit
   * ihr die letzte Stelle, an der die Spalte ueberhaupt noch einen Block anfasste —
   * Bloecke waren die erste Stufe der Sichtbarkeitskette, die dieser Umbau aufloest.
   *
   * Die Routen dahinter bleiben am Server bestehen; sie abzuraeumen ist ein eigener
   * Schritt (der Partner-Server und Skripte rufen sie noch).
   */

  const navigationsReihe = vorrat.filter(x => !x.abschluss && (x.id === offeneBuehne?.id || x.schnellbuehne
    || vorrat.some(q => q.schnellbuehne?.nachBuehneId === x.id) || (!x.rafael_gesehen && !schonVorgelegt.has(x.id))));
  const navigationsIndex = navigationsReihe.findIndex(x => x.id === offeneBuehne?.id);
  /* ── WARTESCHLANGE (Phase 1 der Guide-Flaeche, Rafael 25.10.2026) ───────────────────────────────────────────────────────────────
     Weiter/Zurueck gehen in der Reihenfolge von „Heute dran“ (Rafaels Tagesreihenfolge, sonst der Vorschlag; ein Vorschlag des Assistenten ordnet um) — unabhaengig vom Merker
     „gesehen“. Nur ein KLICK (oder Strg+Alt+Pfeil) oeffnet eine andere Buehne; nichts springt von selbst. Im Assistent-Modus entscheidet dabei Rafaels Klick (der Assistent schlaegt vor);
     die alte Regel „sofortWeiter waehlt im Assistent-Modus keine Buehne“ gilt weiter fuer die AUTOMATISCHEN Uebergaenge nach einer Antwort. */
  const heuteWahl = bereinigeHeuteWahl(heuteGespeichert, new Set(vorrat.map(x => x.id)));
  const queue = warteschlange(vorrat, heuteWahl, Date.now(), offeneBuehne?.id ?? null, { vorschlagIds: steuerZustand.reihenfolgeVorschlag?.ids, letzterIndex: queuePos.current });
  useEffect(() => { if (queue.index >= 0) queuePos.current = queue.index; else if (!offeneBuehne) queuePos.current = null; }, [queue.index, offeneBuehne]);
  useEffect(() => { if (offeneBuehne) setQueueEnde(false); }, [offeneBuehne?.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const queueNachEnde = () => { rafaelNavigiert(); handWahl.current += 1; offeneBuehneRef.current = null; setOffeneBuehne(null); setzeBuehnenLayout(null, null); setQueueEnde(true); };
  const queueWeiter = () => { if (queue.weiter) hofBuehneOeffnen(queue.weiter); };
  const queueZurueck = () => { if (queue.zurueck) hofBuehneOeffnen(queue.zurueck); };
  /** „Später“: nur fuer HEUTE aus der Warteschlange (Schluessel Tag + Nutzer); morgen beginnt wieder der Vorschlag. „Mit Datum“ braeuchte den Server und gibt es noch nicht. */
  const queueSpaeter = () => {
    if (!offeneBuehne) return;
    setHeuteWahl(heuteNutzer, heuteTag, spaeterEintrag(heuteWahl, offeneBuehne.id));
    if (queue.weiter) hofBuehneOeffnen(queue.weiter); else queueNachEnde();
  };
  /** „Erledigt → Weiter“: schliesst die Buehne (dieselbe durable Sendewarteschlange wie der Knopf im Kopf) und oeffnet danach die naechste der Warteschlange. Gesperrt bei offener Frage. */
  const queueErledigtWeiter = () => {
    if (!offeneBuehne || fragenAnDich(offeneBuehne) > 0) return;
    /* Ungesendeter Text oder eine gewaehlte, noch nicht gesendete Antwort: einmal nachfragen — Erledigen schliesst die Buehne, der Entwurf bliebe sonst ohne Ziel liegen. */
    if (hatUngesendetes(window.localStorage, offeneBuehne.id)) {
      const s = erledigtScharfRef.current;
      if (!s || s.id !== offeneBuehne.id || Date.now() - s.ab >= 5000) { erledigtScharfRef.current = { id: offeneBuehne.id, ab: Date.now() }; setScharfTick(n => n + 1); return; }
      if (Date.now() - s.ab < 700) return;      // zu schnell (Doppelklick): die Nachfrage muss gelesen werden koennen
    }
    erledigtScharfRef.current = null;
    /* Scheitert das Einreihen (z. B. Speicher voll), wirft `enqueue` NACH dem laut gemeldeten Eingabefehler (GuideAbsendeFehler) — hier wird bewusst nichts verschluckt, und die
       Buehne bleibt offen: weitergegangen wird nur nach einem gesicherten Auftrag. */
    if (!sendeAuftraege.some(j => j.stage.id === offeneBuehne.id && j.close)) sendeEinreihen(offeneBuehne, undefined, undefined, true);
    if (assistentModusRef.current) wartetAufWeckruf.current.add(offeneBuehne.id);   // der Assistent erfaehrt den Abschluss wie beim Kopf-Knopf
    if (queue.weiter) hofBuehneOeffnen(queue.weiter); else queueNachEnde();
  };
  /* Nach dem Einreihen (Phase 2): KEIN automatisches Weiter mehr — Rafael klickt „Erledigt → Weiter“. Im Assistent-Modus wird weiter der Eingang bestaetigt und der Assistent geweckt
     (sofortWeiter navigiert dort nicht); sonst wird die Buehne nur nachgeladen. */
  const nachAbsenden = (art: "antwort" | "nachricht") => {
    if (!offeneBuehne) return;
    if (assistentModusRef.current && (art === 'antwort' || fragenAnDich(offeneBuehne) > 0)) sofortWeiter(offeneBuehne.id, art);
    else void buehneNachladen(offeneBuehne.id, offeneBuehne.raum);
  };
  const queueAktionen = useRef({ weiter: queueWeiter, zurueck: queueZurueck, spaeter: queueSpaeter });
  queueAktionen.current = { weiter: queueWeiter, zurueck: queueZurueck, spaeter: queueSpaeter };
  useEffect(() => {
    if (!offeneBuehne) return;
    /* Nur Strg+Alt+Pfeil, nie aus einem Eingabefeld, nie bei Tastenwiederholung — und KEIN Kuerzel fuer „Erledigt“ (siehe guide-queue-tasten.ts). */
    const h = (ev: KeyboardEvent) => {
      const taste = queueTaste(ev, document.activeElement);
      if (!taste) return;
      ev.preventDefault();
      queueAktionen.current[taste]();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [offeneBuehne?.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const standWarnung = (() => {
    const st = board?.staende_haeuser;
    if (!st) return null;
    /* SELBSTERKLAEREND (03.10.2026 17:16): „⚠ 45m" liess offen, WAS 45 min sind. Sichtbar steht
       jetzt, welcher Server um wie viel zurueckliegt; der ganze Satz im Tooltip. */
    if (st.unklar) return { text: `Die Software-Stände von Dev- und Partner-Server lassen sich nicht vergleichen`, kurz: 'Stand unklar', zusatz: null as string | null, farbe: 'var(--tn-red)' };
    /* GEMESSEN WIRD DIE BAUZEIT DER OBERFLAECHE (bundleMtime), nicht der Abstand im Code (31.10.2026: „Partner veraltet 15 h“ bei
       einem Code-Abstand von einer CUI-Zusammenfuehrung). Der Satz sagt das jetzt, statt „die Software ist 15 h älter“. */
    if (st.zurueck) return { text: `Die CUI-Oberfläche auf dem ${HAUS_WORT[st.zurueck]}-Server wurde ${dauerWort(st.abstand_minuten ?? 0)} früher gebaut als auf dem anderen (Bauzeit verglichen, nicht der Code-Abstand)`, kurz: `${HAUS_WORT[st.zurueck]}-Oberfläche älter`, zusatz: ` (${dauerKurz(st.abstand_minuten ?? 0)})`, farbe: 'var(--tn-orange)' };
    return null;
  })();
  const mehrWarnfarbe = hofWarnung === 'var(--tn-rosa)' || standWarnung?.farbe === 'var(--tn-red)' ? 'var(--tn-rosa)'
    : hofWarnung ?? standWarnung?.farbe ?? (arbeitsbereichHinweis && !offeneBuehne ? 'var(--tn-orange)' : null);

  return (
    <div
      data-ai-id="guide-panel"
      className="guide-al"
      onClickCapture={event => {
        const button = (event.target as Element).closest('button[data-ai-id]') as HTMLButtonElement | null;
        const action = button?.dataset.aiId;
        if (!button || button.disabled || !action) return;
        // Schließen/Übersicht ist Rafaels eigener Schritt wie ein Öffnen (lib/rafaelNavigation.ts).
        if (['guide-buehne-schliessen', 'guide-close', 'guide-zurueck-board'].includes(action)) { rafaelNavigiert(); if (action === 'guide-close') handWahl.current += 1; }
        if (['guide-buehne-vor', 'guide-buehne-zurueck', 'guide-buehne-schliessen', 'guide-close',
          'guide-historie-oeffnen', 'guide-zurueck-board', 'guide-buehne-angebot-oeffnen', 'guide-berater-oeffnen'].includes(action)) {
          usage('navigation', { action, stage_id: offeneBuehne?.id });
        }
      }}
      style={{
        // Breit: App bestimmt die Breite ueber den Griff, wir fuellen sie aus.
        // Schmal: der ganze Hof (nichts geoeffnet) vs. Spalte neben dem Fenster,
        // sobald ein Berater oder ein Chat rechts wirklich steht.
        ...(breitDargestellt
          ? { width: '100%', flex: 1 }
          : { flex: offeneBuehne || sitzungsBuehne ? '0 0 480px' : '0 0 clamp(440px, 46vw, 860px)' }),
        display: 'flex', flexDirection: 'column',
        borderRight: '1px solid var(--tn-border)', background: 'var(--tn-bg-dark)',
        minHeight: 0, overflow: 'hidden',
      }}
    >
      <div className="guide-al-kopf" style={{ padding: '5px 8px', borderBottom: '1px solid var(--tn-border)', flexShrink: 0 }}>
        {/*
          DIE KOPFZEILE DER ARBEITSLISTE (Rafaels Wahl a, 03.10.2026): links Haus-Umschalter,
          rechts Hofstaat-Schalter, der grosse Primaerknopf „＋ Neue Bühne" und EIN Menue „⋯"
          fuer alles, was Betrieb ist (Assistent, Hinweise, Beendet, Heute, CUI-Stand, Info,
          Bühnenhistorie, Notiz, Sitzung neu starten, Coach-Modell, Farblegende, Sonderfall-
          Hinweise). Liegt dort etwas Warnwuerdiges, traegt das „⋯" einen farbigen Punkt.
        */}
        {/*
          EINE ZEILE (Rafael, 03.10.2026: „so wirklich optimiert ist der obere Teil ja immer
          noch nicht"). Stand bis dahin bei normaler Spaltenbreite auf zwei Zeilen, weil
          „＋ Neue Bühne" umbrach. Jetzt: das Wort „Guide" nur noch als Kompass (die Spalte
          heisst ohnehin so, der GD-Reiter oben leuchtet), die Stand-Warnung kuerzt sich
          mit „…" statt die Zeile zu sprengen, und erst eine wirklich schmale Spalte
          (Tablet-Overlay) bricht um — siehe `.guide-al-kopfzeile` in responsive.css.
        */}
        <div className="guide-al-kopfzeile" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {sitzungsBuehne ? (
            <button
              data-ai-id="guide-zurueck-board"
              onClick={() => { handWahl.current += 1; setSitzungsBuehne(null); setzeBuehnenLayout(null, null); vorhangAus(); }}
              style={{ background: 'none', border: 'none', color: 'var(--tn-blue)', cursor: 'pointer', fontSize: 12, padding: 0 }}
            >
              ← zurück zum Hof
            </button>
          ) : (
            <span role="img" aria-label="Guide" className="guide-kopf-kompass" title="Guide — hier legen dir KI-Berater ihre Bühnen vor: Fragen und Vorschläge, die auf deine Antwort warten"
              style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>🧭</span>
          )}
          <HausUmschalter />
          {/*
            DER CUI-STAND BLEIBT SICHTBAR, WENN EIN HAUS ZURUECKLIEGT — Rafael, 19.09.2026:
            „eine Anzeige, die man erst suchen muss, ist keine Anzeige" (siehe `standZeile` in
            GuideHof). Die volle Zeile steht im Menue; hier steht sie nur, wenn sie etwas sagt.
          */}
          {/* Als kleines Chip mit der Dauer; der ganze Satz steht im Tooltip und im Menue (03.10.2026). */}
          {standWarnung && (
            <button type="button" data-ai-id="guide-stand-warnung" className="guide-kopf-knopf guide-kopf-warnung" onClick={() => setMehrOffen(true)}
              aria-label={standWarnung.text}
              title={`${standWarnung.text} — Klick zeigt die Details im Menü „Mehr“`}
              style={{ color: standWarnung.farbe, borderColor: `color-mix(in srgb, ${standWarnung.farbe} 45%, transparent)` }}>
              ⚠ {standWarnung.kurz}{standWarnung.zusatz && <span className="guide-kopf-wort">{standWarnung.zusatz}</span>}
            </button>
          )}
          {/* Rechts als EINE Gruppe: bricht die Spalte um, wandert sie geschlossen in die zweite Zeile. */}
          <div className="guide-al-kopf-rechts">
          {istBetreiber && <HofSchalter />}
          <button
            type="button"
            data-ai-id="guide-berater-einsetzen-aufklappen"
            aria-expanded={notizArt === 'berater'}
            onClick={() => setNotizArt(notizArt === 'berater' ? null : 'berater')}
            title="Neue Bühne anlegen: du beschreibst einen Auftrag, ein KI-Berater arbeitet ihn aus und legt dir Fragen oder Vorschläge vor"
            aria-label="Neue Bühne anlegen"
            className="guide-kopf-knopf"
            style={{ fontWeight: 700,
              background: 'var(--tn-accent,var(--tn-link))', color: 'var(--tn-tief)', borderColor: 'var(--tn-accent,var(--tn-link))',
              boxShadow: notizArt === 'berater' ? 'inset 0 0 0 2px color-mix(in srgb, var(--tnf-0d1117) 33.3%, transparent)' : undefined }}
          >＋ <span className="guide-kopf-wort">Neue </span>Bühne</button>
          <button
            type="button"
            data-ai-id="guide-mehr"
            aria-expanded={mehrOffen}
            aria-label={`Mehr Funktionen${mehrWarnfarbe ? ' — es liegt ein Hinweis vor' : ''}`}
            onClick={() => setMehrOffen(o => !o)}
            title={`Mehr: frühere Bühnen, Notiz an den Assistenten, Sitzung neu starten, KI-Modell, Betriebshinweise${mehrWarnfarbe ? ' — der farbige Punkt heißt: dort liegt ein Hinweis' : ''}`}
            className="guide-kopf-knopf"
            style={{ position: 'relative', minWidth: 28, padding: '0 7px', justifyContent: 'center',
              background: mehrOffen ? 'color-mix(in srgb, var(--tn-blue) 15%, transparent)' : 'transparent', color: 'var(--tn-text)',
              borderColor: mehrOffen ? 'var(--tn-blue)' : 'var(--tn-border)' }}
          >
            <span aria-hidden="true" style={{ fontSize: 15 }}>⋯</span><span>Mehr</span>
            {mehrWarnfarbe && (
              <span data-ai-id="guide-mehr-punkt" aria-hidden="true"
                style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: 4, background: mehrWarnfarbe }} />
            )}
          </button>
          <button
            data-ai-id="guide-close"
            onClick={schliessen}
            title="Guide schließen — die Spalte geht zu, hergerichtete Arbeitsbereiche gehen auf dein Layout zurück"
            aria-label="Guide schließen"
            className="guide-kopf-knopf guide-kopf-knopf--nackt"
          >
            ✕
          </button>
          </div>
        </div>
        {/*
          DAS MENUE KLAPPT IM NORMALEN FLUSS AUF, unter der Kopfzeile, mit eigenem Schliessen.
          Es bleibt montiert (nur versteckt), weil die Hofansicht ihre Betriebsleiste per
          Portal hineinsetzt — ein Abbauen wuerde deren aufgeklappten Zustand verwerfen.
        */}
        <div data-ai-id="guide-mehr-menue" hidden={!mehrOffen}
          style={{ marginTop: 8, padding: '6px 10px 10px', borderRadius: 8, border: '1px solid var(--tn-border)',
            background: 'var(--tn-surface)', maxHeight: '60vh', overflowY: 'auto' }}
          onKeyDown={e => { if (e.key === 'Escape') setMehrOffen(false); }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--tn-text-muted)', fontWeight: 700 }}>Betrieb</span>
            <span style={{ flex: 1 }} />
            <button type="button" data-ai-id="guide-mehr-schliessen" className="guide-touch-knopf" onClick={() => setMehrOffen(false)}
              aria-label="Menü schließen" style={{ background: 'none', border: 0, color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 13 }}>✕ schließen</button>
          </div>
          {arbeitsbereichHinweis && !offeneBuehne && (
            <div role="status" data-ai-id="guide-mehr-arbeitsbereich-hinweis"
              style={{ fontSize: 12, color: 'var(--tn-orange)', padding: '6px 0', borderBottom: '1px solid var(--tn-border)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ flex: 1 }}>{arbeitsbereichHinweis}</span>
              <button type="button" onClick={() => setArbeitsbereichHinweis(null)}
                style={{ background: 'none', border: '1px solid var(--tn-border)', borderRadius: 5, color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 11 }}>gelesen</button>
            </div>
          )}
          <button type="button" className="guide-mehr-eintrag" data-ai-id="guide-historie-oeffnen" aria-pressed={historieOffen}
            onClick={() => {
              setMehrOffen(false);
              if (historieOffen) historieSchliessen();
              else { setHistorieStart(null); setHistorieOffen(true); }
            }}
            title="Frühere besuchte Bühnen und ihre gespeicherten Fassungen ansehen — ohne alte Sitzungen zu starten">
            🕘 Bühnenhistorie{historieOffen ? ' (offen — schließen)' : ''}
          </button>
          {/* Beide laufen ueber `POST /guide/notiz` (nurAdmin, vom Partner-Tor nicht
              durchgelassen) — fuer Partner endeten sie in 403. */}
          {istBetreiber && (<>
          <button type="button" className="guide-mehr-eintrag" data-ai-id="guide-notiz-aufklappen"
            onClick={() => { setMehrOffen(false); setNotizArt('notiz'); }}>📝 Notiz an den Assistenten</button>
          {/*
            ZWEI KLICKS, WEIL ES SEIN EIGENER CHAT IST. Der erste klappt die Rueckfrage
            auf, der zweite schickt sie los. Eine Abloese des Thronsaals laesst sich
            nicht zuruecknehmen.
          */}
          <button type="button" className="guide-mehr-eintrag" data-ai-id="guide-sitzung-neustart-aufklappen"
            onClick={() => { setMehrOffen(false); setNotizArt('neustart'); }}>↻ Sitzung neu starten …</button>
          </>)}
          {board && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 2px', borderBottom: '1px solid var(--tn-border)' }}>
              <CoachModellSchalter
                modell={board.konfig.coachModell ?? 'fable'}
                coachSessionId={board.konfig.metaKoordinator?.sessionId ?? null}
                onNeuladen={ladeBoard}
              />
            </div>
          )}
          <div ref={setMehrZiel} data-ai-id="guide-mehr-hof" style={{ marginTop: 4 }} />
        </div>
        {/* Nur fuer den Betreiber: Partner haben die lange Karte „Der König und seine Berater"
            und sahen sonst zwei Erklaerungen uebereinander (Rafael, 04.10.2026: „a — Bei Partnern
            nur die lange Karte"). */}
        {istBetreiber && <GuideKurzErklaerung key={user?.id ?? "ohne"} userId={user?.id} />}
      </div>
      {/* Die Formulare zu „＋ Neue Bühne", Notiz und Neustart — direkt unter der Kopfzeile,
          im Fluss, unabhaengig davon, ob eine Buehne offen ist. */}
      <NotizUndNeustart offen={notizArt} setOffen={setNotizArt} onBuehneAngelegt={() => { void ladeBoard(); }} />
        {/*
          KEIN KOPF-SCHALTER MEHR.

          Rafael, 06.09. 15:2xZ, mit Bild: "das habe ich dir schon hunderte Male
          gesagt". Die drei Knoepfe (Auch Schweres / Mittleres / Nur Kleines) und die
          Zeile "offen: 3 leicht · 4 mittel · 0 schwer" sind raus.

          Warum sie falsch waren: Kopfanstrengung ist eine EIGENSCHAFT der Karte, die
          man sehen will — kein Regler, den Rafael vorher bedienen soll. Ein Filter
          verlangt von ihm eine Entscheidung ueber Entscheidungen, bevor er die erste
          gesehen hat, und versteckt Karten, ohne dass er merkt, dass sie fehlen.
          Das Etikett steht jetzt am Thema und an der Karte (`kopfanstrengung`).

          Der Server kennt `modus` weiterhin; er wird von hier aus nur nicht mehr
          gestellt und filtert nichts weg, was Rafael nicht sieht.
        */}

        {/*
          Die aktiven Konten stehen HIER, im festen Kopf — nicht im Rumpf.

          Rafael, 08.09.: "ich wollte es ja oben im guide sehen". Der Kopf
          scrollt nicht weg, also sieht er beim Blick auf den Guide immer, ALS
          WER eine Buehne gerade zeigt. Im Rumpf waere die Zeile nach dem ersten
          Scrollen weg — und damit genau dann unsichtbar, wenn er sie braucht.
        */}
        {/*
          ABER NICHT UEBER EINER OFFENEN BUEHNE (Rafael, 09.09. 16:1xZ, zum Bild der
          Report-Buehne: "da stehen tausend sachen statt dass das wesentliche
          angezeigt wird"). Die Liste ist eine Zugaenge-Uebersicht — sie beantwortet
          "als wer kann eine Buehne eine App zeigen", nicht "was soll ich hier
          entscheiden". In der Themen-LISTE bleibt sie also stehen, wie er es am
          08.09. verlangt hat; sobald er ein Thema geoeffnet hat, gehoert der Platz
          der Entscheidung. Ein Klick auf "← Themen" bringt sie zurueck.
        */}

        {/*
          UND DARUNTER: WELCHES HAUS IST AKTUELL (Rafael, 12.09. 06:25Z).

          Dieselbe Stelle und dieselbe Regel wie die Kontenliste darueber: im festen
          Kopf, weil die Frage "sehe ich gerade den neuen Stand?" beim BLICK auf den
          Guide aufkommt und nicht nach dem Scrollen — und nicht ueber einer offenen
          Buehne, wo der Platz der Entscheidung gehoert (Rafael, 09.09.: "da stehen
          tausend sachen statt dass das wesentliche angezeigt wird").
        */}


      <div ref={guideInhaltRef} style={{
        // Breit: der obere Teil bekommt seinen Anteil, darunter haengt die
        // Entscheidungsgrundlage. Schmal: wie bisher der ganze Rest.
        flex: 1, overflowY: 'auto', padding: breitDargestellt ? 16 : 10, minHeight: 0,
        fontSize: breitDargestellt ? 15 : undefined,
      }}>
        {hausHinweis && <div role="status" data-ai-id="guide-haus-hinweis" style={{ fontSize: 12, color: 'var(--tn-text)', padding: '5px 9px', borderRadius: 6, border: '1px solid var(--tn-border)', background: 'var(--tn-surface)', marginBottom: 8 }}>{hausHinweis}</div>}
        {arbeitsbereichHinweis && offeneBuehne && (
          <details data-ai-id="guide-arbeitsbereich-hinweis" style={{ fontSize: 12, color: 'var(--tn-orange)', marginBottom: 6 }}>
            <summary style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>⚠ Arbeitsbereich fehlt in diesem Haus — Panels darunter gehören nicht zur Bühne</summary>
            <div style={{ padding: '4px 0 0 14px', color: 'var(--tn-text-muted)' }}>{arbeitsbereichHinweis}</div>
          </details>
        )}
        {weiterAuf && !offeneBuehne && (
          <WeiterAufNachfolgerin von={weiterAuf.von} nachfolger={weiterAuf.nachfolger} wort={weiterAuf.wort}
            onOeffnen={id => { setWeiterAuf(null); void buehneOeffnenNachKennung(id); }} onWeg={() => setWeiterAuf(null)} />
        )}
        {angebot && (
          <div data-ai-id="guide-buehne-angebot" role="status"
            style={{ ...box, color: 'var(--tn-text)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>Eine Bühne möchte zu dir: <b>{angebot.titel}</b>. Deine bleibt stehen.</span>
            <button type="button" data-ai-id="guide-buehne-angebot-oeffnen"
              onClick={() => { const z = angebot; setAngebot(null); void buehneOeffnenNachKennung(z.buehneId); }}
              style={{ padding: '4px 10px', fontSize: 11, borderRadius: 5, cursor: 'pointer',
                border: '1px solid var(--tn-accent,var(--tn-link))', background: 'var(--tn-accent,var(--tn-link))', color: 'var(--tn-tief)' }}>
              Jetzt öffnen
            </button>
            <button type="button" data-ai-id="guide-buehne-angebot-weg"
              onClick={() => setAngebot(null)}
              style={{ padding: '4px 10px', fontSize: 11, borderRadius: 5, cursor: 'pointer',
                border: '1px solid var(--tn-border)', background: 'var(--tn-bg)', color: 'var(--tn-text-muted)' }}>
              Später
            </button>
          </div>
        )}
        {/*
          KEIN SENDESTATUS IN RAFAELS ANSICHT (Rafael, 18.10.2026: „was interessiert mich das ob die
          im sendestatus ist — das ist für mich überhaupt keine info — die soll in den sitzungsaal
          gehen"). Hier stand eine zugeklappte Liste „Übermittlung deiner Eingaben · N offen" samt
          roten Zeilen für abgewiesene Eingaben. Gespeicherte Nachrichten/Antworten verlassen die
          Schlange jetzt sofort (GuideSendQueue, `serverTraegtZustellung`); was danach hängt oder der
          Server abweist, meldet der Server in den Sitzungssaal (`meldeSitzungssaal`). Stehen bleibt
          nur der unlesbare lokale Speicher — dann kann der Browser seine Eingabe gar nicht sichern.
        */}
        {sendeQueue?.fault && <div data-ai-id="guide-sendestatus" role="status" style={{ padding: 8, fontSize: 12, borderBottom: '1px solid var(--tn-border)' }}>
          {sendeQueue.fault}
        </div>}
        {/*
          WEGLASSEN, WAS IHM NICHT GEHOERT — Rafael, 22.09.2026, Option a. Umgesetzt
          wird das an der QUELLE (die gesperrten Wege werden fuer ihn gar nicht mehr
          gerufen, s. u.), NICHT durch Verstecken dieser Meldung.

          Der Unterschied ist teuer bezahlt: der erste Versuch blendete das Banner fuer
          Partner komplett aus. Damit verschwand auch „diese Buehne ist noch im Bau:
          ohne Rezept gibt es kein Fenster" — eine echte, IHN betreffende Auskunft.
          Er haette geklickt und nichts waere passiert, ohne jeden Hinweis warum.
          Was ihm nicht gehoert, soll nicht entstehen; was ihn angeht, muss er sehen.
        */}
        {(fehler || Object.keys(leseprobleme).length > 0) && (
          <details style={{ borderBottom: '1px solid var(--tn-border)', color: 'var(--tn-red)', fontSize: 11, padding: '4px 0', marginBottom: 6 }}>
            <summary style={{ cursor: 'pointer' }}><span role="alert">Guide: Vorgang fehlgeschlagen · Details</span></summary>
            <div style={{ maxHeight: 200, overflow: 'auto', overflowWrap: 'anywhere', padding: 6 }}>
              {fehler && <div>{fehler}</div>}
              {Object.values(leseprobleme).map(problem => problem && <div key={problem.vorgang}>
                {problem.grund} · seit <time dateTime={new Date(problem.seit).toISOString()}>{new Date(problem.seit).toLocaleTimeString('de-AT')}</time>
              </div>)}
            </div>
          </details>
        )}
        {!board && !fehler && Object.keys(leseprobleme).length === 0 && istBetreiber && <div style={{ color: 'var(--tn-text-muted)', fontSize: 12 }}>lädt …</div>}

        {/*
          DER RUMPF DER SPALTE IST DER HOF — und sonst nichts (11.09.2026).

          Hier standen bis heute ZWEI WELTEN uebereinander: oben die neue
          Buehnenreihe, darunter unveraendert die alte — die Karten-Reihe
          (`ThemaAnsicht`), "Alle Boards · 19 Themen", die Bloecke, die
          Coach-Uebersicht, "Erledigt. Was als Naechstes?" und die
          Vorbereitet-Zweige. Rafael wollte den Guide "in der neuen Form", leer.

          WARUM DIE ALTE HAELFTE NICHT NUR HAESSLICH WAR, SONDERN FALSCH: ihre
          Sichtbarkeit lief ueber eine vierstufige Kette — Thema in einem Block,
          Block offen, Thema hat eine offene Karte, Karte hat Optionen. Gemessen am
          11.09.: von 19 Themen lagen 13 in Bloecken, 12 in offenen Bloecken, 6
          hatten eine offene Karte. Jede Huerde hing am Thema oder an seinem Block,
          und Rafael sah von 483 Beratern 61. Der Bauplan loest das nicht durch
          Aufraeumen, sondern durch einen Satz: Sichtbarkeit haengt am RAUM und an
          nichts sonst. Das Thema ist ein Etikett ueber der Frage, kein Tor.

          WAS BLEIBT UND WARUM. Unter dem Hof stehen weiter "Heute fuer dich
          entschieden" und "Zustellung unbestaetigt". Beide blenden sich selbst aus,
          wenn sie leer sind, und sind heute leer: der leere Guide sieht also genau
          aus wie die Skizze. Sie zu LOESCHEN waere etwas anderes gewesen — beides
          sind Zusagen an Rafael (Einspruch gegen eine selbst getroffene Entscheidung;
          eine Nachricht, die nie ankam), und eine Zusage still zu entfernen ist kein
          Aufraeumen.
        */}
        {/* Antwort angekommen / Assistent antwortet nicht — über der Bühne, auch über einer inzwischen erledigten. */}
        {!historieOffen && offeneBuehne && wartetAufNaechste?.buehneId === offeneBuehne.id && (() => {
          const uhrzeit = (t: number) => new Date(t).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
          const { seit, gespeichert, art } = wartetAufNaechste;
          // P3-R2-3: ein freies Wort ist keine Antwort auf die Fragen — der Satz sagt, was ankam.
          const was = art === 'nachricht' ? 'Nachricht an den Berater' : 'Antwort';
          const deine = art === 'nachricht' ? 'Deine Nachricht' : 'Deine Antwort';
          // Die Frist laeuft ab dem Speichern (dann wird der Assistent geweckt), sonst ab der Abgabe.
          const zuLange = wartenUhr - (gespeichert ?? seit) >= ASSISTENT_FRIST_MS;
          return <div data-ai-id="assistent-wartet-naechste" role={zuLange ? 'alert' : 'status'} data-zu-lange={zuLange ? 'ja' : 'nein'}
            style={{ margin: '0 0 8px', padding: '9px 12px', borderRadius: 7, fontSize: 14, fontWeight: 600,
              border: `2px solid ${zuLange ? 'var(--tn-red)' : 'var(--tn-green)'}`,
              background: `color-mix(in srgb, ${zuLange ? 'var(--tn-red)' : 'var(--tn-green)'} 14%, var(--tn-bg-dark))`, color: 'var(--tn-text)' }}>
            {gespeichert === null
              ? (zuLange
                ? <>{deine} von {uhrzeit(seit)} ist noch nicht am Server bestätigt – siehe „Zustellung“ unten. Darum kommt noch keine nächste Bühne.</>
                : <>{was} abgegeben ({uhrzeit(seit)}) – wird zugestellt, danach holt der Assistent die nächste Bühne …</>)
              : (zuLange
                ? <>Keine nächste Bühne – der Assistent antwortet nicht. {deine} ist um {uhrzeit(gespeichert)} angekommen. Schreib ihm unten „weiter“.</>
                : <>{was} angekommen ({uhrzeit(gespeichert)}) – der Assistent holt die nächste Bühne …</>)}
          </div>;
        })()}
        {historieOffen ? (
          <GuideHistorie startBuehneId={historieStart} onSchliessen={historieSchliessen} istBetreiber={istBetreiber} />
        ) : offeneBuehne?.schnellbuehne ? (
          <div data-ai-id="guide-buehne-offen" data-buehne-id={offeneBuehne.id} style={{ ...box, fontSize: 13 }}>
            <strong>{offeneBuehne.titel}</strong>
            <p>Eine schnelle Darstellung aus deinem Zwischenfrage-Chat.</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button type="button" data-ai-id="guide-schnellbuehne-zurueck" onClick={() => void buehneWechseln(navigationsReihe[navigationsIndex - 1]?.id || offeneBuehne.schnellbuehne?.nachBuehneId || offeneBuehne.id)}>‹ Bühne zurück</button>
              <button type="button" data-ai-id="guide-schnellbuehne-vor" disabled={navigationsIndex < 0 || !navigationsReihe[navigationsIndex + 1]}
                onClick={() => { const next = navigationsReihe[navigationsIndex + 1]; if (next) hofBuehneOeffnen(next); }}>Bühne vor ›</button>
            </div>
            <Schnellchat haus={offeneBuehne.schnellbuehne.haus} parent={offeneBuehne.schnellbuehne.parent} buehneId={offeneBuehne.schnellbuehne.nachBuehneId} onOffen={() => {}} />
            <p>Für Änderungen: Zwischenfrage öffnen, Änderung beschreiben und „Schnellbühne“ klicken.</p>
            <button type="button" onClick={() => { rafaelNavigiert(); handWahl.current += 1; offeneBuehneRef.current = null; setOffeneBuehne(null); setzeBuehnenLayout(null, null); }}>Zur Übersicht</button>
          </div>
        ) : offeneBuehne ? (offeneBuehne.abschluss ? (wartendSichtbar ? (
          /* Gerade von Rafael erledigt, der Assistent holt die nächste (P2-1). Kein Archivfoto-Text:
             rechts stehen noch die echten Fenster, nicht ein Foto. */
          <div data-ai-id="guide-buehne-erledigt-wartet" data-buehne-id={offeneBuehne.id} style={{ ...box, fontSize: 12 }}>
            <b>{offeneBuehne.titel}</b>
            <div style={{ marginTop: 4, color: 'var(--tn-text-muted)' }}>Erledigt. Rechts siehst du sie noch, bis die nächste Bühne kommt.</div>
            {offeneBuehne.abschluss.art === 'abgeloest' && offeneBuehne.nachfolger && (
              <WeiterAufNachfolgerin von={offeneBuehne.id} nachfolger={offeneBuehne.nachfolger} wort={angenommenesWortDerBuehne(offeneBuehne)}
                onOeffnen={id => void buehneWechseln(id)} />
            )}
          </div>
        ) : (
          <HistorischeBuehne buehne={offeneBuehne} onZurueck={() => { handWahl.current += 1; offeneBuehneRef.current = null; setOffeneBuehne(null); setzeBuehnenLayout(null, null); }} onOeffnen={id => void buehneWechseln(id, { ohneAbloesung: true })} />
        )) : (
          /*
            EINE OFFENE BUEHNE ERSETZT DIE RAEUME — offene Frage der Skizze, hier
            entschieden.

            Die Skizze laesst ausdruecklich offen, wo die geoeffnete Buehne
            erscheint. Die Antwort steht in Rafaels eigener Trennung vom 11.09.:
            links wird GEKLICKT, rechts wird GELESEN. Der Berater selbst (seine
            Seite, seine Fenster) geht als Ueberlagerung ueber den Arbeitsbereich —
            dort ist Platz, und genau dafuer hat jede Buehne ihr eigenes Skript. In
            die schmale Spalte gehoert dann nur noch, was er ANKLICKT.

            Blieben die Raeume daneben stehen, staende ueber der Frage, die er
            gerade beantworten soll, wieder der ganze Hof. Rafael, 09.09. 16:1xZ,
            zu genau diesem Bild: "da stehen tausend sachen statt dass das
            wesentliche angezeigt wird." Der Rueckweg ist ein Knopf, kein Suchen.
          */
          <div data-ai-id="guide-buehne-offen" data-buehne-id={offeneBuehne.id} data-buehne-titel={offeneBuehne.titel} data-buehne-eigentuemer={offeneBuehne.eigentuemer} data-beratungsrevision={offeneBuehne.antwortbedarf_revision} data-buehne-stand-am={offeneBuehne.stand?.am} data-guide-zugang={offeneBuehne.zugang.ueberholt ? "ueberholt" : "gueltig"} data-guide-entscheiden={offeneBuehne.zugang.entscheiden ? "ja" : "nein"} style={{minWidth:0}}>
            {/*
              EINE REIHE STATT VIER STOCKWERKE (Rafael, 21.09.2026: *"Erstens könnte das
              oben mal ein bisschen kompakter sein. Das braucht relativ viel Platz, nur
              für die Überschrift."*). Gemessen an seiner Flaeche vor dieser Aenderung:
              368 Bildpunkte vom Panelanfang bis zur ersten Frage.

              UND JETZT WIRKLICH EINE (Rafael, 03.10.2026 16:05: „jetzt können wir vielleicht
              das hier noch kompakter machen – das sieht auch immer komisch aus"). Bis dahin
              standen hier drei Zeilen: die Kettenzeile „vorher: …", darunter „← zurück zu den
              Räumen" neben einem Knopf mit der ROHEN Kennung der Vorgaengerin, darunter
              Erledigt/Später. Der Kennungs-Knopf tat dasselbe wie die Kettenzeile (beide oeffnen
              `vorgaenger`) — er ist in ihr aufgegangen; die Kettenzeile steht jetzt auch ohne
              `kette_an`, dann nur mit dem Titel. Brotkrumen links, Erledigt/Später rechts.
            */}
            <div data-ai-id="guide-buehne-kopfzeile" className="guide-buehne-kopfzeile">
            <button
              type="button"
              data-ai-id="guide-buehne-schliessen"
              onClick={() => { handWahl.current += 1; offeneBuehneRef.current = null; setOffeneBuehne(null); setzeBuehnenLayout(null, null); }}
              title="Zurück zur Übersicht aller Bühnen — schließt diese Bühne (nichts geht verloren)"
              aria-label="Zurück zur Übersicht aller Bühnen"
              style={{ color: 'var(--tn-blue)' }}
            >← Übersicht</button>
            {/* „vorher: … · deine Antwort: … → …" (Rafael, 03.10.2026); ohne Vorgaengerin leer. */}
            <GeteilteBuehneBearbeiten buehne={offeneBuehne} onFehler={setFehler} onGestartet={() => void buehneWechseln(offeneBuehne.id, {ohneAbloesung:true})} />
            <BuehnenKettenZeile buehne={offeneBuehne} onOeffnen={id => void buehneWechseln(id, { ohneAbloesung: true })} inZeile />
            {buehneZurueck.length === 0 && <span style={{ flex: 1 }} />}
            {buehneVor.map(id => (
              /* Abgeloeste Buehne (`nachfolger`/`weitere_nachfolger`, Rafael 15.09.2026): je
                 Nachfolgerin ein kleiner Knopf — die Kennung steht nur im Tooltip. */
              <button
                key={`vor:${id}`}
                type="button"
                data-ai-id="guide-buehne-neuere"
                onClick={() => void buehneWechseln(id)}
                title={`Neuere Bühne öffnen — sie hat diese abgelöst (${id})`}
                aria-label="Neuere Bühne öffnen"
              >Neuere Bühne →</button>
            ))}
            <BuehneAbschliessen key={`abschluss:${offeneBuehne.id}`} buehne={offeneBuehne} onFehler={setFehler}
              onAbgeschlossen={() => sofortWeiter(offeneBuehne.id)} />
            <BuehneAufSpaeter key={`spaeter:${offeneBuehne.id}`} buehne={offeneBuehne} onFehler={setFehler} />
            </div>
            <FrageBeantwortetAuf buehne={offeneBuehne} onOeffnen={id => void buehneWechseln(id, { ohneAbloesung: true })} />
            <div
              data-ai-id="guide-buehne-kopf"
              data-buehne-id={offeneBuehne.id}
              data-buehne-titel={offeneBuehne.titel}
              data-buehne-eigentuemer={offeneBuehne.eigentuemer}
              data-beratungsrevision={offeneBuehne.antwortbedarf_revision}
              data-buehne-stand-am={offeneBuehne.stand?.am}
              data-guide-zugang={offeneBuehne.zugang.ueberholt ? 'ueberholt' : 'gueltig'}
              data-guide-entscheiden={offeneBuehne.zugang.entscheiden ? 'ja' : 'nein'}
              style={{ ...box, marginBottom: 8, fontSize: 11 }}
            >
              {/*
                BUEHNE VOR / BUEHNE ZURUECK (Rafael, 20.09.2026: *"ich will auch oben
                bei den entscheidungen einen 'buehne vor' und 'buehne zurueck' button
                damit ich schnell umschalten und durchnavigieren kann"*).

                DIESELBE REIHENFOLGE WIE IM HOF LINKS. `buehnenVorrat` kommt von dort
                schon sortiert (`Hofansicht.onBestand`, nach `seitImRaum`), also ist
                "vor" wirklich die naechste Zeile, die er drueben sieht - eine eigene
                Sortierung hier waere eine zweite Wahrheit ueber dieselbe Reihe.

                ABGESCHLOSSENE SIND UEBERSPRUNGEN: sie tragen keine offenen
                Entscheidungen mehr, und der Weg zu ihnen ist die Historie, nicht
                dieser Knopf. Am Ende der Reihe wird der Knopf stumpf statt
                zu verschwinden - eine Leiste, die die Breite wechselt, verschiebt
                beim Durchklicken den Titel darunter.
              */}
              {/*
                EINE ZEILE (Rafael, 03.10.2026): ‹ › 4/4 · Titel · Berater-Chat. Bis dahin drei
                Zeilen — „← Bühne zurück / Bühne vor →  4 von 4", darunter der Titel, darunter
                der Berater-Knopf. Die Knoepfe heissen weiter „Bühne zurück"/„Bühne vor"
                (aria-label), der Titel kuerzt sich mit „…" und steht ganz im Tooltip.
              */}
              {(() => {
                /*
                 * EIN EINMALIGER DURCHGANG, KEIN KREISVERKEHR (Rafael, 21.09.2026:
                 * *"dass ich jede Bühne nur einmal sehe"*, *"einmal schnell alle
                 * abarbeiten"*). Gesehene fallen raus, die Reihe wird kuerzer.
                 *
                 * DIE GERADE OFFENE BLEIBT DRIN, auch wenn sie schon markiert ist —
                 * sonst verloere die Leiste im selben Moment ihren eigenen Platz und
                 * "3 von 12" spraenge beim Ansehen auf etwas anderes.
                 *
                 * Was er liegen laesst, holt er sich selbst ueber die Raumansicht
                 * (*"da möchte ich halt dann extra hin navigieren"*) — hier kommt es
                 * ausdruecklich NICHT von selbst wieder.
                 */
                const knopf = (ziel: BuehneAnsicht | undefined, text: string, wort: string, kennung: string) => (
                  <button
                    type="button"
                    data-ai-id={kennung}
                    disabled={!ziel}
                    aria-label={wort}
                    onClick={() => { if (ziel) hofBuehneOeffnen(ziel); }}
                    title={ziel ? `${wort}: ${ziel.titel}` : `${wort} — Ende der Reihe`}
                    style={{
                      padding: '1px 8px', fontSize: 14, lineHeight: '20px', borderRadius: 5, minWidth: 28,
                      cursor: ziel ? 'pointer' : 'default',
                      border: '1px solid var(--tn-border)',
                      background: 'var(--tn-bg)',
                      color: ziel ? 'var(--tn-text)' : 'var(--tn-text-muted)',
                      opacity: ziel ? 1 : 0.45,
                    }}
                  >{text}</button>
                );
                return (
                  <>
                  <div data-ai-id="guide-buehne-navzeile" className="guide-buehne-navzeile">
                    {knopf(queue.zurueck ?? undefined, '\u2039', `Zurück in der Warteschlange (${QUEUE_KUERZEL.zurueck})`, 'guide-queue-zurueck')}
                    {knopf(queue.weiter ?? undefined, '\u203a', `Weiter in der Warteschlange (${QUEUE_KUERZEL.weiter})`, 'guide-queue-weiter')}
                    <span data-ai-id="guide-queue-nr" style={{ fontSize: 11, color: 'var(--tn-text-muted)', flexShrink: 0, whiteSpace: 'nowrap', fontFamily: 'var(--tn-mono, monospace)' }}
                      title={queue.quelle === 'heute' ? 'Reihenfolge: deine Tagesreihenfolge aus „Heute dran“' : queue.quelle === 'assistent' ? 'Reihenfolge: Vorschlag deines Assistenten' : 'Reihenfolge: Vorschlag aus „Heute dran“'}>
                      {queue.index >= 0 ? `Nr. ${queue.index + 1} von ${queue.liste.length}` : `nicht in der Schlange · ${queue.liste.length}`}
                    </span>
                    <span className="guide-buehne-navtitel" title={offeneBuehne.titel}>{offeneBuehne.titel}</span>
                    {onBeraterOeffnen && <button type="button" data-ai-id="guide-berater-oeffnen"
                      onClick={onBeraterOeffnen}
                      title="Öffnet rechts den Chat mit dem Berater — der KI-Sitzung, die diese Bühne vorbereitet hat. Dort kannst du nachfragen."
                      aria-label="Chat mit dem Berater dieser Bühne rechts öffnen"
                      style={{ padding: '2px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
                        border: '1px solid var(--tn-blue)', borderRadius: 5, background: 'var(--tn-bg-dark)', color: 'var(--tn-blue)' }}>
                      Chat mit Berater →
                    </button>}
                    <button type="button" data-ai-id="guide-queue-spaeter" onClick={queueSpaeter}
                      title={`Später (${QUEUE_KUERZEL.spaeter}): für heute aus der Warteschlange nehmen — morgen beginnt wieder der Vorschlag. Mit Datum gibt es noch nicht.`}
                      style={{ padding: '2px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid var(--tn-border)', borderRadius: 5, background: 'var(--tn-bg)', color: 'var(--tn-text)' }}>Später</button>
                    <button type="button" data-ai-id="guide-queue-erledigt-weiter" onClick={queueErledigtWeiter} disabled={fragenAnDich(offeneBuehne) > 0 || scharfGesperrt}
                      title={fragenAnDich(offeneBuehne) > 0 ? 'Erst die offene Frage beantworten' : 'Erledigt → Weiter: Bühne abschließen und die nächste öffnen (nur mit der Maus — dafür gibt es bewusst kein Tastenkürzel)'}
                      style={{ padding: '2px 8px', fontSize: 11, cursor: fragenAnDich(offeneBuehne) > 0 ? 'default' : 'pointer', whiteSpace: 'nowrap', border: '1px solid var(--tn-green)', borderRadius: 5, background: 'var(--tn-bg)', color: 'var(--tn-green)', opacity: fragenAnDich(offeneBuehne) > 0 ? 0.45 : 1 }} data-scharf={scharfJetzt ? (scharfGesperrt ? 'gesperrt' : 'bereit') : undefined}>{scharfJetzt ? (scharfGesperrt ? 'Ungesendetes liegt im Feld — wirklich erledigen? (gleich bereit)' : 'Ja, trotzdem erledigen') : 'Erledigt → Weiter'}</button>
                  </div>
                  {(() => {
                    const z = zielZeile(offeneBuehne);
                    return <div data-ai-id="guide-buehne-zielzeile" className="guide-buehne-zielzeile" title={z.grund ? `Unter „Divers“, weil: ${z.grund}` : z.pfad}
                      style={{ fontSize: 11.5, color: 'var(--tn-text-muted)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <span style={{ color: 'var(--tn-text)', fontWeight: 600 }}>{z.pfad}</span>
                      {z.grund && <span data-ai-id="guide-buehne-zielzeile-grund">· {z.grund}</span>}
                      {z.meta.map(m => <span key={m}>· {m}</span>)}
                    </div>;
                  })()}
                  </>
                );
              })()}
              {(() => {
                /* Ein Satz statt Raum-/Belegungsnamen (03.10.2026 17:16), siehe `buehnenStatusSatz`. */
                const status = buehnenStatusSatz(offeneBuehne);
                const { satz, innen } = buehneVeraltetSeit == null ? status
                  : { satz: 'Letzter bekannter Stand — aktuelle Tätigkeit noch nicht bestätigt', innen: status.satz };
                return <div data-ai-id="guide-buehne-status" className="guide-buehne-status" title={`${satz}\n${innen}`}>{satz}</div>;
              })()}
              {/*
                DIE WAHL AUSSPRECHEN (Rafael, 13.09. 10:27Z: „dann kann ich entscheiden ob
                ich direkt auf die buehnenfragen antworte oder chatte"). Ohne diesen Satz
                muesste er raten, welches der beiden Eingabefelder wen erreicht — und genau
                das war schon vorher die Falle, siehe `FreiesWort`.
              */}

            </div>

            {/*
              LINKS WIRD GEKLICKT, RECHTS GELESEN — Rafaels Trennung vom 11.09.
              Hier stand der alte `KartenBlock`. Er ist weg, weil er eine KARTE
              zeichnet und die Buehne ihre Entscheidungen seither selbst traegt; ihn
              mit einem uebersetzten Objekt zu fuettern waere eine zweite Wahrheit
              ueber dieselbe Sache gewesen.
            */}
            <GuideBeratungsstand buehne={offeneBuehne} />
            {offeneBuehne.eigentuemer === 'rafael' && offeneBuehne.aufgaben !== undefined && (offeneBuehne.aufgabenVollzugriff === true
              ? <GuideAufgaben key={`aufgaben:${offeneBuehne.id}`} buehneId={offeneBuehne.id}
                  bedienbar={!offeneBuehne.abschluss && offeneBuehne.raum!=='pausenraum'}
                  arbeitGemessen={offeneBuehne.arbeitsmessung==='unbekannt' ? undefined : offeneBuehne.arbeit_laeuft}
                  onGespeichert={() => void buehneNachladen(offeneBuehne.id)} />
              : <GuideAufgabenKontext key={`aufgaben-kontext:${offeneBuehne.id}`} buehneId={offeneBuehne.id} kontext={offeneBuehne.aufgaben}
                  arbeitGemessen={offeneBuehne.arbeitsmessung==='unbekannt' ? undefined : offeneBuehne.arbeit_laeuft} />)}
            <details><summary>Bisheriges Zielverständnis und Quellen</summary>
              {offeneBuehne.eigentuemer === 'rafael' && offeneBuehne.aufgabenVollzugriff !== true
                ? (offeneBuehne.zielinventar ? <GuideZielinventarInhalt doc={offeneBuehne.zielinventar} /> : <p data-ai-id="guide-zielinventar-fehlt">Teilziele werden noch aufbereitet.</p>)
                : <GuideZielinventar buehneId={offeneBuehne.id} aktuelleVersion={offeneBuehne.zielinventar?.version} />}
            </details>
            {offeneBuehne.eigentuemer === 'rafael' && offeneBuehne.aufgabenVollzugriff !== true
              ? <GuideRichtungKontext key={`richtung-kontext:${offeneBuehne.id}`} richtung={offeneBuehne.richtung} />
              : <GuideRichtung key={offeneBuehne.id} buehneId={offeneBuehne.id} bedienbar={!offeneBuehne.abschluss && offeneBuehne.raum!=='pausenraum' && !wartendSichtbar} aktuelleVersion={offeneBuehne.richtung?.version} onGespeichert={() => void buehneNachladen(offeneBuehne.id)} />}
            <GuideBeratung buehne={offeneBuehne} />
            <AbsendeRegister key={`absenden:${offeneBuehne.id}`} buehneId={offeneBuehne.id} nachSenden={nachAbsenden}>
            {/*
              MARKIEREN, NIE LEEREN — siehe buehneVeraltetSeit oben. Direkt an der
              Karte, nicht als generischer hausHinweis irgendwo darueber: was hier
              steht, ist der Inhalt, der veraltet sein könnte.
            */}
            {buehneVeraltetSeit != null && (
              <div
                data-ai-id="guide-buehne-veraltet"
                style={{ ...box, marginBottom: 8, fontSize: 11, borderStyle: 'dashed',
                  color: 'var(--tn-text-muted)' }}
              >
                {/* Plain statt „veraltet" (Rafael 16.10. 10:51Z: „drin steht dann wieder bühne veraltet??"): das ist
                    eine Verbindungsstörung der Anzeige, kein Zustand der Bühne. */}
                <b style={{ color: 'var(--tn-orange, var(--tn-text))' }}>Anzeige hängt.</b>{' '}
                Seit {new Date(buehneVeraltetSeit).toLocaleTimeString('de-DE')} konnte kein neuer Stand angewendet werden.{' '}
                {buehnenAktualisierungsproblem?.grund} Was hier steht, ist der letzte bekannte. Ob der Berater gerade arbeitet, lässt sich daraus nicht feststellen.
                Die Anzeige versucht es automatisch erneut.
                <button type="button" data-ai-id="guide-buehne-erneut-laden"
                  onClick={() => { aktualisierungAnfordern(); guideVerbindung.reconnectNow(); }}>Jetzt aktualisieren</button>
              </div>
            )}

            {/*
              ENTSCHEIDEN NUR, WO DIE ANTWORT ZAEHLT. Die Bloecke ganz WEGZULASSEN ist
              Absicht: ein ausgegrauter Knopf laedt zum Klicken ein und erklaert erst
              danach, warum nichts passiert. Was es hier zu lesen gibt — Titel,
              Zusammenhang, Verlauf, das Eingabefeld darunter —, bleibt vollstaendig da.
            */}
            {/*
              WAEHREND „ANTWORT ANGEKOMMEN" STEHT, GIBT ES NICHTS ZU KLICKEN (Pruefurteil ASSIOEFFNEN3R
              P3-1): die Buehne zeigte sonst bis zur Auffrischung weiter „gewählt, noch nicht
              gesendet" und einen aktiven „Absenden"-Knopf — ein zweiter Klick ergab einen roten Satz
              oder einen zweiten Weckruf. Weggelassen statt ausgegraut, wie unten begruendet.
            */}
            {wartendSichtbar ? null : offeneBuehne.zugang.entscheiden
              ? (() => {
                /*
                 * GENAU EINE OFFENE FRAGE: Klick sendet sofort, wie bisher —
                 * EntscheidungsBlock bleibt fuer diesen Fall unveraendert. ERST AB
                 * ZWEI offenen Fragen sammelt EntscheidungenListe lokal, bis Rafael
                 * ausdruecklich auf "Absenden" klickt (Rafael 12.09., siehe deren
                 * Kopfkommentar).
                 *
                 * ERLEDIGTES IST KEIN BLOCK MEHR, SONDERN EINE ANMERKUNG (Rafael,
                 * 13.09.: "das entshcieudngsfenster soll nur neue sachen anzeigen -
                 * vergangen sollen hoechstens angemekrt ... werden") — siehe
                 * ErledigteAnmerkung. Bis dahin standen beantwortete und
                 * zurueckgezogene Fragen als volle Bloecke UEBER den lebenden.
                 *
                 * REIHENFOLGE: ERST die Anmerkung, DANN die offenen Fragen mit dem
                 * Absenden-Knopf am Ende (Rafael 12.09. 15:12Z, mit Bild: "es macht
                 * keinen sinn das unter dem absenden noch was steht ... das ist echt
                 * inkonsistent"). Sein Knopf soll das Ende der Buehne sein.
                 */
                const angebot = angeboteneAntwortwege(offeneBuehne,{entscheiden:offeneBuehne.zugang.entscheiden,wartend:wartendSichtbar,richtungBedienbar:false});
                const offen = offeneBuehne.entscheidungen.filter(e=>angebot.some(a=>a.art==='entscheidung'&&a.id===e.id));
                const erledigt = offeneBuehne.entscheidungen.filter(e => !istOffeneFrage(e));
                return (
                  <>
                    <ErledigteAnmerkung erledigt={erledigt} />
                    {offen.length > 1
                      ? (
                        <EntscheidungenListe
                          buehne={offeneBuehne}
                          offene={offen}
                          onFehler={setFehler}
                          onNachladen={() => sofortWeiter(offeneBuehne.id)}
                        />
                      )
                      : offen.map(e => (
                        <EntscheidungsBlock
                          key={e.id}
                          buehne={offeneBuehne}
                          e={e}
                          onFehler={setFehler}
                          onNachladen={() => sofortWeiter(offeneBuehne.id)}
                        />
                      ))}
                  </>
                );
              })()
              : offeneBuehne.offen.entscheidungen > 0 && (
                <div data-ai-id="guide-buehne-entscheiden-gesperrt"
                  style={{ ...box, marginBottom: 8, fontSize: 11, color: 'var(--tn-text-muted)' }}>
                  {offeneBuehne.offen.entscheidungen} offene Frage(n) — sie werden dir vorgelegt,
                  sobald dieser Berater vor dir steht. Hier wuerde deine Antwort auf einem Stand
                  landen, den es dann nicht mehr gibt.
                </div>
              )}

            {/*
              VORSCHLAEGE — EIGENER BEREICH, GETRENNT VON ECHTEN ENTSCHEIDUNGEN.
              Bewusst UNTER den Entscheidungen und UEBER dem freien Wort (das die
              Zustellungs-/Fehlerzeilen traegt, siehe `FreiesWort`) — beide Bereiche
              stehen nebeneinander, keiner verdraengt den anderen.
            */}
            {(() => {
              const angebot = angeboteneAntwortwege(offeneBuehne,{entscheiden:offeneBuehne.zugang.entscheiden,wartend:wartendSichtbar,richtungBedienbar:false});
              const offeneVorschlaege = (offeneBuehne.vorschlaege ?? []).filter(e=>angebot.some(a=>a.art==='vorschlag'&&a.id===e.id));
              if (!offeneVorschlaege.length || wartendSichtbar) return null;
              return (
                <VorschlaegeListe
                  buehne={offeneBuehne}
                  offene={offeneVorschlaege}
                  onFehler={setFehler}
                />
              );
            })()}

            {/*
              DAS FELD, DAS GEFEHLT HAT.

              Rafael hat am 11.09. die offene Buehne fotografiert: sie sagte "Keine
              Entscheidung offen", und darunter war nichts. Er stand vor einem Berater
              und konnte ihn nicht ansprechen. Sein Wort: "Ich muss auf jeder Buehne
              eine Antwort geben koennen. Auch wenn keine Frage da steht, muss ich
              zumindest eine Nachricht verschicken koennen."

              Deshalb steht es IMMER da — unter den Optionen, wenn es welche gibt, und
              allein, wenn nicht. Es fuellt zugleich die Flaeche, die ihm leer
              aufgefallen ist.
            */}
            <GuideAuftrag buehne={offeneBuehne} />
            <FreiesWort key={`wort:${offeneBuehne.id}`} buehne={offeneBuehne} onFehler={setFehler}
              onGesendet={() => fragenAnDich(offeneBuehne) > 0
                ? sofortWeiter(offeneBuehne.id, 'nachricht')
                : void buehneNachladen(offeneBuehne.id, offeneBuehne.raum)}
              onNachladen={() => void buehneNachladen(offeneBuehne.id, offeneBuehne.raum)} />
            </AbsendeRegister>
          </div>
        )) : null}
        {/* Die Hofansicht bleibt auch im Assistent-Modus stehen (Rafael, 24.10.2026: „oben die gleiche Ansicht“); versteckt wird sie nur fuer Verlauf oder eine offene Bühne. */}
        {queueEnde && !offeneBuehne && !historieOffen && (
          <div data-ai-id="guide-queue-ende" role="status" style={{ ...box, marginBottom: 8, padding: 14, textAlign: 'center' }}>
            <b style={{ fontSize: 14 }}>Keine weiteren Bühnen für heute</b>
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', margin: '4px 0 8px' }}>Die Warteschlange ist durch. Der Rest steht unten in der Übersicht.</div>
            <button type="button" data-ai-id="guide-queue-ende-schliessen" onClick={() => setQueueEnde(false)}>Zur Übersicht</button>
          </div>
        )}
        <div data-ai-id="guide-hofansicht-huelle" hidden={hofansichtVersteckt({ historieOffen, offeneBuehne: !!offeneBuehne })}>
          {/* Nur fuer Partner: was der Guide ist und warum er gut ist (Rafael, 23.09.2026).
              Rafael selbst kennt ihn — seine Sicht bleibt ohne Karte. */}
          {!istBetreiber && user?.id && <GuideErklaerkarte userId={user.id} onGesehen={() => setGuideAnsichtMerk(user.id, { hinweisGesehen: true })} />}
          <Hofansicht onBestand={merkeBestand} mehrZiel={mehrZiel} onWarnung={setHofWarnung} statusInfo={board ? <AktiveKonten liste={board.konten ?? []} /> : undefined} standZeile={board ? <StandDerHaeuser stand={board.staende_haeuser} /> : undefined} onOeffnen={hofBuehneOeffnen} offeneId={offeneBuehne?.id ?? null} aemter={board?.konfig.aemter ?? null} onOeffnenOhneBerater={sitzungOhneBeraterOeffnen} />
        </div>

        {sitzungsBuehne && <div data-ai-id="guide-im-gespraech" style={{ ...box, fontSize: 12 }}>
          Im Gespräch · {sitzungsBuehne.name}. Rechts steht sein Chat.
        </div>}

        {/*
          HIER STAND DIE SITZUNGSLISTE — und sie ist raus (Rafael, 11.09.2026).

          Sie war mein Fehlschluss: ich habe Rafaels Regel "ein Klick zu jeder lebenden
          Sitzung" beim Wort genommen und die Sitzungen als zweite Liste unter die
          Raeume gesetzt. Sein Einwand beim ersten Blick darauf: *"die Sachen haben
          keinen Raum oder wie?"* — richtig, denn es sind keine Berater. Ein Chat ist im
          Hofstaat kein Berater; ein Berater IST eine Buehne, ein Chat nur der, der
          gerade davor steht. Eine Liste ohne Raum neben den Raeumen war wieder die
          zweite Welt, gegen die dieser ganze Umbau gebaut ist — eine Ebene tiefer.

          Sein Satz, der es entscheidet: *"de facto interessieren mich die Chats ja gar
          nicht mehr — die muessen eh alle mit Buehnen in Verbindung stehen. Wenn nicht,
          muss der Assistent erinnert werden, sie einzuordnen."*

          Damit ist die Erreichbarkeit einer Sitzung kein eigener Weg mehr, sondern eine
          Eigenschaft ihres Beraters. Was bleibt, ist die Gegenprobe: eine Zeile im Hof
          zaehlt die Sitzungen OHNE Berater (`OhneBerater` in GuideHof.tsx). Ob ein
          Prozess gerade laeuft, steht nirgends mehr — das ist Maschinenzustand.
        */}

        {/*
          Beide blenden sich selbst aus, wenn sie nichts zu sagen haben — siehe die
          Begruendung oben. "Heute fuer dich entschieden" ist Rafaels Einspruchsweg
          gegen Stufe-1-Entscheidungen, "Zustellung unbestaetigt" sind Antworten, die
          gespeichert, aber nie bestaetigt wurden.
        */}
        <HeuteEntschieden liste={board?.selbst_entschieden ?? []} onNeuladen={neuladen} />
        {/* „Zustellung unbestätigt" (NieAngekommen) steht nicht mehr in Rafaels Ansicht (18.10.2026):
            liegengebliebene Zustellungen sind Sache des Hofs, abrufbar unter GET /guide/zustellung/offen. */}
      </div>

      {/*
        DER RUECKWEG — als Fussleiste, nicht als Kasten im Lesefluss.

        Rafael, 06.09.: die "zurueck zu meinem Layout"-Knoepfe gehoeren nicht in die
        Guide-Spalte. Sie standen dort als drei breite Schaltflaechen ueber dem, was
        er eigentlich lesen wollte. Weg duerfen sie trotzdem nicht: ein Umbau, den er
        nicht rueckgaengig machen kann, waere schlimmer als das Durcheinander, gegen
        das er hilft. Also klein, unten, einzeilig — erreichbar, aber nicht im Weg.
      */}
      {/*
        UEBER DEM RUECKWEG, AUS DEMSELBEN GRUND: klein, unten, einzeilig — erreichbar,
        aber nicht im Weg. Steht bewusst AUSSERHALB der Verzweigung oben (offene Buehne
        ODER Hof): ein Einfall kommt nicht danach, ob gerade ein Berater vor ihm steht.
      */}
      {Object.keys(sicherungen).length > 0 && (
        <div style={{
          flexShrink: 0, borderTop: '1px solid var(--tn-border)', padding: '4px 10px',
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
          fontSize: 11, color: 'var(--tn-text-muted)',
        }}>
          <span>↩ mein Layout:</span>
          {Object.keys(sicherungen).map(pid => (
            <button
              key={pid}
              data-ai-id={`guide-layout-zurueck-${pid}`}
              onClick={() => zurueckstellen(pid)}
              style={{
                background: 'transparent', border: 0, padding: 0, fontSize: 11,
                color: 'var(--tn-blue)', cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              {pid}
            </button>
          ))}
          {ausDemBlick && (
            <span title="Beendet wurde nichts — die Chats laufen weiter, sie sind nur nicht eingeblendet.">
              · {ausDemBlick.anzahl} Chat(s) aus dem Blick ({ausDemBlick.laufen} laufen)
            </span>
          )}
        </div>
      )}
      {/*
        DER INFO-CHAT IST WEG (Rafael, 06.09., 12:41Z: "Der Info-Chat ist unten noch
        da (eingeklappt) — er soll ganz raus.").

        Er stand hier als zweiter Spaltenchat neben dem Koordinator. Was er zeigen
        sollte, zeigt jetzt der Fliesstext oben und "Gerade in Arbeit"; der eine Chat,
        in dem Rafael wirklich weiterarbeitet, haengt unter dem Guide (App.tsx).
        Untenherum bleibt nur noch der Rueckweg.
      */}
    </div>
  );
}



/**
 * „Fuer Buehnen freigegeben" — Rafaels Uebersicht der freigegebenen Konten.
 *
 * DER NAME IST WICHTIG: die Liste sagt, welche Konten eine Buehne anmelden DARF,
 * nicht welche gerade angemeldet SIND. Sie hiess bis 08.09. „Gerade angemeldet"
 * und wurde genau so missverstanden — Rafael sah drei Kacheln auf der
 * Login-Maske und darueber die Zeile, er sei mit all diesen Konten angemeldet.
 * Nichts an dieser Liste misst einen Anmeldezustand; sie ist eine Erlaubnis.
 *
 * Er am 08.09. 04:44Z: "Ich habe oben dort, wo Thema, Guide oder was auch immer
 * steht, eine Uebersicht, welche grad verfuegbar sind. Dann kann ich sagen: Zum
 * Beispiel gibt es grad drei aktive User und die koennen die ganzen Apps dann
 * auch autonom steuern, komplett."
 *
 * Die Liste ist zugleich das Tor: nur was hier steht, meldet eine Buehne an. Sie
 * wird deshalb auch dann gezeigt, wenn sie LEER ist — eine leere Liste heisst,
 * dass gerade keine Buehne eine App zeigen kann, und das soll man sehen und
 * nicht raten. Produktions-Konten sind hervorgehoben: dort wirft die Anmeldung
 * einen echten Kunden aus seiner Sitzung.
 */
const LS_KONTEN_OFFEN = 'guide-konten-offen';

function AktiveKonten({ liste }: { liste: AktivesKonto[] }) {
  /*
   * ZUGEKLAPPT PER DEFAULT (Rafael, 09.09. 14:5xZ, mit Bild: "da muss ich staendig
   * scrollen"). Die Liste steht im FESTEN Kopf, der nie wegscrollt — zehn Zeilen
   * hier sind zehn Zeilen weniger fuer alles darunter, bei jedem Blick. Zugeklappt
   * bleibt die Auskunft, die der Kopf tragen soll (wie viele Konten, wie viele
   * davon Produktion); die Namen stehen im Hover-Titel und einen Klick entfernt.
   */
  const [offen, setOffen] = useState(() => {
    try { return localStorage.getItem(LS_KONTEN_OFFEN) === '1'; } catch { return false; }
  });
  const umschalten = () => {
    setOffen(o => {
      try { localStorage.setItem(LS_KONTEN_OFFEN, o ? '0' : '1'); } catch { /* egal */ }
      return !o;
    });
  };
  const produktion = liste.filter(k => k.umgebung === 'produktion').length;
  const staging = liste.length - produktion;
  const namen = liste.map(k => `${k.label}${k.app ? ` · ${k.app}` : ''} · ${k.umgebung === 'produktion' ? 'Produktion' : 'Staging'}`).join('\n');

  return (
    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--tn-border)' }}>
      <button
        data-ai-id="guide-konten-umschalten"
        onClick={umschalten}
        title={liste.length ? `${offen ? 'Liste zuklappen' : 'Liste aufklappen'}\n${namen}` : 'Kein Konto freigegeben'}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 11, fontWeight: 600, color: 'var(--tn-text)',
        }}
      >
        <span style={{ color: 'var(--tn-text-muted)', fontSize: 9 }}>{offen ? '▼' : '▶'}</span>
        <span>Fuer Buehnen freigegeben</span>
        <span style={{ fontWeight: 400, color: 'var(--tn-text-muted)' }}>
          ({liste.length})
          {liste.length > 0 && ` · ${staging} Staging`}
          {produktion > 0 && <span style={{ color: 'var(--tn-orange)' }}> · {produktion} Produktion</span>}
        </span>
      </button>
      {liste.length === 0 && (
        <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', paddingLeft: 15 }}>
          Kein Konto freigegeben — Bühnen können gerade keine App zeigen.
        </div>
      )}
      {offen && liste.map(k => (
        <div key={k.kuerzel} title={`${k.email}${k.hinweis ? ` — ${k.hinweis}` : ''}\nCookie-Box: ${k.box}`}
          style={{ fontSize: 10, color: 'var(--tn-text-muted)', lineHeight: 1.5, paddingLeft: 15 }}>
          <span style={{ color: 'var(--tn-text)' }}>{k.label}</span>
          {k.app ? ` · ${k.app}` : ''}
          {k.umgebung === 'produktion'
            ? <span style={{ color: 'var(--tn-orange)' }}> · Produktion</span>
            : ' · Staging'}
        </div>
      ))}
    </div>
  );
}

/**
 * „Welches Haus ist aktuell" — gebaut und gestartet, beide Haeuser nebeneinander.
 *
 * Rafael, 12.09.2026 06:25Z: "bau auch in den guide eine uebersicht ein wann dev und
 * wann partner guide das letzte mal das cui neu gebaut und gestartet wurde — damit ich
 * weiss welcher aktuell ist."
 *
 * DER LETZTE HALBSATZ IST DIE ANZEIGE. Zwei Uhrzeiten hinzuschreiben und ihn selbst
 * rechnen zu lassen, waere die Arbeit, die er gerade abgegeben hat — die eingeklappte
 * Zeile sagt deshalb nicht "06:20 / 03:41", sondern "Partner 2 h 39 min zurueck".
 * Verglichen wird GEBAUT: das ist die Oberflaeche, die er vor sich hat.
 *
 * WARUM DER START TROTZDEM DASTEHT. Er ist der Wert, der taeuscht. Am 12.09. hatte Haus
 * Partner um 06:04:40Z neu gestartet und trug dabei ein Buendel von 03:41:26Z — wer nur
 * die Startzeit sieht, haelt so einen Server fuer frisch. Deshalb steht bei einem Start,
 * der deutlich nach dem Buendel liegt, ausdruecklich daneben, dass dieser Neustart keine
 * neue Oberflaeche gebracht hat. (Der gesunde Fall sind Sekunden: gebaut 06:34:08,
 * gestartet 06:34:16 — darunter meldet die Zeile nichts.)
 *
 * NICHTS WIRD BESCHOENIGT. Ist der Nachbar nicht lesbar, steht das im Kopf und der
 * Wortlaut des Fehlers in der Zeile; "gleichauf" wird dann NICHT behauptet. Fehlt das
 * Feld ganz (aeltere Server-Fassung), verschwindet die Anzeige, statt Nullwerte zu
 * zeigen — eine Anzeige, die still "alles gut" sagt, obwohl sie nichts erreicht hat,
 * waere schlimmer als keine.
 *
 * ZUSTAND MIT ID: der Auf/Zu-Schalter merkt sich seine Stellung in `localStorage`,
 * genau wie die Kontenliste darueber.
 */
const LS_STAENDE_OFFEN = 'guide-staende-offen';

/** "2 h 39 min", "14 min", "3 Tage" — kurz genug fuer eine Kopfzeile. */
function dauerWort(minuten: number): string {
  const m = Math.max(0, Math.round(minuten));
  if (m < 60) return `${m} min`;
  if (m < 60 * 48) {
    const std = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? `${std} h ${rest} min` : `${std} h`;
  }
  return `${Math.floor(m / 1440)} Tage`;
}

/** Dieselbe Dauer fuer das kleine ⚠-Chip im Guide-Kopf: nur die groesste Einheit, ohne
 *  Leerzeichen ("12h", "30m") — damit der Kopf auch bei ~460 px Spalte in einer Zeile bleibt. */
export function dauerKurz(minuten: number): string {
  const m = Math.max(0, Math.round(minuten));
  if (m < 60) return `${m}m`;
  if (m < 60 * 48) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}T`;
}

/** Uhrzeit plus Abstand zu jetzt — beides, weil die eine Zahl ohne die andere nichts sagt. */
function zeitWort(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const uhr = new Date(t).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
  return `${uhr} (vor ${dauerWort((Date.now() - t) / 60000)})`;
}

const HAUS_WORT: Record<'dev' | 'partner', string> = { dev: 'Dev', partner: 'Partner' };

function StandDerHaeuser({ stand }: { stand?: StaendeHaeuser }) {
  const [offen, setOffen] = useState(() => {
    try { return localStorage.getItem(LS_STAENDE_OFFEN) === '1'; } catch { return false; }
  });
  const umschalten = () => {
    setOffen(o => {
      try { localStorage.setItem(LS_STAENDE_OFFEN, o ? '0' : '1'); } catch { /* egal */ }
      return !o;
    });
  };

  // Alte Server-Fassung liefert das Feld nicht. Dann gar nichts zeigen — erfundene
  // Nullwerte waeren hier die schaedlichste aller Anzeigen.
  if (!stand) return null;

  const alarm = 'var(--tn-red)';
  const warnung = 'var(--tn-orange)';
  const ruhig = 'var(--tn-text-muted)';

  /* Die eine Zeile, die auch zugeklappt dasteht — sie muss die Frage allein beantworten. */
  let kopf: string;
  let kopfFarbe: string;
  if (stand.unklar) {
    kopf = `nicht vergleichbar — ${stand.unklar}`;
    kopfFarbe = alarm;
  } else if (stand.zurueck) {
    // Dieselbe Aussage wie die Kopfmarke: verglichen wird die Bauzeit der Oberflaeche, nicht der Code (Zweitpruefung R1 N2).
    kopf = `${HAUS_WORT[stand.zurueck]}-Oberfläche älter: ${dauerWort(stand.abstand_minuten ?? 0)} früher gebaut (Bauzeit, nicht Code-Abstand)`;
    kopfFarbe = warnung;
  } else if (stand.gleicher_commit === true && stand.commit_aussagekraeftig) {
    // Beide Prozesse auf demselben Commit. Die Bauzeiten duerfen dabei um Minuten
    // auseinanderliegen — jedes Haus baut selbst (gemessen 12.09.: gleicher Commit,
    // Buendel-Hashes B-IOgrt5 vs Blj-psqM). Das ist kein Rueckstand und heisst hier auch
    // nicht so.
    kopf = 'beide auf demselben Stand';
    kopfFarbe = ruhig;
  } else {
    kopf = 'gleichauf';
    kopfFarbe = ruhig;
  }

  return (
    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--tn-border)' }}>
      <button
        data-ai-id="guide-staende-umschalten"
        onClick={umschalten}
        title={'Wann wurde das CUI zuletzt GEBAUT (die Oberfläche) und wann GESTARTET (der Server) —'
          + ' je Haus. Verglichen wird das Bündel: der Start allein sagt nichts über die Aktualität.'}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 11, fontWeight: 600, color: 'var(--tn-text)',
        }}
      >
        <span style={{ color: ruhig, fontSize: 9 }}>{offen ? '▼' : '▶'}</span>
        <span>CUI-Stand</span>
        <span style={{ fontWeight: 400, color: kopfFarbe }}>{kopf}</span>
      </button>
      {offen && stand.haeuser.map(h => (
        <div key={h.haus} style={{ fontSize: 10, color: ruhig, lineHeight: 1.5, paddingLeft: 15 }}>
          <span style={{ color: 'var(--tn-text)', fontWeight: h.haus === stand.zurueck ? 400 : 600 }}>
            {HAUS_WORT[h.haus]}
          </span>
          {h.hier && <span title="Dieser Guide läuft in diesem Haus."> · hier</span>}
          {h.fehler
            ? <span style={{ color: alarm }}> · {h.fehler}</span>
            : (
              <>
                <span> · gebaut {zeitWort(h.gebaut)}</span>
                <span> · gestartet {zeitWort(h.gestartet)}</span>
                {h.commit && (
                  <span title={'Commit, mit dem der SERVERPROZESS gestartet ist — er friert dort ein.'
                    + ' Ein Neubau der Oberfläche ohne Neustart bewegt ihn nicht; er sagt deshalb nicht,'
                    + ' was ausgeliefert ist, sondern nur, ob beide Häuser vom selben Stand gestartet sind.'}>
                    {' · '}<code>{h.commit.slice(0, 7)}</code>
                  </span>
                )}
              </>
            )}
          {h.start_ohne_neues_buendel_minuten !== null && (
            <div style={{ color: warnung, paddingLeft: 8 }}>
              ⚠ {dauerWort(h.start_ohne_neues_buendel_minuten)} nach dem Bündel gestartet — dieser
              Neustart hat keine neue Oberfläche gebracht.
            </div>
          )}
        </div>
      ))}
      {offen && (
        <div style={{ fontSize: 9, color: ruhig, paddingLeft: 15, marginTop: 2 }}>
          gemessen {zeitWort(stand.gemessen_am)} · verglichen wird „gebaut", nicht „gestartet"
          {stand.abstand_minuten !== null && stand.zurueck === null && stand.abstand_minuten >= 2
            && ` · ${dauerWort(stand.abstand_minuten)} Bauzeit-Unterschied, aber kein Rückstand`}
          {/*
            WARUM DAS URTEIL SO LAUTET — sonst sieht ein Rollout-Fenster wie ein Fehler aus.
            Läuft ein Haus mit einem älteren Bündel an (Neustart, Bau läuft noch), beschreibt
            sein Commit den Prozess und nicht die Oberfläche; dann trägt allein die Bauzeit.
          */}
          {!stand.commit_aussagekraeftig && !stand.unklar
            && ' · Commit taugt hier nicht als Gegenprobe: ein Haus lief mit älterem Bündel an'}
        </div>
      )}
    </div>
  );
}

/**
 * Schalter Fable/Opus fuer den Coach — PERSISTENT.
 *
 * Rafael, 09.09. 14:5xZ: "mach da fuer die coaches einen button rein mit dem ich
 * persistent wechseln kann zwischen opus und fable — weil sonst beim respawnen des
 * coaches immer wieder auf fable gewechselt wird". Der Klick schreibt zwei Dinge:
 * konfig.coachModell (das liest coach-wechsel.sh beim naechsten Neuaufsetzen) und
 * das Modell der LAUFENDEN Coach-Sitzung (greift bei ihrem naechsten Aufwachen).
 * Scheitert eines von beiden, steht es hier — ein stiller Schalter, der nur die
 * Haelfte tut, waere genau der Fehler, den er beschreibt.
 */
function CoachModellSchalter({ modell, coachSessionId, onNeuladen }: {
  modell: CoachModell; coachSessionId: string | null; onNeuladen: () => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const setzen = async (neu: CoachModell) => {
    if (neu === modell || laeuft) return;
    setLaeuft(true); setFehler(null);
    try {
      const r = await fetch(`${API}/guide/konfig`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coachModell: neu }),
      });
      if (!r.ok) throw new Error(`Konfig: ${r.status} ${await r.text()}`);
      if (coachSessionId) {
        const m = await fetch(`${API}/mission/model/${encodeURIComponent(coachSessionId)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ model: neu }),
        });
        if (!m.ok) throw new Error(`laufender Coach: ${m.status} ${await m.text()}`);
      }
      onNeuladen();
    } catch (e) {
      setFehler(String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const knopf = (m: CoachModell, text: string) => (
    <button
      data-ai-id={`guide-coach-modell-${m}`}
      onClick={() => setzen(m)}
      disabled={laeuft}
      title={m === modell
        ? `Der Coach laeuft mit ${text}; auch der naechste Coach startet damit.`
        : `Coach dauerhaft auf ${text} stellen — gilt fuer den laufenden Coach ab seiner naechsten Runde und fuer jeden neu aufgesetzten.`}
      style={{
        background: m === modell ? 'var(--tn-bg-highlight, rgba(122,162,247,0.18))' : 'none',
        border: '1px solid var(--tn-border)', borderRadius: 3, padding: '0 6px',
        cursor: m === modell ? 'default' : 'pointer', fontSize: 10,
        color: m === modell ? 'var(--tn-blue)' : 'var(--tn-text-muted)', fontWeight: m === modell ? 700 : 400,
      }}
    >
      {text}
    </button>
  );

  return (
    <span data-ai-id="guide-coach-modell" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8 }}>
      <span style={{ fontSize: 10, color: 'var(--tn-text-muted)' }}>Coach:</span>
      {knopf('fable', 'Fable')}
      {knopf('opus', 'Opus')}
      {fehler && <span title={fehler} style={{ fontSize: 10, color: 'var(--tn-red)' }}>Umstellen fehlgeschlagen</span>}
    </span>
  );
}

/** localStorage-Schluessel fuer HeuteEntschieden — Sichtbarkeit + letzter Blick. */
const LS_HEUTE_OFFEN = 'guide-heute-entschieden-offen';
const LS_HEUTE_BLICK = 'guide-heute-entschieden-letzter-blick';

/**
 * „Heute für dich entschieden" — Stufe 1.
 *
 * Rafael, 06.09.: "Die Sachen, die sich nicht auszahlen, dass ich sie anschaue, soll
 * er selbstaendig beschliessen." Dieser Abschnitt ist die andere Haelfte davon. Ohne
 * ihn waere „selbst entschieden" keine Entlastung, sondern etwas, das hinter seinem
 * Ruecken passiert — er hat das Entscheiden abgegeben, nicht das Wissen darueber.
 *
 * Bewusst eine LISTE zum Ueberfliegen, keine zweite Schlange: Titel, ein Satz warum,
 * wer und wann. Wer mehr wissen will, widerspricht — und dann wird die Karte wieder
 * zu einer normalen offenen Entscheidung.
 *
 * Der Einspruch fragt NICHT nach einer Begruendung. Rafael muss nicht rechtfertigen,
 * dass er etwas selbst sehen will; eine Pflichtnotiz waere eine Huerde vor genau dem
 * Recht, das dieser Abschnitt einloest.
 *
 * ZUGEKLAPPT PER DEFAULT (Rafael, 09.09.: "muss ich da immer ewig scrollen" bei 104
 * Eintraegen). Aufgeklappt zeigt sie zuerst nur Titel je Zeile, nach Herkunft
 * gruppiert; das "weil" steht als Hover-Titel UND laesst sich je Zeile aufklappen.
 * Der Knopf "das will ich sehen" bleibt an JEDER Zeile sichtbar, zugeklappt wie
 * aufgeklappt — das ist Rafaels Einspruchsrecht und wird nicht hinter einen
 * zweiten Klick versteckt (Kommentar oben, Rafael 06.09.).
 */
function HeuteEntschieden({ liste, onNeuladen }: { liste: SelbstEntschieden[]; onNeuladen: () => void }) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [offen, setOffen] = useState(() => {
    try { return localStorage.getItem(LS_HEUTE_OFFEN) === '1'; } catch { return false; }
  });
  // Der Blick-Zeitpunkt wird NUR beim Aufklappen fortgeschrieben — sonst waere
  // "seit deinem letzten Blick" schon beim Rendern der zugeklappten Zeile verbraucht.
  const [letzterBlick, setLetzterBlick] = useState<string | null>(() => {
    try { return localStorage.getItem(LS_HEUTE_BLICK); } catch { return null; }
  });
  const [ausgeklappt, setAusgeklappt] = useState<Set<string>>(() => new Set());

  if (!liste.length) return null;

  const neuSeitBlick = letzterBlick ? liste.filter(e => e.am > letzterBlick).length : liste.length;

  const umschalten = () => {
    const naechsterZustand = !offen;
    setOffen(naechsterZustand);
    try { localStorage.setItem(LS_HEUTE_OFFEN, naechsterZustand ? '1' : '0'); } catch { /* egal */ }
    if (naechsterZustand) {
      const jetzt = new Date().toISOString();
      try { localStorage.setItem(LS_HEUTE_BLICK, jetzt); } catch { /* egal */ }
      setLetzterBlick(jetzt);
    }
  };

  const zeileUmschalten = (id: string) => {
    setAusgeklappt(prev => {
      const kopie = new Set(prev);
      if (kopie.has(id)) kopie.delete(id); else kopie.add(id);
      return kopie;
    });
  };

  const widersprechen = async (id: string) => {
    setLaeuft(id);
    try {
      await fetch(`${API}/guide/karten/${id}/einspruch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      onNeuladen();
    } finally {
      setLaeuft(null);
    }
  };

  // Gruppieren nach Herkunft (`von`) — Reihenfolge der Gruppen folgt der schon vom
  // Server nach `am` absteigend sortierten Liste, also neueste Herkunft zuerst.
  const gruppen: { von: string; eintraege: SelbstEntschieden[] }[] = [];
  const indexVon = new Map<string, number>();
  for (const e of liste) {
    const schluessel = e.von ?? 'Ohne Herkunft';
    let idx = indexVon.get(schluessel);
    if (idx === undefined) {
      idx = gruppen.length;
      indexVon.set(schluessel, idx);
      gruppen.push({ von: schluessel, eintraege: [] });
    }
    gruppen[idx].eintraege.push(e);
  }

  const zeile = (e: SelbstEntschieden) => {
    const istNeu = letzterBlick ? e.am > letzterBlick : false;
    const istOffen = ausgeklappt.has(e.id);
    return (
      <div key={e.id} style={{ padding: '3px 0' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12 }}>
          <span
            onClick={() => zeileUmschalten(e.id)}
            title={`${e.weil}${e.von ? ` (${e.von})` : ''}`}
            style={{
              flex: 1, lineHeight: 1.4, cursor: 'pointer',
              color: istNeu ? 'var(--tn-text)' : 'var(--tn-text-muted)',
            }}
          >
            {istOffen ? '▾' : '▸'} {e.titel}
          </span>
          {e.einspruch ? (
            <span style={{ fontSize: 10, color: 'var(--tn-orange)', whiteSpace: 'nowrap' }}>steht wieder offen</span>
          ) : (
            <button
              data-ai-id={`guide-einspruch-${e.id}`}
              onClick={() => widersprechen(e.id)}
              disabled={laeuft === e.id}
              style={{
                background: 'none', border: '1px solid var(--tn-border)', borderRadius: 4,
                padding: '1px 7px', fontSize: 10, color: 'var(--tn-text-muted)',
                cursor: laeuft === e.id ? 'default' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              das will ich sehen
            </button>
          )}
        </div>
        {istOffen && (
          <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', paddingLeft: 14, lineHeight: 1.4 }}>
            {e.weil}{e.von ? ` (${e.von})` : ''}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-ai-id="guide-selbst-entschieden" style={{ marginTop: 12, paddingTop: 8, borderTop: '1px dashed var(--tn-border)' }}>
      <button
        data-ai-id="guide-selbst-entschieden-umschalten"
        onClick={umschalten}
        style={{
          background: 'none', border: 'none', padding: 0, width: '100%',
          display: 'flex', alignItems: 'baseline', gap: 6, cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-text)' }}>{offen ? '▾' : '▸'} Heute für dich entschieden</span>
        <span style={{ fontSize: 11, color: 'var(--tn-text-muted)' }}>
          ({liste.length}){!offen && neuSeitBlick > 0 ? ` · ${neuSeitBlick} seit deinem letzten Blick` : ''}
        </span>
      </button>
      {offen && (
        <div style={{ marginTop: 6 }}>
          {gruppen.map(g => (
            <div key={g.von} style={{ marginBottom: 6 }}>
              {gruppen.length > 1 && (
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tn-text-muted)', margin: '4px 0 2px' }}>
                  {g.von} <span style={{ fontWeight: 400 }}>({g.eintraege.length})</span>
                </div>
              )}
              {g.eintraege.map(zeile)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/*
 * WAS EINE ANTWORT BEDEUTET — die Kombinationen, die der Server kennt.
 *
 * (Die Vorgeschichte betraf die Handlungs-LISTE der Themenansicht, die es seit dem
 * Spalten-Umbau vom 11.09.2026 nicht mehr gibt. Die Bedeutungstabelle unten gilt
 * unveraendert weiter — sie beschreibt, was der Server aus einer Antwort macht, und
 * `KartenBlock` schickt genau diese Formen.)
 *
 * Rafael, 10.09.2026, zum Bild der
 * Vertrieb-&-Marketing-Buehne: "hier fehlt mir ueberall die Moeglichkeit, eine
 * andere Anmerkung zu geben ... es muss bei jeder einzelnen Frage auch moeglich
 * sein, offene Antworten zu geben"). Bis hierher gab es je Frage nur eine der
 * vorgegebenen Optionen, "spaeter" oder "weiss nicht" — wer keine der drei
 * meinte, hatte auf dieser Buehne keine Stimme. Die einzeln ausgeschriebene
 * Karte hatte ihr Textfeld die ganze Zeit; die Liste hat es nie bekommen.
 *
 * Der Text steht deshalb NEBEN der Wahl, nicht statt ihr, und behaelt in jeder
 * Kombination die Bedeutung, die der Server ihr ohnehin schon gibt:
 *   Option + Text      -> Entscheidung mit Notiz (die Karte schliesst ab),
 *   nur Text           -> Auflage ohne Kuerzel (die Karte bleibt offen),
 *   spaeter + Text     -> sein Grund statt des Platzhalters,
 *   weiss nicht + Text -> was an der Frage nicht getragen hat.
 */
type Auswahl =
  | { art: 'option'; kuerzel: string; notiz?: string }
  | { art: 'spaeter'; text?: string }
  | { art: 'unklar'; text?: string }
  | { art: 'freitext'; text: string };


/** Eine Sitzung als Zeile: Titel, Zustand, und ein Klick, der sie oeffnet. */
function SitzungsZeile({ sz, onOeffnen, onHebel, imAnderenHaus, onFehler }: {
  sz: Sitzung;
  onOeffnen: (p: string, s: string, a: string) => void;
  /*
   * Anhalten und Schliessen — OPTIONAL, damit die Zeile an den Stellen unveraendert
   * bleibt, wo Rafael nur hineinsehen soll. Die Hebel liegen NEBEN dem Zeilen-Knopf,
   * nicht darin: ein Knopf im Knopf ist ungueltiges HTML, und der Klick landete dann
   * je nach Browser irgendwo — genau die Sorte Fehler, die man erst beim Nutzer sieht.
   */
  onHebel?: (art: 'anhalten' | 'schliessen', sz: Sitzung) => void;
  /**
   * Gesetzt, wenn die Karte zu dieser Zeile im ANDEREN Haus lebt. Dann sagt
   * `gefunden: false` nichts ueber die Sitzung aus — sie steht nur in DIESER
   * Konversationsliste nicht, weil sie drueben laeuft.
   */
  imAnderenHaus?: string | null;
  /**
   * OPTIONAL, aber wenn gesetzt: der Klick verpufft nicht mehr stumm, wenn
   * `projectId`/`accountId` fehlen (nur lebende Sitzungen tragen sie). Befund
   * 11.09.2026 — genau dieselbe Handschrift wie der 403 in `wirklichRafael`: ein
   * abgewiesener Klick, der nichts sagt, sieht fuer Rafael wie "kaputt" aus.
   */
  onFehler?: (f: string | null) => void;
}) {
  if (!sz.gefunden) {
    /*
     * Ehrliche Fehlanzeige statt totem Knopf — aber die ehrliche Fehlanzeige ist
     * eine andere, wenn die Karte drueben lebt. "Vermutlich beendet" stand am
     * 09.09.2026 19:2xZ im Bild direkt ueber dem Satz "laeuft auf dem
     * Partner-Server"; zwei Zeilen uebereinander, die einander widersprachen.
     */
    if (imAnderenHaus) {
      const drueben = imAnderenHaus === 'partner' ? 'Partner-Server' : 'Dev-Server';
      return (
        <div style={{ ...box, fontSize: 10, color: 'var(--tn-text-muted)', marginBottom: 4 }}>
          Sitzung {sz.sessionId.slice(0, 8)} läuft auf dem {drueben} — von hier nicht einsehbar.
        </div>
      );
    }
    return (
      <div style={{ ...box, fontSize: 10, color: 'var(--tn-orange)', marginBottom: 4 }}>
        Sitzung {sz.sessionId.slice(0, 8)} ist in keiner Konversationsliste mehr — vermutlich beendet.
      </div>
    );
  }
  const zeile = (
    <button
      data-ai-id={`guide-sitzung-${sz.sessionId.slice(0, 8)}`}
      onClick={() => {
        if (!sz.projectId || !sz.accountId) {
          onFehler?.(
            `„${sz.titel || sz.sessionId.slice(0, 8)}" laesst sich nicht oeffnen — `
            + `Arbeitsbereich oder Konto fehlen (nur lebende Sitzungen tragen sie).`,
          );
          return;
        }
        // Auch hier synchron: der Sprung in einen Chat baut den Arbeitsbereich um.
        vorhangAn(`Chat wird geöffnet: ${sz.titel || sz.sessionId.slice(0, 8)}`);
        onOeffnen(sz.projectId, sz.sessionId, sz.accountId);
        vorhangAus();
      }}
      style={{ ...box, width: '100%', textAlign: 'left', cursor: 'pointer', marginBottom: 4 }}
    >
      <div style={{ fontSize: 11, color: 'var(--tn-text)' }}>
        {sz.rolle === 'wartet' ? '⏳ ' : sz.taetigkeit ? (sz.taetigkeit.art === 'arbeitet' ? '● ' : '○ ') : '● '}
        {sz.betreff || sz.titel || sz.sessionId.slice(0, 8)}
      </div>
      {/*
        BETREFF UND EIN SATZ — mehr nicht.

        Rafael, 06.09. 15:2xZ: unter dem Report-Thema stand als "Stand" die letzte
        Assistenten-Nachricht ("Geprueft, und es fehlt nichts an meinen Karten..."),
        also Chat-Geplapper. Der Satz kommt jetzt vom Meta-Koordinator; fehlt er,
        steht die Luecke da, statt dass ein Auszug sie zudeckt.
      */}
      {sz.rolle === 'arbeitet' && (
        sz.stand
          ? <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', marginTop: 2, lineHeight: 1.4 }}>{sz.stand}</div>
          : <div style={{ fontSize: 10, color: 'var(--tn-orange)', marginTop: 2 }}>Kein Stand vom Koordinator</div>
      )}
      {/*
        "steht" sagte bisher nur, dass kein Prozess laeuft — und liess offen, was das
        fuer Rafael bedeutet. Seit 06.09. blockiert eine schlafende Sitzung die Karte
        nicht mehr, weil die Antwort sie weckt. Dann muss hier auch das stehen, sonst
        sieht es aus wie ein Hindernis, das keines ist.
      */}
      {/*
        Seit 07.09. steht hier die TAETIGKEIT aus dem Transkript-Ende (arbeitet /
        im Limit bis / wartet auf / still seit / kein Prozess), nicht mehr
        laeuft/schlaeft aus `processAlive` — "läuft" hiess vorher nur, dass ein
        Prozess auf stdin wartet. Ohne `taetigkeit` (alte Serverfassung) wie bisher.
      */}
      <div
        title={sz.taetigkeit ? `Gemessen: ${sz.taetigkeit.quelle}` : undefined}
        style={{
          fontSize: 10, marginTop: 2,
          color: sz.beendet || sz.taetigkeit?.art === 'wartet_auf_antwort' || sz.taetigkeit?.art === 'limit' ? 'var(--tn-orange)'
            : sz.taetigkeit?.art === 'tot' ? 'var(--tn-red)' : 'var(--tn-text-muted)',
        }}
      >
        {sz.projectName} · {sz.taetigkeit
          ? sz.taetigkeit.wort
          : sz.beendet
            ? 'abgeschlossen — deine Antwort öffnet sie wieder'
            : sz.laeuft ? 'läuft' : 'schläft — deine Antwort weckt sie'}
        {!sz.taetigkeit && sz.aufmerksamkeit && sz.aufmerksamkeit !== 'idle' ? ` · ${sz.aufmerksamkeit}` : ''}
      </div>
    </button>
  );

  if (!onHebel) return zeile;
  return (
    <div style={{ marginBottom: 4 }}>
      {zeile}
      <div style={{ display: 'flex', gap: 6, marginTop: 2, marginLeft: 2 }}>
        <button
          type="button"
          data-ai-id={`guide-anhalten-${sz.sessionId.slice(0, 8)}`}
          title="Prozess stoppen; der Chat bleibt, deine Nachricht startet ihn wieder"
          onClick={() => onHebel('anhalten', sz)}
          style={hebelStil}
        >
          ‖ anhalten
        </button>
        <button
          type="button"
          data-ai-id={`guide-schliessen-${sz.sessionId.slice(0, 8)}`}
          title="Sitzung schliessen"
          onClick={() => onHebel('schliessen', sz)}
          style={hebelStil}
        >
          × schließen
        </button>
      </div>
    </div>
  );
}

const hebelStil: React.CSSProperties = {
  fontSize: 10, padding: '1px 7px', borderRadius: 4, cursor: 'pointer',
  background: 'transparent', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)',
};

/**
 * Die EINE ausgeschriebene Karte: Sachlage, Optionen mit ihrer Folge, Herkunft der
 * Angaben, die drei Knoepfe. Alles, was Rafael braucht, um zu entscheiden, ohne
 * vorher irgendwo nachzulesen.
 */
function KartenBlock({
  karte, onNeuladen, onWeiter, wahlStand, onFehler, onOeffnenWorkspace, onOeffnenSitzung, onSitzungAlsBuehne, onArbeit, onFensterNeu,
}: {
  karte: Karte & { thema_aufgeloest?: ThemaBezug | null; im_anderen_haus?: string | null };
  onArbeit: (aktiv: boolean) => void;
  onNeuladen: () => void;
  /** Nach einer Antwort: naechste Karte UND ihr hergerichtetes Fenster. */
  onWeiter: () => void;
  /** Stand von Rafaels Handwahl — siehe handWahl im GuidePanel. */
  wahlStand: () => number;
  onFehler: (f: string | null) => void;
  onOeffnenWorkspace: (projectId: string, panels?: string[]) => boolean | void;
  onOeffnenSitzung: (projectId: string, sessionId: string, accountId: string) => void;
  /** Chat als Buehne zeigen, ohne Rafaels gespeichertes Layout zu schreiben. */
  onSitzungAlsBuehne: (projectId: string, sessionId: string, accountId: string) => void;
  /** Not-Knopf: setzt das Fenster auf genau das Rezept dieser Karte zurueck. */
  onFensterNeu?: (karte: Karte) => void;
}) {
  const antwortSpeichern = useGuideAntwort();
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [notiz, setNotiz] = useState('');
  const [modal, setModal] = useState<'zurueck' | 'abgeben' | null>(null);
  const [zurueckGrund, setZurueckGrund] = useState<string | null>(null);
  const [zurueckText, setZurueckText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  /*
   * DER FEHLER GEHOERT DORTHIN, WO GETIPPT WURDE.
   *
   * Gemessen 06.09. 14:52Z: der Sendeweg selbst ist heil (Wegwerf-Karte angelegt,
   * mit Freitext beantwortet, per GET gegengeprueft, wieder geloescht). Rafaels
   * Antwort auf k14 um ~14:44Z ging trotzdem verloren — der laufende Server war zu
   * dem Zeitpunkt noch der Stand VOR 14:35Z und wies `freitext` mit 400 ab; neu
   * gestartet wurde erst 14:45:12Z. Die Oberflaeche geht in fuenf Minuten von selbst
   * live, Servercode erst beim Neustart.
   *
   * Bitter daran ist nicht der 400 — bitter ist, dass Rafael ihn nicht sah: die
   * Fehlerzeile der Buehne sitzt ganz oben im Panel, das Feld weit darunter. Er
   * tippte, druckte Enter, und nichts geschah. Deshalb steht der Fehler jetzt
   * ZUSAETZLICH direkt unter dem Feld, und der Text bleibt stehen, damit er ihn
   * noch einmal abschicken kann statt ihn neu zu tippen.
   */
  const [sendefehler, setSendefehler] = useState<string | null>(null);
  const [wartende, setWartende] = useState<Sitzung[]>([]);
  const vorgelegt = useRef<Set<string>>(new Set());
  const beruehrt = useRef<Set<string>>(new Set());

  // Karte gewechselt: Auswahl zuruecksetzen und den ersten Blick melden.
  useEffect(() => {
    setGewaehlt(null); setNotiz(''); setModal(null); setZurueckGrund(null); setZurueckText('');
    onArbeit(false);
    if (vorgelegt.current.has(karte.id)) return;
    vorgelegt.current.add(karte.id);
    fetch(`${API}/guide/karten/${karte.id}/vorgelegt`, { method: 'POST' }).catch(() => {});
  }, [karte.id]);

  // Wer auf DIESE Entscheidung wartet — live aufgeloest, nichts davon steht in der Karte.
  useEffect(() => {
    let lebt = true;
    if (!karte.wartet_auf.length) { setWartende([]); return; }
    fetch(`${API}/guide/karten/${karte.id}/wartende`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (lebt && d) setWartende(d.wartende ?? []); })
      .catch(() => {});
    return () => { lebt = false; };
  }, [karte.id, karte.wartet_auf.length]);

  /**
   * EIN Satz darueber, wen Rafaels Antwort erreicht — an EINER Stelle entschieden.
   *
   * Rafael, 07.09.: "Karten zeigen auf tote Sitzungen — du klickst in einen leeren
   * Chat." Gemessen am selben Tag: von 41 offenen Karten zeigten 11 auf eine
   * Sitzung, in der kein Prozess mehr lief. Vier davon warnten (abgeschlossen),
   * SIEBEN sagten gar nichts — der haeufigste Fall, ein schlafender Faden, hatte
   * keinen Zweig. Er klickte hinein und fand niemanden vor.
   *
   * Vorher standen hier zwei getrennte `every(...)`-Bedingungen nebeneinander, und
   * jede Kombination, die in keine von beiden fiel, blieb stumm. Deshalb jetzt EINE
   * Ableitung mit einem Ausgang fuer JEDEN Zustand — dieselbe Lehre wie beim
   * Ungelesen-Zaehler, der viermal unabhaengig nachgebaut worden war.
   *
   * Nicht gefiltert wird: sobald EIN Ziel wirklich laeuft, ist die Karte in
   * Ordnung und es steht nichts da.
   */
  const zielhinweis = ((): { text: string; farbe: string } | null => {
    if (!wartende.length) return null;
    if (wartende.some(sz => sz.gefunden && sz.laeuft && !sz.beendet)) return null;
    /*
     * EINE KARTE AUS DEM ANDEREN HAUS: ihre Sitzung steht nicht in DIESER
     * Konversationsliste — das heisst nicht, dass es sie nicht gibt.
     *
     * Ohne diesen Zweig behauptet die Spalte "Deine Antwort erreicht niemanden",
     * und zwar ausgerechnet bei einer Karte, die beantwortbar ist. Am Bild gesehen
     * (09.09.2026 19:1xZ, Aufnahme des Guide mit ?karte=): die angeklickte
     * Partner-Karte stand richtig oben — und darunter stand in Rot, sie sei an
     * niemanden zustellbar. Das haette Rafael vom Antworten abgehalten.
     *
     * Was hier NICHT behauptet wird: dass der Chat drueben lebt. Von hier ist das
     * nicht messbar, und eine beruhigende Erfindung waere derselbe Fehler mit
     * anderem Vorzeichen. Gesagt wird nur, was sicher ist: die Antwort wird an der
     * Karte festgehalten und von dort zugestellt (zustellungVorbereiten).
     */
    if (karte.im_anderen_haus) {
      const drueben = karte.im_anderen_haus === 'partner' ? 'Partner-Server' : 'Dev-Server';
      return {
        farbe: 'var(--tn-orange)',
        text: `Der Chat dazu läuft auf dem ${drueben} und ist von hier nicht einsehbar — ob er noch offen ist, `
          + 'lässt sich hier nicht sagen. Deine Antwort wird an der Karte festgehalten und dort zugestellt.',
      };
    }
    if (wartende.every(sz => !sz.gefunden)) return {
      farbe: 'var(--tn-red)',
      text: 'Der Chat dazu ist in keiner Liste mehr. Deine Antwort erreicht niemanden — die Karte braucht eine neue Adresse.',
    };
    if (wartende.every(sz => sz.gefunden && sz.beendet)) return {
      farbe: 'var(--tn-orange)',
      text: `${wartende.length === 1 ? 'Der Chat dazu ist abgeschlossen' : 'Alle Chats dazu sind abgeschlossen'} — „Erledigt" schickt deine Antwort trotzdem hin und weckt ${wartende.length === 1 ? 'ihn' : 'sie'} wieder auf.`,
    };
    // Bleibt: gefunden, nicht abgeschlossen, aber kein Prozess — der schlafende
    // Faden. Er ist NICHT verloren: die Antwort startet ihn mit seinem Verlauf.
    return {
      farbe: 'var(--tn-orange)',
      text: `${wartende.length === 1 ? 'Dort arbeitet gerade niemand' : 'Dort arbeitet gerade niemand mehr'} — der Chat schläft. Deine Antwort startet ihn wieder, mit allem, was er bisher weiß.`,
    };
  })();

  /** Erster Klick auf der Karte — einmal je Karte, Messpunkt. */
  const melde = useCallback((id: string) => {
    if (beruehrt.current.has(id)) return;
    beruehrt.current.add(id);
    fetch(`${API}/guide/karten/${id}/beruehrt`, { method: 'POST' }).catch(() => {});
  }, []);

  /**
   * Sendet den vom Server komponierten Wortlaut an alle wartenden Sitzungen und
   * meldet jedes Ergebnis einzeln zurueck. Fehler werden NICHT verschluckt: sie
   * stehen danach auf der Karte.
   *
   * `broadcast` (seit 08.09.2026, Arbeitsmodus-Auswertung): bei einer Antwort mit
   * Tragweite (Stufe 3 oder langer Freitext) liefert der Server zusaetzlich andere
   * lebende Sitzungen desselben Themas mit — eigener Wortlaut je Empfaenger, weil
   * es fuer sie eine FYI-Nachricht ist, keine Zustellung ihrer eigenen wartenden
   * Entscheidung. Beide Listen teilen sich denselben Zustellungsmechanismus.
   */
  const zustellen = useCallback(async (
    k: Karte, nachricht: string | null, broadcast?: Array<{ sessionId: string; nachricht: string }>,
  ) => {
    /*
     * DER WEG SELBST STEHT AUF MODUL-EBENE (zustellenAn) — hier bleibt nur, was
     * die ausgeschriebene Karte zusaetzlich tut: den Fehlschlag SAGEN. Auf der
     * Karte steht er ohnehin (offeneZustellung unten), aber nach einer Antwort
     * rueckt die naechste Karte vor — dann sieht Rafael die Kartenmeldung nie.
     */
    onFehler(zustellFehlerSatz(k.titel, await zustellenAn(k, nachricht, broadcast)));
  }, [onFehler]);

  /*
   * Beim Abbau wird die Uhr NICHT mehr abgeraeumt. Die Zustellung an die Sitzung
   * laeuft nur hier im Browser (der Server darf /mission/send nicht selbst rufen);
   * wer die Uhr beim Verlassen der Karte loeschte, verlor Rafaels Antwort still —
   * Karte "beantwortet", Sitzung wartet weiter. Stattdessen merkt sich der Baustein,
   * dass er weg ist, und laesst nur den Weitersprung aus.
   */
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);
  /** Weiter nur, wenn die Karte noch steht und Rafael seit dem Klick nichts selbst gewaehlt hat. */
  const weiterWennNochHier = useCallback((standBeimKlick: number) => {
    if (!lebt.current || wahlStand() !== standBeimKlick) return;
    onWeiter();
  }, [wahlStand, onWeiter]);

  const handeln = useCallback(async (pfad: string, koerper: unknown) => {
    const stand = wahlStand();
    setLaeuft(true);
    try {
      const r = await fetch(`${API}/guide/karten/${karte.id}/${pfad}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(koerper),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { onFehler(d?.error ?? `${r.status}`); return; }
      onFehler(null);
      weiterWennNochHier(stand);
      /*
       * ZUSTELLUNG NICHT MEHR ABWARTEN (Rafael, 07.09. 07:34Z: "es dauert nach dem
       * bestaetigen recht lange - er geht nicht automatisch zurueck - das nervt").
       * Der Sprung zur naechsten Karte hing hier am vollen Netzwerk-Umlauf von
       * /mission/send, bevor er ueberhaupt losging. Die Zustellung laeuft jetzt im
       * Hintergrund weiter — ihr Fehler landet trotzdem ueber onFehler/zustellen —,
       * der Ruecksprung wartet nicht mehr auf sie.
       */
      void zustellen(karte, d?.nachricht ?? null, d?.broadcast);
    } finally {
      setLaeuft(false);
      onArbeit(false);
      // Nach einer Antwort rueckt die naechste Karte vor — dann muss auch IHR Fenster
      // hergerichtet werden, nicht nur die Daten geladen. Dasselbe gilt fuer
      // "Spaeter"/"Nicht meins": die Karte verlaesst die Vorlage, die naechste kommt.
    }
  }, [karte, zustellen, weiterWennNochHier, wahlStand, onFehler, onArbeit]);

  // Auswahl wird vor der dauerhaften Annahme bestätigt. Der neue Kern besitzt
  // keinen Widerruf; eine nachträgliche lokale "Rücknahme" wäre eine Falschaussage.
  const [schwebt, setSchwebt] = useState<{ kuerzel: string; text: string } | null>(null);
  const optionKlick = useCallback((o: Option) => {
    setGewaehlt(o.kuerzel);
    setSchwebt({ kuerzel: o.kuerzel, text: o.text });
  }, []);
  const optionBestaetigen = async () => {
    if (!schwebt || laeuft) return;
    const stand = wahlStand();
    setLaeuft(true);
    onArbeit(true);
    try {
      await antwortSpeichern(karte, { kuerzel: schwebt.kuerzel, notiz: notiz.trim() });
      setSchwebt(null);
      setSendefehler(null);
      onFehler(null);
      weiterWennNochHier(stand);
    } catch (e) { setSendefehler(String(e)); onFehler(String(e)); }
    finally { setLaeuft(false); onArbeit(false); }
  };

  /*
   * FREI SCHREIBEN STATT WAEHLEN (Rafael, 06.09. 14:31Z).
   *
   * "Praktisch ist es, wenn ich dann einfach links da jeweils reinschreibe, beim
   * Guide, da, wo ich normal die Felder beantworten kann. Da gehoert dann so ein
   * kleines Chatfenster rein. Wenn das so ist, schmeisst er das rechts in den Chat
   * rein und da hat er damit auch eine Begruendung, dass er zur naechsten Session
   * weitergeht."
   *
   * Der geschriebene Satz geht als `freitext` an dieselbe Route wie ein
   * Optionsklick und laeuft an die wartende Sitzung — der Guide springt hier lokal
   * trotzdem weiter (onWeiter, unten). Anders als ein Optionsklick gilt die Karte
   * serverseitig aber NICHT als beantwortet (Korrektur 10.09.2026): ohne gewaehltes
   * Kuerzel ist ein Satz eine Auflage an die Sitzung, keine abgeschlossene
   * Entscheidung — sie bleibt offen, bis tatsaechlich eine Option gewaehlt wird.
   * Sonst gingen Auflagen wie "das gefaellt mir nicht, vergleiche expliziter"
   * spurlos verloren, sobald Rafael sie hier statt im Chat rechts eintippte.
   *
   * BILDER: /mission/send kennt nur Text, ein Anhang laesst sich dort nicht
   * mitgeben. Eingefuegte Bilder werden deshalb abgelegt (POST /upload) und ihr
   * PFAD an die Nachricht gehaengt — den kann die empfangende Sitzung oeffnen. Das
   * ist der ehrliche Weg: das Bild ist wirklich da, statt in einer Vorschau zu
   * existieren, die beim Senden verschwindet.
   */
  const [freitext, setFreitext] = useState('');

  const freitextSenden = useCallback(async () => {
    const text = freitext.trim();
    if (!text || laeuft) return;
    const stand = wahlStand();
    melde(karte.id);
    onArbeit(true);
    setLaeuft(true);
    try {
      await antwortSpeichern(karte, { freitext: text });
      setSendefehler(null);
      onFehler(null);
      setFreitext('');
      weiterWennNochHier(stand);
    } catch (e) {
      setSendefehler(String(e));
      onFehler(String(e));
    } finally {
      setLaeuft(false);
      onArbeit(false);
    }
  }, [freitext, laeuft, karte, antwortSpeichern, weiterWennNochHier, wahlStand, onFehler, onArbeit]);

  const zuruecknehmen = () => {
    setSchwebt(null);
    setGewaehlt(null);
  };

  /*
   * NICHT NUR DAS, WAS LAUT GESCHEITERT IST (Koordinator, 09.09.2026 15:26Z).
   *
   * Bisher stand hier nur `fehlgeschlagen`. Ein Eintrag auf 'offen' heisst
   * "Zustell-Auftrag angelegt, Ergebnis nie gemeldet" — genau der Zustand, in dem
   * 57 Antworten lagen, ohne dass irgendwo etwas rot war. Er ist kein Zwischen-
   * schritt, den man aussitzen kann: es meldet ihn niemand nach, weil der einzige
   * Melder der Client ist, der die Antwort abgeschickt hat.
   */
  const offeneZustellung = karte.zustellung?.filter(z => z.zustand !== 'zugestellt') ?? [];

  return (
    <div onMouseDown={() => melde(karte.id)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
          background: DRINGLICH_FARBE[karte.dringlichkeit], color: 'var(--tn-bg)',
        }}>
          {karte.dringlichkeit}
        </span>
        <span style={{ fontSize: 10, color: 'var(--tn-text-muted)' }}>{karte.kopfanstrengung}</span>
        <div style={{ flex: 1 }} />
        {/*
          IN WELCHEM HAUS DIE SITZUNG LAEUFT (Rafael, 08.09. 17:39Z, verstaerkt 09.09.
          05:2xZ: "es muss einfach ... gekennzeichnet sein" — nicht nur ein kleines
          Label zwischen anderen Angaben). Sichtbar, nicht nur ein Filterfeld: die
          Ansicht zeigt zwar schon nur das eigene Haus, aber wer eine Karte woanders
          wiedersieht — in einem Bericht, in einer Nachricht, im eigenen Chat — soll
          ohne Nachfragen wissen, wo ihre Buehne lebt. Gemeinsame Kachel-Marke mit den
          Bloecken: siehe HausMarke().
        */}
        <HausMarke haus={karte.haus} />
        <span style={{ fontSize: 10, color: 'var(--tn-text-muted)' }} title="Wer die Karte eingestellt hat">
          {karte.eingestellt_von.label}
        </span>
      </div>

      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tn-text)', marginBottom: 8, lineHeight: 1.3 }}>
        {karte.titel}
      </div>

      {/*
        LESESTOFF IST KEINE ENTSCHEIDUNG (Rafael, 09.09.2026 06:2xZ, Block
        "Dein Tag 08.09.": "es kommt eine Buehne aber keine Entscheidung ...
        ALLES was gruen ist und fertig ist soll eine Buehne UND eine
        Entscheidung haben — sonst braucht es andere Markierungen."). Eine
        Karte ohne `entscheidung` und ohne `optionen` stellt keine Frage — sie
        wuerde sonst wie eine Entscheidung aussehen (gleicher Rahmen, gleiche
        Stelle), nur ohne Klickmoeglichkeit, und genau das las er als Fehler.
      */}
      {!karte.entscheidung?.trim() && !karte.optionen.length && (
        <div style={{
          fontSize: 10, fontWeight: 700, color: 'var(--tn-text-muted)', marginBottom: 8,
          border: '1px solid var(--tn-border)', borderRadius: 4, padding: '3px 6px', display: 'inline-block',
        }}>
          📄 Lesestoff — keine Entscheidung nötig
        </div>
      )}

      {karte.thema_aufgeloest?.fehlt && (
        <div style={{ fontSize: 10, color: 'var(--tn-orange)', marginBottom: 6 }}>
          Zugeordnetes Thema „{karte.thema_aufgeloest.id}" gibt es auf dem Board nicht mehr.
        </div>
      )}

      {/*
        UEBERARBEITET, WAEHREND SIE DIR VORLAG (Rafael, 07.09.).

        Nicht verhindert, sondern gesagt. Eine Sitzung, die einen Fehler in ihrer
        eigenen Vorlage korrigiert, bevor Rafael antwortet, tut das Richtige — das
        Problem war, dass er es nicht merkte: der Text wechselte still unter ihm,
        und er beantwortete am Ende eine andere Frage als die gelesene.

        Nur nach dem ersten Vorlegen: davor ist Umbauen Verfassen, nicht Austauschen.
      */}
      {karte.geaendert_am && karte.messung?.vorgelegt_am
        && karte.geaendert_am > karte.messung.vorgelegt_am && (
        <div style={{
          fontSize: 10, color: 'var(--tn-orange)', marginBottom: 6,
          border: '1px solid var(--tn-orange)', borderRadius: 4, padding: '3px 6px',
        }}>
          Diese Karte wurde überarbeitet, während sie dir vorlag —{' '}
          {new Date(karte.geaendert_am).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}
          {karte.geaendert_felder?.length ? `, geändert: ${karte.geaendert_felder.join(', ')}` : ''}.
        </div>
      )}

      {/*
        ERST DIE FRAGE UND DIE KNOEPFE, DANN DIE ERKLAERUNG.
        (Rafael, 06.09., mit offener Karte: "ich kenne mich von der Bedienung her
        nicht aus".)

        Vorher stand zwischen Titel und Optionen der ganze Sachlage-Block. Wer nicht
        scrollte, sah keine Antwortmoeglichkeit — die Karte sah aus wie ein Text, den
        man liest, nicht wie eine Frage, die man beantwortet. Ausgerechnet an der
        Stelle, an der Rafael nur noch klicken sollte.

        Der Preis ist bewusst in Kauf genommen: die Optionen stehen jetzt vor ihrer
        Begruendung, er kann also klicken, bevor er die Sachlage gelesen hat. Genau
        dafuer gibt es die ausdrueckliche Bestaetigung vor dem Senden — und wer die Lage schon kennt,
        soll gar nicht erst lesen muessen. Aufwand waechst mit der Tragweite, nicht
        mit dem Fleiss.

        Was BLEIBT: die Empfehlung steht weiterhin nach den Optionen. Sie zuerst zu
        zeigen hiesse, ihn die Optionen mit dem Ergebnis im Kopf lesen zu lassen.
      */}
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-text)', marginBottom: 6 }}>
        {karte.entscheidung}
      </div>

      {/* Optionen — jede mit ihrer Folge. Erst Auswahl, dann Bestätigung.
          Verwerfen vor der Bestätigung schreibt nichts auf den Server.

          KEINE FARBE FUER "AUSGEWAEHLT" (Rafael, 08.09.: "wenn ich draufklicke, ist
          das auch blau — das wuerde doch heissen, dass es arbeitet"). Seit die
          Uebersicht des Coach Farbe fest bedeutet (gruen = Karte/Klick-oeffnet-
          Buehne, blau = Sitzung/Klick-oeffnet-Chat — Legende in uebersicht.py),
          waere ein blau markierter Klick auf eine Option hier ein Widerspruch dazu:
          die Option ist weder Karte noch Sitzung. Die Auswahl bekommt deshalb einen
          RAHMEN statt eine der beiden belegten Farben — der Punkt (●/○) bleibt das
          zweite, textliche Signal. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
        {karte.optionen.map(o => {
          const ausgewaehlt = gewaehlt === o.kuerzel;
          return (
            <button
              key={o.kuerzel}
              data-ai-id={`guide-option-${o.kuerzel}`}
              onClick={() => { if (!schwebt && !laeuft) optionKlick(o); }}
              style={{
                ...box, textAlign: 'left', cursor: 'pointer',
                border: ausgewaehlt ? '2px solid var(--tn-text)' : '1px solid var(--tn-border)',
                padding: ausgewaehlt ? 9 : 10,
                background: 'var(--tn-surface)',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tn-text)' }}>
                {ausgewaehlt ? '● ' : '○ '}{o.text}
              </div>
              <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginTop: 3, lineHeight: 1.4 }}>
                → {o.folge}
              </div>
            </button>
          );
        })}
      </div>

      {/*
        SELBST SCHREIBEN — gleichwertig neben den Optionen, nicht darunter versteckt.

        Rafael, 06.09. 14:31Z: "so eine ganz normale Chatsicht, die dann zur
        entsprechenden Session weiterleitet." Der Guide springt lokal weiter, genau
        wie beim Optionsklick — serverseitig gilt die Karte OHNE gewaehltes Kuerzel
        aber nicht als beantwortet, sondern bleibt offen: der Satz ist eine Auflage
        an die Sitzung, keine Entscheidung (Korrektur 10.09.2026, siehe Kommentar bei
        freitextSenden oben).

        Ohne Rueckgaengig-Streifen, mit Absicht: einen selbst getippten Satz schickt
        niemand versehentlich ab. Die Bestaetigung schuetzt vor dem Fehlklick auf
        eine Option, nicht vor dem eigenen Text.
      */}
      <div style={{ marginBottom: 8 }}>
        {/*
          Die Zone ist SICHTBAR und dieselbe wie im Chat — das war Rafaels Punkt:
          "dieses hier klicken, dann Cmd+V fuer Bild einfuegen". Ein unsichtbares
          onPaste am Textfeld ist kein Angebot, sondern ein Geheimnis.
        */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <BildEinfuegeZone
            hoehe={40}
            onFehler={onFehler}
            onText={verweis => setFreitext(t => (t ? `${t}\n` : '') + verweis)}
          />
          <textarea
            data-ai-id="guide-freitext"
            value={freitext}
            onChange={e => setFreitext(e.target.value)}
            onKeyDown={e => {
              // Enter sendet, Shift+Enter macht eine Zeile — wie im Chat daneben.
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); freitextSenden(); }
            }}
            placeholder="…oder schreib deine Antwort (Enter sendet)"
            rows={2}
            disabled={laeuft || !!schwebt}
            style={{
              flex: 1, resize: 'vertical', fontSize: 12, lineHeight: 1.45,
              padding: '5px 7px', borderRadius: 5, fontFamily: 'inherit', minHeight: 40,
              background: 'var(--tn-surface)', color: 'var(--tn-text)',
              border: '1px solid var(--tn-border)',
            }}
          />
        </div>
        {sendefehler && (
          <div
            data-ai-id="guide-freitext-fehler"
            style={{
              marginTop: 4, padding: '4px 7px', fontSize: 11, borderRadius: 4,
              border: '1px solid var(--tn-red)', color: 'var(--tn-red)', background: 'rgba(247,118,142,0.08)',
            }}
          >
            {sendefehler} — deine Eingabe bleibt für einen erneuten Versuch erhalten.
          </div>
        )}
        {freitext.trim() && (
          <button
            data-ai-id="guide-freitext-senden"
            onClick={freitextSenden}
            disabled={laeuft}
            style={{
              marginTop: 4, padding: '4px 10px', fontSize: 11, borderRadius: 5, cursor: 'pointer',
              background: 'var(--tn-blue)', color: 'var(--tn-bg)', border: 'none', fontWeight: 700,
            }}
          >
            Antwort schicken{wartende.length === 1 ? '' : ` (an ${wartende.length} Sitzungen)`}
          </button>
        )}
      </div>

      {/*
        DER HANDLUNGSBEREICH BLEIBT BEI DEN OPTIONEN.

        Beim Hochziehen der Optionen waere er sonst 120 Zeilen unter ihnen gelandet:
        Rafael klickt oben, und der Rueckgaengig-Streifen erscheint ausserhalb des
        sichtbaren Bereichs — also genau dann unsichtbar, wenn er gebraucht wird.
        Eine Sicherheitsfunktion, die man suchen muss, ist keine.
      */}
      {schwebt && (
        <div
          data-ai-id="guide-rueckgaengig"
          style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
            padding: '5px 8px', borderRadius: 5, fontSize: 11,
            background: 'rgba(158,206,106,0.12)', border: '1px solid rgba(158,206,106,0.4)',
          }}
        >
          <span style={{ flex: 1 }}>
            Deine Auswahl: „{schwebt.text}“
          </span>
          <button data-ai-id="guide-antwort-bestaetigen" disabled={laeuft} onClick={() => void optionBestaetigen()}>
            {laeuft ? 'Speichert …' : 'Antwort bestätigen'}
          </button>
          <button disabled={laeuft}
            data-ai-id="guide-rueckgaengig-knopf"
            onClick={zuruecknehmen}
            style={{
              background: 'none', border: '1px solid var(--tn-border)', borderRadius: 4,
              padding: '2px 8px', fontSize: 11, color: 'var(--tn-text)', cursor: 'pointer',
            }}
          >
            Auswahl schließen
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 5, marginBottom: 6 }}>
        {/* "Erledigt" bleibt fuer Karten OHNE Optionen — dort gibt es keinen Klick,
            der die Antwort sein koennte. Bei Karten mit Optionen waere der Knopf
            jetzt ein zweiter Weg zum selben Ziel, und zwei Wege driften. */}
        {karte.optionen.length === 0 && (
          <Knopf
            id="erledigt"
            label="Erledigt"
            ton="stark"
            disabled={laeuft || !!schwebt}
            onClick={() => handeln('antwort', { kuerzel: gewaehlt, notiz: notiz.trim() || undefined })}
          />
        )}
        <Knopf id="spaeter" label="Später, weil …" disabled={laeuft} onClick={() => { melde(karte.id); onArbeit(true); setModal(modal === 'zurueck' ? null : 'zurueck'); }} />
        {/*
          "UNKLAR" — der Ein-Klick-Ersatz fuer 18-mal "Fenster neu" getippt
          (Arbeitsmodus-Auswertung 08.09.2026). Anders als "Später" ist das ein
          Befund ueber die KARTE (sie hat nicht orientiert), kein Grund aus
          Rafaels eigener Kapazitaet — deshalb kein Modal, kein Text noetig,
          ein Klick genuegt (Server: POST /karten/:id/unklar).
        */}
        <Knopf id="unklar" label="Unklar" disabled={laeuft} onClick={() => { melde(karte.id); handeln('unklar', {}); }} />
        {/*
          Der Not-Knopf auch hier: Rafael sitzt vor der Karte, das Fenster daneben
          stimmt nicht, und er soll das in einem Klick geraderuecken koennen, statt
          das Thema zu verlassen und neu hineinzugehen.
        */}
        {karte.layout && onFensterNeu && (
          <Knopf
            id="fenster-neu"
            label="Fenster neu herrichten"
            disabled={laeuft}
            onClick={() => { melde(karte.id); onFensterNeu(karte); }}
          />
        )}
        <Knopf id="nicht-meins" label="Nicht meins" disabled={laeuft} onClick={() => { melde(karte.id); onArbeit(true); setModal(modal === 'abgeben' ? null : 'abgeben'); }} />
      </div>
      {!gewaehlt && !modal && karte.optionen.length > 0 && (
        <div style={{ fontSize: 10, color: 'var(--tn-text-muted)', marginBottom: 6 }}>
          Ein Klick auf eine Option entscheidet — fünf Sekunden lang rückgängig.
        </div>
      )}

      {/* Ab hier das Ausfuehrliche: worum es geht, was ohne Entscheidung passiert,
          Empfehlung, Gegenargument. Wer die Lage kennt, hat oben schon geklickt. */}
      <div style={{ ...box, fontSize: 12, lineHeight: 1.5, color: 'var(--tn-text)', marginBottom: 8 }}>
        <LangerFliesstext text={karte.sachlage} />
        {/* Rafaels Kontext-Kopf, dritter Punkt (05.09.): fuer wen aendert sich was,
            und merken die Nutzer es ueberhaupt. Steht direkt an der Sachlage, weil
            es zu ihr gehoert und nicht als eigener Block gelesen werden soll. */}
        {karte.nutzersicht && (
          <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginTop: 6, lineHeight: 1.45 }}>
            Für wen: {karte.nutzersicht}
          </div>
        )}
      </div>

      {/* "Was passiert, wenn ich nichts entscheide" — der Satz, der eine Karte
          ueberhaupt erst dringlich macht (Rafael, 05.09., Punkt 2). */}
      {karte.ohne_entscheidung && (
        <div style={{ fontSize: 11, color: 'var(--tn-orange)', marginBottom: 8, lineHeight: 1.45 }}>
          Ohne Entscheidung: {karte.ohne_entscheidung}
        </div>
      )}

      {/* Die Empfehlung in einem Satz (Rafael, 05.09., Punkt 4). Sie steht NACH den
          Optionen: erst seine eigene Abwaegung, dann unsere — umgekehrt liest er
          die Optionen schon mit dem Ergebnis im Kopf.

          KEIN BLAU: blau ist seit der Coach-Uebersicht exklusiv "Sitzung, Klick
          oeffnet Chat" (Rafael, 08.09. — dieselbe Kollision wie bei den Optionen
          oben, hier zusaetzlich nicht klickbar, also ohne Ausnahme). Signal ist
          jetzt eine gestrichelte Umrandung statt Farbe. */}
      {karte.empfehlung && (
        <div style={{
          ...box, fontSize: 11, lineHeight: 1.45, marginBottom: 6,
          border: '1px dashed var(--tn-text-muted)', color: 'var(--tn-text)',
        }}>
          <span style={{ fontWeight: 700 }}>Empfehlung: </span>
          {karte.empfehlung}
        </div>
      )}
      {/*
        Der Einwand steht direkt darunter und wird NICHT eingeklappt.
        Recherche 06.09. (stark belegt): eine Empfehlung ohne Gegenargument wird
        gerade dann durchgewunken, wenn wenig Kopf da ist — also genau in der Lage,
        fuer die diese Spalte gebaut ist.
      */}
      {karte.gegenargument && (
        <div style={{
          ...box, fontSize: 11, lineHeight: 1.45, marginBottom: 8,
          borderColor: 'var(--tn-orange)', color: 'var(--tn-orange)',
        }}>
          <span style={{ fontWeight: 700 }}>Dagegen spricht: </span>
          {karte.gegenargument}
        </div>
      )}

      {/* Herkunft: selbst gemessen wiegt anders als gelesen. */}
      {karte.quelle.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {karte.quelle.map((q, i) => (
            <div key={i} style={{ fontSize: 10, color: 'var(--tn-text-muted)', marginBottom: 3, lineHeight: 1.4 }}>
              <span style={{ color: QUELLE_FARBE[q.art], fontWeight: 700 }}>{QUELLE_TEXT[q.art]}</span>
              {': '}{q.text}
            </div>
          ))}
        </div>
      )}

      {/* Wer auf diese Entscheidung wartet — der Chat, in dem die Arbeit haengt. */}
      {wartende.map(sz => (
        <SitzungsZeile key={sz.sessionId} sz={{ ...sz, rolle: 'wartet' }} onOeffnen={onSitzungAlsBuehne}
          imAnderenHaus={karte.im_anderen_haus} onFehler={onFehler} />
      ))}

      {/*
        Wer auf die Antwort wartet und in welchem Zustand — genau ein Satz, aus
        `zielhinweis` oben. Eine Karte, die stumm einen schlafenden oder beendeten
        Chat aufweckt, ist die Selbstkorrektur, die Rafael am 04.09. vermisst hat.
      */}
      {zielhinweis && (
        <div style={{ fontSize: 10, color: zielhinweis.farbe, marginBottom: 6, lineHeight: 1.4 }}>
          {zielhinweis.text}
        </div>
      )}

      {karte.ziel_workspace && karte.panels.length > 0 && (
        <button
          data-ai-id="guide-open-panels"
          onClick={() => { melde(karte.id); onOeffnenWorkspace(karte.ziel_workspace!, karte.panels); }}
          style={{
            width: '100%', padding: '5px', fontSize: 10, borderRadius: 5, cursor: 'pointer', margin: '4px 0 8px',
            background: 'transparent', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)',
          }}
        >
          zusätzlich öffnen: {karte.panels.join(', ')}
        </button>
      )}


      {gewaehlt && !modal && (
        <textarea
          data-ai-id="guide-note"
          value={notiz}
          onChange={e => setNotiz(e.target.value)}
          placeholder="Zusatz für die Umsetzung (optional)"
          style={{
            width: '100%', minHeight: 44, fontSize: 11, padding: 6, borderRadius: 5, resize: 'vertical',
            background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
            fontFamily: 'inherit', marginBottom: 8,
          }}
        />
      )}

      {modal === 'zurueck' && (
        <div style={{ ...box, marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginBottom: 6 }}>
            Warum später? Der Grund ist das Signal, wann diese Karte besser passt.
          </div>
          {/* Gleicher Grund wie bei den Optionen oben: Auswahl bekommt einen Rahmen,
              keine der beiden belegten Farben (gruen=Karte, blau=Sitzung). */}
          {ZURUECK_PRESETS.map(pr => {
            const ausgewaehlt = zurueckGrund === pr.grund;
            return (
              <button
                key={pr.grund}
                data-ai-id={`guide-defer-${pr.grund}`}
                onClick={() => setZurueckGrund(pr.grund)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: 4,
                  padding: ausgewaehlt ? '4px 6px' : '5px 7px',
                  fontSize: 11, borderRadius: 4, cursor: 'pointer',
                  background: 'transparent',
                  color: 'var(--tn-text)',
                  border: ausgewaehlt ? '2px solid var(--tn-text)' : '1px solid var(--tn-border)',
                }}
              >
                {pr.label}
              </button>
            );
          })}
          {ZURUECK_PRESETS.find(pr => pr.grund === zurueckGrund)?.brauchtText && (
            <textarea
              data-ai-id="guide-defer-text"
              value={zurueckText}
              onChange={e => setZurueckText(e.target.value)}
              placeholder={zurueckGrund === 'brauche-info' ? 'Welche Information fehlt dir?' : 'Grund'}
              style={{
                width: '100%', minHeight: 40, fontSize: 11, padding: 6, borderRadius: 4, resize: 'vertical',
                background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
                fontFamily: 'inherit', marginTop: 4,
              }}
            />
          )}
          <Knopf
            id="zurueckstellen"
            label="Zurückstellen"
            ton="stark"
            disabled={!zurueckGrund || laeuft
              || (!!ZURUECK_PRESETS.find(pr => pr.grund === zurueckGrund)?.brauchtText && !zurueckText.trim())}
            onClick={() => handeln('zurueck', { grund: zurueckGrund, text: zurueckText.trim() || undefined })}
          />
        </div>
      )}

      {modal === 'abgeben' && (
        <div style={{ ...box, marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginBottom: 6 }}>
            Geht an die wartenden Sitzungen: entscheidet das ohne dich.
          </div>
          <textarea
            data-ai-id="guide-delegate-note"
            value={notiz}
            onChange={e => setNotiz(e.target.value)}
            placeholder="Hinweis für die Umsetzung (optional)"
            style={{
              width: '100%', minHeight: 40, fontSize: 11, padding: 6, borderRadius: 4, resize: 'vertical',
              background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
              fontFamily: 'inherit', marginBottom: 6,
            }}
          />
          <Knopf id="abgeben" label="Abgeben" ton="stark" disabled={laeuft} onClick={() => handeln('abgeben', { notiz: notiz.trim() || undefined })} />
        </div>
      )}

      {offeneZustellung.length > 0 && (
        <div style={{ ...box, borderColor: 'var(--tn-red)', marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--tn-red)', marginBottom: 4 }}>
            Deine Antwort ist gespeichert, aber nicht angekommen:
          </div>
          {offeneZustellung.map(z => (
            <div key={z.sessionId} style={{ fontSize: 10, color: 'var(--tn-text-muted)', marginBottom: 4 }}>
              {z.sessionId.slice(0, 8)}: {z.zustand === 'offen'
                ? `nie losgeschickt (Auftrag steht seit ${z.am.slice(11, 16)}Z offen)`
                : z.fehler}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// --- Die zwei Chats unten in der Spalte --------------------------------------


type GuideLeseAbruf = { vorgang: 'board' | 'thema' | 'buehne'; generation: number };
type GuideLeseProblem = GuideLeseAbruf & { grund: string; seit: number };
/** A read can recover only its own operation. Late generations and unrelated actions remain separate. */
function useGuideLesefehler() {
  const generationen = useRef({ board: 0, thema: 0, buehne: 0 });
  const [probleme, setProbleme] = useState<Partial<Record<GuideLeseAbruf['vorgang'], GuideLeseProblem>>>({});
  const beginneLesen = useCallback((vorgang: GuideLeseAbruf['vorgang']): GuideLeseAbruf =>
    ({ vorgang, generation: ++generationen.current[vorgang] }), []);
  const meldeLesefehler = useCallback((abruf: GuideLeseAbruf, grund: string) => {
    setProbleme(alt => generationen.current[abruf.vorgang] !== abruf.generation ? alt : {
      ...alt, [abruf.vorgang]: { ...abruf, grund, seit: alt[abruf.vorgang]?.seit ?? Date.now() },
    });
  }, []);
  const bestaetigeLesen = useCallback((abruf: GuideLeseAbruf) => {
    setProbleme(alt => {
      if (generationen.current[abruf.vorgang] !== abruf.generation || !alt[abruf.vorgang]) return alt;
      const neu = { ...alt }; delete neu[abruf.vorgang]; return neu;
    });
  }, []);
  return { probleme, beginneLesen, meldeLesefehler, bestaetigeLesen };
}

export default function GuidePanel(props: Props) { return <GuideSendProvider><GuidePanelInhalt {...props} /></GuideSendProvider>; }
